import React, { useState, memo } from "react";
import { ScrollArea } from "./ui/scroll-area";
import AutoMarkupInput from "./AutoMarkupInput";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Slider } from "./ui/slider";
import { 
  Image, 
  Upload, 
  Sparkles, 
  Search, 
  Type, 
  Droplet, 
  Download, 
  RefreshCw,
  Loader2,
  Trash2,
  FileImage,
  FileDown,
  Edit3,
  Sun,
  Moon,
  Layers,
  Hash,
  Save,
  FolderOpen,
  FolderPlus,
  Folder,
  Edit2,
  Grid3X3,
  Maximize2,
  Scissors,
  Pencil,
  Minus,
  ArrowRight,
  ArrowLeftRight,
  Smile,
  SlidersHorizontal,
  Circle,
  Square,
  Eraser,
  BookmarkCheck,
  Copy,
  Lock,
  Move,
  Play,
  Camera,
  ChevronDown,
  ChevronRight,
  MousePointer2,
} from "lucide-react";

function Sidebar({
  isDarkMode,
  setIsDarkMode,
  templateSize,
  setTemplateSize,
  templateSizes,
  backgroundColor,
  setBackgroundColor,
  backgroundImage,
  backgroundImageFit,
  setBackgroundImageFit,
  backgroundImageScale,
  setBackgroundImageScale,
  backgroundImageLoading,
  generateBackgroundImage,
  generateBackgroundFromReference,
  selectBackgroundStockImage,
  uploadBackgroundImage,
  clearBackground,
  hasBackground,
  searchStockImages,
  overlayImages,
  selectedOverlayId,
  setSelectedOverlayId,
  overlayImageLoading,
  generateOverlayImage,
  // Animated Overlay (fal.ai image-to-video) — Phase 1+2
  overlayAiMode,
  setOverlayAiMode,
  animatedSourceImage,
  setAnimatedSourceImage,
  animatedOverlayPrompt,
  setAnimatedOverlayPrompt,
  animatedOverlayDuration,
  setAnimatedOverlayDuration,
  animatedOverlayLoading,
  animatedOverlayProgress,
  generateAnimatedOverlay,
  selectOverlayStockImage,
  uploadOverlayImage,
  removeOverlayImage,
  savedOverlays,
  loadSavedOverlays,
  saveOverlayToLibrary,
  deleteSavedOverlay,
  addOverlayImage,
  removeOverlayBackground,
  cutoutLoading,
  updateOverlayOpacity,
  updateOverlayAboveBoards,
  updateOverlayOutline,
  updateOverlayTransform,
  updateOverlayShadow,
  duplicateOverlay,
  overlayShadow,
  setOverlayShadow,
  overlayShadowOffsetX = 2,
  setOverlayShadowOffsetX,
  overlayShadowOffsetY = 2,
  setOverlayShadowOffsetY,
  boardCount,
  handleBoardCountChange,
  boardLayout,
  getDefaultGridDims,
  applyBoardLayout,
  boardOpacity,
  setBoardOpacity,
  transparentBoards,
  setTransparentBoards,
  transparentKeepHeader,
  setTransparentKeepHeader,
  transparentKeepFreeSpace,
  setTransparentKeepFreeSpace,
  multiColorRows,
  setMultiColorRows,
  boardRowColors,
  setBoardRowColors,
  currentBoardWidth,
  handleManualBoardResize,
  loteriaMoveMode,
  setLoteriaMoveMode,
  neighborsEnabled,
  setNeighborsEnabled,
  neighborsPosition,
  setNeighborsPosition,
  neighborsDirection,
  setNeighborsDirection,
  neighborsVerticalPairNumbering,
  setNeighborsVerticalPairNumbering,
  verticalPairBadgeAlign,
  setVerticalPairBadgeAlign,
  pairStackNumbersEnabled,
  setPairStackNumbersEnabled,
  pairStackOffsetX,
  setPairStackOffsetX,
  pairStackOffsetY,
  setPairStackOffsetY,
  neighborsHighlightColor,
  setNeighborsHighlightColor,
  neighborsShape,
  setNeighborsShape,
  neighborsBorderThickness,
  setNeighborsBorderThickness,
  neighborsShadowEnabled,
  setNeighborsShadowEnabled,
  neighborsOutlineStyle,
  setNeighborsOutlineStyle,
  neighborsCustomImage,
  setNeighborsCustomImage,
  neighborsCustomImageSize,
  setNeighborsCustomImageSize,
  neighborsCustomOutlineWidth,
  setNeighborsCustomOutlineWidth,
  neighborsCustomOutlineColor,
  setNeighborsCustomOutlineColor,
  neighborsRowsPerBoard,
  setNeighborsRowsPerBoard,
  neighborsQuadEnabled,
  setNeighborsQuadEnabled,
  customVerticalPairs = [],
  setCustomVerticalPairs,
  neighborsQuadCellsPerBoard,
  setNeighborsQuadCellsPerBoard,
  lastCardEnabled,
  setLastCardEnabled,
  lastCardCorner,
  setLastCardCorner,
  lastCardSize,
  setLastCardSize,
  lastCardShape,
  setLastCardShape,
  lastCardHighlightColor,
  setLastCardHighlightColor,
  lastCardBorderThickness,
  setLastCardBorderThickness,
  lastCardShadowEnabled,
  setLastCardShadowEnabled,
  lastCardOutlineStyle,
  setLastCardOutlineStyle,
  lastCardTilt,
  setLastCardTilt,
  lastCardOffsetX,
  setLastCardOffsetX,
  lastCardOffsetY,
  setLastCardOffsetY,
  lastCardOrientation,
  setLastCardOrientation,
  lastCardScale,
  setLastCardScale,
  neighborsPairShiftX,
  setNeighborsPairShiftX,
  neighborsPairShiftY,
  setNeighborsPairShiftY,
  boardStyle,
  setBoardStyle,
  boardStyles,
  boardColor,
  setBoardColor,
  headerColor,
  setHeaderColor,
  headerLetterColor,
  setHeaderLetterColor,
  cellColor,
  setCellColor,
  textColor,
  setTextColor,
  headerOpacity,
  setHeaderOpacity,
  cellOpacity,
  setCellOpacity,
  freeSpaceOpacity,
  setFreeSpaceOpacity,
  boardFont,
  setBoardFont,
  headerFont,
  setHeaderFont,
  fontOptions,
  freeSpaceText,
  setFreeSpaceText,
  showFreeSpaceEditor,
  setShowFreeSpaceEditor,
  freeSpaceColor,
  setFreeSpaceColor,
  freeSpaceTextColor,
  setFreeSpaceTextColor,
  freeSpaceNumbering,
  setFreeSpaceNumbering,
  freeSpaceTextEffect,
  setFreeSpaceTextEffect,
  freeSpaceTextThickness,
  setFreeSpaceTextThickness,
  watermarkEnabled,
  setWatermarkEnabled,
  watermarkText,
  setWatermarkText,
  watermarkOpacity,
  setWatermarkOpacity,
  watermarkColor,
  setWatermarkColor,
  watermarkFont,
  setWatermarkFont,
  templateTextEnabled,
  setTemplateTextEnabled,
  prizeDots,
  setPrizeDots,
  templateText,
  setTemplateText,
  templateTextFont,
  setTemplateTextFont,
  templateTextColor,
  setTemplateTextColor,
  templateTextOutlineEnabled,
  setTemplateTextOutlineEnabled,
  templateTextOutlineColor,
  setTemplateTextOutlineColor,
  templateTextOutlineWidth,
  setTemplateTextOutlineWidth,
  templateTextSize,
  setTemplateTextSize,
  templateTextLetterSpacing,
  setTemplateTextLetterSpacing,
  templateTextOpacity,
  setTemplateTextOpacity,
  // Extra template texts (unlimited list — paid + owner)
  extraTemplateTexts,
  addExtraTemplateText,
  updateExtraTemplateText,
  removeExtraTemplateText,
  // Saved Template Texts library (paid + owner)
  savedTemplateTexts,
  saveTemplateTextBundle,
  saveTemplateTextName,
  setSaveTemplateTextName,
  loadTemplateTextBundle,
  deleteTemplateTextBundle,
  savedTemplateTextBusy,
  callerBgVideoSrc,
  setCallerBgVideoSrc,
  callerBgVideoOpacity,
  setCallerBgVideoOpacity,
  callerBgVideoFit,
  setCallerBgVideoFit,
  obsMode,
  setObsMode,
  handleCallerBgVideoUpload,
  aiVideoPrompt,
  setAiVideoPrompt,
  aiVideoLoading,
  aiVideoElapsed,
  generateAiCallerVideo,
  isOwner,
  savedCallerVideos,
  saveCallerVideo,
  loadCallerVideo,
  deleteCallerVideo,
  savedCallerVideoBusy,
  exportAsPNG,
  exportAsPNGNoBackground,
  exportAsPDF,
  isPaidSubscriber,
  onOpenCaller,
  regenerateBoards,
  currentTemplateName,
  setCurrentTemplateName,
  savedTemplates,
  isLoadingTemplates,
  retryLoadTemplates,
  saveTemplate,
  loadTemplate,
  deleteTemplate,
  // AI-vision import of a finished Loteria template image
  importTemplateFromImage,
  importingTemplate,
  // Folder management props (Saved Templates organization)
  folders,
  selectedFolderId,
  setSelectedFolderId,
  templateSearchQuery,
  setTemplateSearchQuery,
  createFolder,
  renameFolder,
  deleteFolder,
  moveTemplateToFolder,
  markupElements,
  activeMarkupTool,
  setActiveMarkupTool,
  markupColor,
  setMarkupColor,
  markupThickness,
  setMarkupThickness,
  eraserThickness,
  setEraserThickness,
  markupTipStyle,
  setMarkupTipStyle,
  markupOpacity,
  setMarkupOpacity,
  clearMarkup,
  deleteSelectedMarkup,
  selectedMarkupId,
  markupStampImage,
  setMarkupStampImage,
  markupStampTemplate,
  resizeAllMarkupStamps,
  emojis,
  addEmoji,
  selectedEmojiId,
  updateEmojiSize,
  removeEmoji,
  filterSettings,
  updateFilter,
  applyFilterPreset,
  EMOJI_OPTIONS,
  FILTER_PRESETS,
  handleAutoMarkupSubmit,
  autoMarkupResetKey,
  autoMarkupScale,
  setAutoMarkupScale,
  calledNumbers,
  clearCalledNumbers,
  removeCalledNumber,
  gameMode,
  setGameMode,
  loteriaFigures,
  setLoteriaFigures,
  loteriaDeck,
  setLoteriaDeck,
  canvasQuality,
  setCanvasQuality,
  loteriaGridSize,
  setLoteriaGridSize,
  loteriaOneOffEnabled,
  setLoteriaOneOffEnabled,
  loteriaOneOffBoardIndex,
  setLoteriaOneOffBoardIndex,
  loteriaOneOffGridSize,
  setLoteriaOneOffGridSize,
  loteriaOneOffNumberingEnabled,
  setLoteriaOneOffNumberingEnabled,
  loteriaOneOffShowRowNums,
  setLoteriaOneOffShowRowNums,
  loteriaOneOffShowColNums,
  setLoteriaOneOffShowColNums,
  loteriaOneOffStartingNum,
  setLoteriaOneOffStartingNum,
  loteriaSingleRepeatEnabled,
  setLoteriaSingleRepeatEnabled,
  loteriaSingleRepeatBoardIndex,
  setLoteriaSingleRepeatBoardIndex,
  loteriaSingleRepeatMode,
  setLoteriaSingleRepeatMode,
  loteriaSingleRepeatGridSize,
  setLoteriaSingleRepeatGridSize,
  loteriaBoardCount,
  handleLoteriaBoardCountChange,
  loteriaCellColors,
  setLoteriaCellColors,
  loteriaColorMode,
  setLoteriaColorMode,
  loteriaRowColors,
  setLoteriaRowColors,
  loteriaColColors,
  setLoteriaColColors,
  loteriaStripeColors,
  setLoteriaStripeColors,
  loteriaBorderColor,
  setLoteriaBorderColor,
  loteriaGridColor,
  setLoteriaGridColor,
  loteriaCenterHighlight,
  setLoteriaCenterHighlight,
  loteriaCenterHighlightColor,
  setLoteriaCenterHighlightColor,
  loteriaCenterHighlightThickness,
  setLoteriaCenterHighlightThickness,
  loteriaCenterHighlightOffsetX,
  setLoteriaCenterHighlightOffsetX,
  loteriaCenterHighlightOffsetY,
  setLoteriaCenterHighlightOffsetY,
  loteriaBorderStyle,
  setLoteriaBorderStyle,
  loteriaBorderWidth,
  setLoteriaBorderWidth,
  loteriaGridWidth,
  setLoteriaGridWidth,
  loteriaBoardShadow,
  setLoteriaBoardShadow,
  loteriaGapH,
  setLoteriaGapH,
  loteriaGapV,
  setLoteriaGapV,
  loteriaRepeatMode,
  setLoteriaRepeatMode,
  loteriaRepeatHighlight,
  setLoteriaRepeatHighlight,
  loteriaRepeatHighlightColor,
  setLoteriaRepeatHighlightColor,
  loteriaRepeatHighlightThickness,
  setLoteriaRepeatHighlightThickness,
  loteriaCenterOrientation,
  setLoteriaCenterOrientation,
  loteriaNumbering,
  setLoteriaNumbering,
  regenerateLoteriaBoards,
  reshuffleLoteriaImages,
}) {
  // Background controls
  const [bgAiPrompt, setBgAiPrompt] = useState("");
  const [bgAiProvider, setBgAiProvider] = useState("openai");
  const [bgStockQuery, setBgStockQuery] = useState("");
  const [bgStockImages, setBgStockImages] = useState([]);
  const [bgStockLoading, setBgStockLoading] = useState(false);
  const [refImage, setRefImage] = useState(null);
  const [refProvider, setRefProvider] = useState("openai");
  const [refExtraPrompt, setRefExtraPrompt] = useState("");

  // Overlay controls (completely separate)
  const [overlayAiPrompt, setOverlayAiPrompt] = useState("");
  const [overlayAiProvider, setOverlayAiProvider] = useState("openai");
  const [overlayStockQuery, setOverlayStockQuery] = useState("");
  const [overlayStockImages, setOverlayStockImages] = useState([]);
  const [overlayStockLoading, setOverlayStockLoading] = useState(false);

  const [showSavedTemplates, setShowSavedTemplates] = useState(false);
  // Folder UI affordances — kept local to Sidebar so they don't pollute the
  // upper-level state.
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renamingFolderName, setRenamingFolderName] = useState("");
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [moveMenuTemplateId, setMoveMenuTemplateId] = useState(null);
  // Folder-list collapse toggle — starts collapsed so the search bar stays
  // uncluttered. A chevron icon toggles visibility of the folder pills row.
  const [foldersExpanded, setFoldersExpanded] = useState(false);

  // Background image handlers
  const handleBgAIGenerate = async () => {
    if (!bgAiPrompt.trim()) return;
    await generateBackgroundImage(bgAiPrompt, bgAiProvider);
  };

  const handleBgStockSearch = async () => {
    if (!bgStockQuery.trim()) return;
    setBgStockLoading(true);
    const images = await searchStockImages(bgStockQuery);
    setBgStockImages(images);
    setBgStockLoading(false);
  };

  const handleBgFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadBackgroundImage(file);
  };

  // Overlay image handlers (separate)
  const handleOverlayAIGenerate = async () => {
    if (!overlayAiPrompt.trim()) return;
    await generateOverlayImage(overlayAiPrompt, overlayAiProvider);
  };

  const handleOverlayStockSearch = async () => {
    if (!overlayStockQuery.trim()) return;
    setOverlayStockLoading(true);
    const images = await searchStockImages(overlayStockQuery);
    setOverlayStockImages(images);
    setOverlayStockLoading(false);
  };

  const handleOverlayFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadOverlayImage(file);
  };

  // Color picker component (replaces dots with spectrum picker)
  const ColorPicker = ({ value, onChange, label, testId }) => (
    <div className="flex items-center gap-2">
      <Label className={`font-semibold text-xs flex-shrink-0 ${isDarkMode ? 'text-neutral-300' : ''}`}>{label}</Label>
      <div className="flex items-center gap-2 flex-1 justify-end">
        <label className="relative w-8 h-8 rounded cursor-pointer border-2 block flex-shrink-0" style={{ borderColor: isDarkMode ? '#4B5563' : '#000', backgroundColor: value }}>
          <input
            data-testid={testId}
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9a-fA-F]{0,6}$/.test(v) && v.length === 7) onChange(v);
          }}
          className={`w-20 text-xs font-mono px-2 py-1 border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'border-gray-300 bg-white'}`}
        />
      </div>
    </div>
  );

  // Collapsible Steps — clicking the header toggles visibility. Set holds keys
  // of CURRENTLY-COLLAPSED steps. Default on FIRST OPEN: all collapsed so users
  // see a clean overview with descriptions, then expand only what they need.
  // Persists in sessionStorage so it survives reloads within the same browser tab.
  const ALL_STEP_KEYS = [
    'step-background', 'step-overlay', 'step-bingo-boards', 'step-loteria-boards',
    'step-free-space', 'step-board-numbering', 'step-template-text', 'step-markup',
    'step-emojis', 'step-filters', 'step-prize-dots', 'step-watermark',
  ];
  const [collapsedSteps, setCollapsedSteps] = useState(() => {
    try {
      const raw = sessionStorage.getItem('sidebarCollapsedSteps');
      if (raw) return new Set(JSON.parse(raw));
    } catch (_) { /* ignore */ }
    return new Set(ALL_STEP_KEYS);
  });
  const toggleStep = (key) => {
    setCollapsedSteps(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { sessionStorage.setItem('sidebarCollapsedSteps', JSON.stringify([...next])); } catch (_) { /* ignore */ }
      return next;
    });
  };

  // Step header component — now clickable, with chevron + collapsed-only
  // description so users see what each step does at a glance.
  const StepHeader = ({ number, title, icon: Icon, stepKey, description }) => {
    const isCollapsed = stepKey ? collapsedSteps.has(stepKey) : false;
    return (
      <button
        type="button"
        data-testid={stepKey ? `step-header-${stepKey}` : undefined}
        onClick={() => stepKey && toggleStep(stepKey)}
        className={`w-full text-left mb-2 ${isDarkMode ? 'text-white' : 'text-black'} ${stepKey ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      >
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black bg-[#FF6B6B] text-white">
            {number}
          </div>
          <Icon className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-neutral-800'}`} strokeWidth={2.5} />
          <span className={`text-sm font-bold flex-1 ${isDarkMode ? 'text-white' : 'text-black'}`}>{title}</span>
          {stepKey && (
            isCollapsed
              ? <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`} />
              : <ChevronDown className={`w-4 h-4 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`} />
          )}
        </div>
        {isCollapsed && description && (
          <p className={`mt-1 text-[11px] leading-snug pl-7 ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
            {description}
          </p>
        )}
      </button>
    );
  };

  // Styled select
  const StyledSelect = ({ value, onValueChange, children, testId, placeholder }) => (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger 
        data-testid={testId}
        className={`w-full border-2 px-4 py-2 font-medium ${
          isDarkMode 
            ? 'bg-neutral-800 border-neutral-600 text-white' 
            : 'bg-white border-black text-black'
        }`}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={`border-2 ${isDarkMode ? 'border-neutral-600 bg-neutral-800 text-white' : 'border-black bg-white'}`}>
        {children}
      </SelectContent>
    </Select>
  );

  return (
    <aside 
      data-testid="sidebar" 
      className={`w-[340px] min-w-[340px] border-r-2 flex flex-col h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-neutral-950 border-neutral-800' : 'bg-white border-black'
      }`}
    >
      {/* Header */}
      <div className={`p-3 border-b-2 ${isDarkMode ? 'border-neutral-800 bg-black' : 'border-black bg-red-600'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-black tracking-tight">
              <span className="text-white">BingoGrid</span>{' '}
              <span className={isDarkMode ? 'text-red-500' : 'text-black'}>AI</span>
            </h1>
            <p className={`text-[10px] font-medium ${isDarkMode ? 'text-neutral-400' : 'text-white/80'}`}>
              Create step by step
            </p>
          </div>
          {/* Game Mode Switcher - centered */}
          <div className={`flex border-2 rounded overflow-hidden ${isDarkMode ? 'border-neutral-600' : 'border-white/40'}`}>
            <button data-testid="mode-loteria" onClick={() => setGameMode('loteria')}
              className={`px-3 py-1.5 text-[10px] font-black tracking-wide transition-colors ${gameMode === 'loteria'
                ? 'bg-white text-amber-600'
                : isDarkMode ? 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700' : 'bg-red-700/60 text-white/70 hover:bg-red-700/80'
              }`}>LOTERIA</button>
            <button data-testid="mode-bingo" onClick={() => setGameMode('bingo')}
              className={`px-3 py-1.5 text-[10px] font-black tracking-wide transition-colors ${gameMode === 'bingo'
                ? 'bg-white text-red-600'
                : isDarkMode ? 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700' : 'bg-red-700/60 text-white/70 hover:bg-red-700/80'
              }`}>BINGO</button>
          </div>
          <button
            data-testid="sidebar-theme-toggle"
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`p-2 border-2 transition-colors ${
              isDarkMode 
                ? 'border-neutral-600 bg-yellow-500 text-neutral-900 hover:bg-yellow-400' 
                : 'border-white/50 bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            {isDarkMode ? <Sun className="w-5 h-5" strokeWidth={2.5} /> : <Moon className="w-5 h-5" strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-4">
          
          {/* Save/Load Section */}
          <section className={`border-2 p-3 ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-black bg-gray-50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Save className={`w-3.5 h-3.5 ${isDarkMode ? 'text-white' : 'text-black'}`} strokeWidth={2.5} />
              <span className={`font-bold text-xs ${isDarkMode ? 'text-white' : 'text-black'}`}>Save / Load</span>
              <span
                data-testid="build-tag"
                className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}
                title="Build tag — confirms latest UI is loaded"
              >v3.15</span>
            </div>
            <Input
              data-testid="template-name-input"
              placeholder="Template name..."
              value={currentTemplateName}
              onChange={(e) => setCurrentTemplateName(e.target.value)}
              className={`mb-2 border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-400' : 'border-black'}`}
            />
            <div className="flex gap-2 mb-2">
              <button
                data-testid="save-progress-btn"
                onClick={() => saveTemplate(false)}
                className={`flex-1 py-2 px-3 text-xs font-bold border-2 flex items-center justify-center gap-1 ${
                  isDarkMode ? 'border-neutral-600 text-white hover:bg-neutral-800' : 'border-black hover:bg-gray-100'
                }`}
              >
                <Save className="w-3 h-3" /> Save Progress
              </button>
              <button
                data-testid="save-finished-btn"
                onClick={() => saveTemplate(true)}
                className="flex-1 py-2 px-3 text-xs font-bold bg-[#4ECDC4] text-black border-2 border-[#4ECDC4] flex items-center justify-center gap-1 hover:bg-[#3dbdb5]"
              >
                <Save className="w-3 h-3" /> Save Finished
              </button>
            </div>
            <button
              data-testid="toggle-saved-templates"
              onClick={() => setShowSavedTemplates(!showSavedTemplates)}
              className={`w-full py-2 text-xs font-bold border-2 flex items-center justify-center gap-1 ${
                isDarkMode ? 'border-neutral-600 text-white hover:bg-neutral-800' : 'border-black hover:bg-gray-100'
              }`}
            >
              <FolderOpen className="w-3 h-3" /> {showSavedTemplates ? 'Hide' : 'Show'} Saved ({isLoadingTemplates ? '…' : savedTemplates.length})
            </button>
            {showSavedTemplates && (
              <div className="mt-2 space-y-2">
                {/* ── Search input — compact width so it doesn't dominate
                    the panel. Placeholder trimmed to fit. */}
                <div className={`relative flex items-center border-2 max-w-[220px] ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-gray-300 bg-white'}`}>
                  <Search className={`w-3 h-3 ml-2 ${isDarkMode ? 'text-neutral-500' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    value={templateSearchQuery || ""}
                    onChange={(e) => setTemplateSearchQuery && setTemplateSearchQuery(e.target.value)}
                    placeholder="Search…"
                    data-testid="templates-search-input"
                    className={`flex-1 min-w-0 px-2 py-1 text-[11px] bg-transparent outline-none ${isDarkMode ? 'text-neutral-200 placeholder-neutral-500' : 'text-gray-800 placeholder-gray-400'}`}
                  />
                  {templateSearchQuery && (
                    <button
                      data-testid="templates-search-clear"
                      onClick={() => setTemplateSearchQuery && setTemplateSearchQuery("")}
                      className={`px-2 text-[10px] font-bold ${isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                    >✕</button>
                  )}
                </div>

                {/* ── Folders toggle — click chevron to expand/collapse the
                    folder pill row. Keeps the search bar uncluttered when you
                    have many folders. The selected folder name (or "All")
                    shows next to the toggle so you know the active filter
                    even while collapsed. */}
                <div className="flex items-center gap-2 mt-1">
                  <button
                    type="button"
                    data-testid="folders-toggle"
                    onClick={() => setFoldersExpanded(v => !v)}
                    className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold border-2 transition-colors ${
                      isDarkMode
                        ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                    title={foldersExpanded ? 'Hide folders' : 'Show folders'}
                  >
                    {foldersExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <FolderOpen className="w-3 h-3" />
                    Folders
                  </button>
                  {!foldersExpanded && (
                    <span
                      className={`text-[10px] font-mono truncate ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}
                      title="Active folder"
                    >
                      {selectedFolderId === null
                        ? `All (${savedTemplates.length})`
                        : selectedFolderId === '__unfiled__'
                          ? `Unfiled (${savedTemplates.filter(t => !t.folder_id).length})`
                          : (() => {
                              const f = (folders || []).find(x => x.id === selectedFolderId);
                              if (!f) return 'All';
                              const c = savedTemplates.filter(t => t.folder_id === f.id).length;
                              return `${f.name} (${c})`;
                            })()}
                    </span>
                  )}
                </div>

                {/* ── Folder pills ─────────────────────────────────────── */}
                {foldersExpanded && (
                <div className={`flex flex-col gap-1 mt-1 p-1 border-2 rounded-sm max-h-56 overflow-y-auto ${isDarkMode ? 'border-neutral-800 bg-neutral-900/40' : 'border-gray-200 bg-gray-50/60'}`}>
                  {/* Built-in: All Templates */}
                  <button
                    data-testid="folder-pill-all"
                    onClick={() => setSelectedFolderId && setSelectedFolderId(null)}
                    className={`w-full px-2 py-1 text-[10px] font-bold border-2 transition-colors flex items-center gap-1 justify-start ${
                      (selectedFolderId === null)
                        ? 'bg-amber-500 text-black border-amber-600'
                        : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <FolderOpen className="w-3 h-3" />
                    All ({savedTemplates.length})
                  </button>
                  {/* Built-in: Unfiled (only show if there are unfiled templates) */}
                  {(() => {
                    const unfiledCount = savedTemplates.filter(t => !t.folder_id).length;
                    if (unfiledCount === 0) return null;
                    return (
                      <button
                        data-testid="folder-pill-unfiled"
                        onClick={() => setSelectedFolderId && setSelectedFolderId("__unfiled__")}
                        className={`w-full px-2 py-1 text-[10px] font-bold border-2 transition-colors flex items-center gap-1 justify-start ${
                          (selectedFolderId === "__unfiled__")
                            ? 'bg-amber-500 text-black border-amber-600'
                            : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                        onDragOver={(e) => { e.preventDefault(); setDragOverFolderId("__unfiled__"); }}
                        onDragLeave={() => setDragOverFolderId(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          const tid = e.dataTransfer.getData("text/template-id");
                          if (tid && moveTemplateToFolder) moveTemplateToFolder(tid, null);
                          setDragOverFolderId(null);
                        }}
                        style={dragOverFolderId === "__unfiled__" ? { outline: '2px dashed #f59e0b' } : {}}
                      >
                        <Folder className="w-3 h-3" />
                        Unfiled ({unfiledCount})
                      </button>
                    );
                  })()}
                  {/* Custom folders */}
                  {(folders || []).map((f) => {
                    const count = savedTemplates.filter(t => t.folder_id === f.id).length;
                    const isSel = selectedFolderId === f.id;
                    const isRenaming = renamingFolderId === f.id;
                    if (isRenaming) {
                      return (
                        <div key={f.id} className="flex items-center gap-1 w-full">
                          <input
                            autoFocus
                            type="text"
                            value={renamingFolderName}
                            onChange={(e) => setRenamingFolderName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                renameFolder && renameFolder(f.id, renamingFolderName);
                                setRenamingFolderId(null);
                              } else if (e.key === 'Escape') {
                                setRenamingFolderId(null);
                              }
                            }}
                            data-testid={`folder-rename-input-${f.id}`}
                            className={`flex-1 px-2 py-1 text-[10px] border-2 ${isDarkMode ? 'bg-neutral-900 border-amber-500 text-white' : 'bg-white border-amber-500 text-black'}`}
                          />
                          <button
                            data-testid={`folder-rename-save-${f.id}`}
                            onClick={() => { renameFolder && renameFolder(f.id, renamingFolderName); setRenamingFolderId(null); }}
                            className="px-2 py-1 text-[10px] font-bold bg-green-500 text-white"
                          >OK</button>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={f.id}
                        className={`group flex items-center border-2 transition-colors w-full ${
                          isSel
                            ? 'bg-amber-500 text-black border-amber-600'
                            : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                        onDragOver={(e) => { e.preventDefault(); setDragOverFolderId(f.id); }}
                        onDragLeave={() => setDragOverFolderId(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          const tid = e.dataTransfer.getData("text/template-id");
                          if (tid && moveTemplateToFolder) moveTemplateToFolder(tid, f.id);
                          setDragOverFolderId(null);
                        }}
                        style={dragOverFolderId === f.id ? { outline: '2px dashed #f59e0b' } : {}}
                      >
                        <button
                          data-testid={`folder-pill-${f.id}`}
                          onClick={() => setSelectedFolderId && setSelectedFolderId(f.id)}
                          className="flex-1 px-2 py-1 text-[10px] font-bold flex items-center gap-1 text-left justify-start truncate"
                          title={f.name}
                        >
                          <Folder className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{f.name} ({count})</span>
                        </button>
                        {/* Rename / Delete affordances appear on hover */}
                        <button
                          data-testid={`folder-rename-${f.id}`}
                          onClick={(e) => { e.stopPropagation(); setRenamingFolderId(f.id); setRenamingFolderName(f.name); }}
                          className={`px-1.5 py-1 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity ${isSel ? 'text-black' : isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                          title="Rename folder"
                        ><Edit2 className="w-3 h-3" /></button>
                        <button
                          data-testid={`folder-delete-${f.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const inside = savedTemplates.filter(t => t.folder_id === f.id).length;
                            const msg = inside > 0
                              ? `Delete folder "${f.name}"? Its ${inside} template${inside === 1 ? '' : 's'} will be moved to Unfiled.`
                              : `Delete empty folder "${f.name}"?`;
                            if (window.confirm(msg)) deleteFolder && deleteFolder(f.id);
                          }}
                          className={`px-1.5 py-1 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity ${isSel ? 'text-red-700' : 'text-red-500 hover:text-red-700'}`}
                          title="Delete folder"
                        ><Trash2 className="w-3 h-3" /></button>
                      </div>
                    );
                  })}
                  {/* "+ New Folder" row / inline input */}
                  {showNewFolderInput ? (
                    <div className="flex items-center gap-1 w-full">
                      <input
                        autoFocus
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            const ok = await (createFolder && createFolder(newFolderName));
                            if (ok) { setNewFolderName(""); setShowNewFolderInput(false); }
                          } else if (e.key === 'Escape') {
                            setNewFolderName(""); setShowNewFolderInput(false);
                          }
                        }}
                        placeholder="Folder name…"
                        data-testid="new-folder-input"
                        className={`flex-1 px-2 py-1 text-[10px] border-2 ${isDarkMode ? 'bg-neutral-900 border-amber-500 text-white placeholder-neutral-500' : 'bg-white border-amber-500 text-black placeholder-gray-400'}`}
                      />
                      <button
                        data-testid="new-folder-save"
                        onClick={async () => {
                          const ok = await (createFolder && createFolder(newFolderName));
                          if (ok) { setNewFolderName(""); setShowNewFolderInput(false); }
                        }}
                        className="px-2 py-1 text-[10px] font-bold bg-green-500 text-white"
                      >OK</button>
                      <button
                        data-testid="new-folder-cancel"
                        onClick={() => { setNewFolderName(""); setShowNewFolderInput(false); }}
                        className={`px-2 py-1 text-[10px] font-bold ${isDarkMode ? 'border-2 border-neutral-600 text-neutral-300' : 'border-2 border-gray-300 text-gray-600'}`}
                      >✕</button>
                    </div>
                  ) : (
                    <button
                      data-testid="new-folder-btn"
                      onClick={() => setShowNewFolderInput(true)}
                      className={`w-full px-2 py-1 text-[10px] font-bold border-2 border-dashed transition-colors flex items-center gap-1 justify-start ${isDarkMode ? 'border-neutral-600 text-neutral-400 hover:bg-neutral-800 hover:text-white' : 'border-gray-400 text-gray-600 hover:bg-gray-100 hover:text-black'}`}
                      title="New folder"
                    >
                      <FolderPlus className="w-3 h-3" />
                      New Folder
                    </button>
                  )}
                </div>
                )}

                {/* ── Filtered template list ───────────────────────────── */}
                {(() => {
                  const q = (templateSearchQuery || "").toLowerCase().trim();
                  const folderById = Object.fromEntries((folders || []).map(f => [f.id, f.name]));
                  let visible = savedTemplates;
                  // Main drop ("All") intentionally hides templates that are
                  // filed inside a folder — user wants folder templates to
                  // ONLY appear when that folder is selected. Global search
                  // (below) can still surface them by name/folder-name match.
                  if (selectedFolderId === null) {
                    visible = visible.filter(t => !t.folder_id);
                  } else if (selectedFolderId === "__unfiled__") {
                    visible = visible.filter(t => !t.folder_id);
                  } else if (selectedFolderId) {
                    visible = visible.filter(t => t.folder_id === selectedFolderId);
                  }
                  // When user is searching, expand scope to ALL templates so
                  // a name/folder-name match can find items across folders.
                  if (q) {
                    visible = savedTemplates.filter(t => {
                      const nameMatch = (t.name || "").toLowerCase().includes(q);
                      const folderMatch = t.folder_id && folderById[t.folder_id] && folderById[t.folder_id].toLowerCase().includes(q);
                      return nameMatch || folderMatch;
                    });
                  }
                  if (visible.length === 0) {
                    return (
                      <div className={`mt-2 p-3 text-[11px] text-center border border-dashed ${isDarkMode ? 'border-neutral-700 text-neutral-500' : 'border-gray-300 text-gray-500'}`}>
                        {savedTemplates.length === 0 ? 'No saved templates yet.' : (q ? 'No matches for your search.' : 'This folder is empty.')}
                      </div>
                    );
                  }
                  return (
                    <div
                      className="mt-2 space-y-1 max-h-[40vh] overflow-y-auto pr-1"
                      style={{ scrollbarGutter: 'stable' }}
                      data-testid="templates-list-filtered"
                    >
                      {visible.map((t) => {
                        const MAX_LEN = 18;
                        const display = t.name.length > MAX_LEN ? t.name.slice(0, MAX_LEN) + '…' : t.name;
                        const inFolderName = t.folder_id ? folderById[t.folder_id] : null;
                        return (
                          <div
                            key={t.id}
                            draggable
                            onDragStart={(e) => { e.dataTransfer.setData("text/template-id", t.id); e.dataTransfer.effectAllowed = "move"; }}
                            className={`flex items-center gap-1.5 p-1.5 border min-w-0 transition-colors ${isDarkMode ? 'border-neutral-700 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-50'}`}
                          >
                            <button
                              type="button"
                              data-testid={`load-template-${t.id}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => loadTemplate(t.id)}
                              className={`flex-1 min-w-0 truncate text-left text-xs font-medium ${isDarkMode ? 'text-neutral-200 hover:text-white' : 'text-gray-800 hover:text-black'}`}
                              title={`Load: ${t.name}${inFolderName ? `  (in folder: ${inFolderName})` : ''}`}
                            >
                              {display} {t.isFinished && '✓'}
                              {inFolderName && selectedFolderId === null && (
                                <span className={`ml-1 text-[9px] font-normal ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>· {inFolderName}</span>
                              )}
                            </button>
                            <button
                              type="button"
                              data-testid={`load-template-btn-${t.id}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => loadTemplate(t.id)}
                              className="flex-shrink-0 px-2 py-0.5 text-[10px] font-bold rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                              title="Load template"
                            >Load</button>
                            {/* Move-to-folder mini menu */}
                            <div className="relative flex-shrink-0">
                              <button
                                type="button"
                                data-testid={`move-template-btn-${t.id}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setMoveMenuTemplateId(moveMenuTemplateId === t.id ? null : t.id)}
                                className={`px-2 py-0.5 text-[10px] font-bold rounded ${isDarkMode ? 'bg-neutral-700 text-neutral-200 hover:bg-neutral-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} transition-colors`}
                                title="Move to folder"
                              >Move</button>
                              {moveMenuTemplateId === t.id && (
                                <div
                                  className={`absolute right-0 top-full mt-1 z-50 border-2 shadow-lg min-w-[140px] ${isDarkMode ? 'bg-neutral-900 border-neutral-600' : 'bg-white border-gray-300'}`}
                                  data-testid={`move-menu-${t.id}`}
                                >
                                  <button
                                    data-testid={`move-menu-item-${t.id}-unfiled`}
                                    onClick={() => { moveTemplateToFolder && moveTemplateToFolder(t.id, null); setMoveMenuTemplateId(null); }}
                                    className={`block w-full px-3 py-1.5 text-[10px] text-left ${!t.folder_id ? 'bg-amber-500 text-black' : isDarkMode ? 'text-neutral-200 hover:bg-neutral-800' : 'text-gray-800 hover:bg-gray-100'}`}
                                  >Unfiled</button>
                                  {(folders || []).map(f => (
                                    <button
                                      key={f.id}
                                      data-testid={`move-menu-item-${t.id}-${f.id}`}
                                      onClick={() => { moveTemplateToFolder && moveTemplateToFolder(t.id, f.id); setMoveMenuTemplateId(null); }}
                                      className={`block w-full px-3 py-1.5 text-[10px] text-left ${t.folder_id === f.id ? 'bg-amber-500 text-black' : isDarkMode ? 'text-neutral-200 hover:bg-neutral-800' : 'text-gray-800 hover:bg-gray-100'}`}
                                    >{f.name}</button>
                                  ))}
                                  {(folders || []).length === 0 && (
                                    <div className={`px-3 py-1.5 text-[10px] italic ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                                      No folders yet — create one above.
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              data-testid={`delete-template-${t.id}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Are you sure you want to delete "${t.name}"? This cannot be undone.`)) {
                                  deleteTemplate(t.id);
                                }
                              }}
                              className="flex-shrink-0 px-2 py-0.5 text-[10px] font-bold rounded bg-red-500 text-white hover:bg-red-600 transition-colors"
                              title="Delete template"
                            >✕</button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </section>

          {/* STEP 1: Background */}
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={1} title="Background" icon={Layers}  stepKey="step-background" description="Set the canvas size, solid color, gradient, or background image." />
              {!collapsedSteps.has("step-background") && (<>
            
            {/* Template Size */}
            <div className="mb-4">
              <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Template Size</Label>
              <StyledSelect value={templateSize} onValueChange={setTemplateSize} testId="template-size-select" placeholder="Select size">
                {Object.entries(templateSizes).map(([key, size]) => (
                  <SelectItem key={key} value={key} className="font-medium">{size.label}</SelectItem>
                ))}
              </StyledSelect>
            </div>

            {/* Canvas Quality (DPI) */}
            <div className="mb-4">
              <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Canvas Quality</Label>
              <div className={`flex border-2 overflow-hidden ${isDarkMode ? 'border-neutral-700' : 'border-black'}`}>
                {[
                  { v: 150, label: 'SD' },
                  { v: 220, label: 'HD' },
                  { v: 300, label: '4K' },
                ].map((q) => (
                  <button
                    key={q.v}
                    data-testid={`canvas-quality-${q.v}`}
                    onClick={() => setCanvasQuality(q.v)}
                    className={`flex-1 py-2 text-xs font-bold transition-colors ${
                      canvasQuality === q.v
                        ? (isDarkMode ? 'bg-[#FFD700] text-black' : 'bg-black text-white')
                        : (isDarkMode ? 'bg-neutral-900 text-neutral-300 hover:bg-neutral-800' : 'bg-white text-black hover:bg-gray-100')
                    }`}
                  >{q.label}</button>
                ))}
              </div>
              <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                Higher = crisper screenshots/recordings. Lower = faster editing.
              </p>
            </div>

            {/* Background Color */}
            <div className="mb-4">
              <ColorPicker value={backgroundColor} onChange={setBackgroundColor} label="Background Color" testId="bg-color-picker" />
            </div>

            {/* Background Image */}
            <div className="mb-3">
              <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Background Image (optional)</Label>
            </div>
            
            <Tabs defaultValue="ai" className="w-full">
              <TabsList className={`grid w-full grid-cols-4 border-2 rounded-none h-auto p-0 ${isDarkMode ? 'border-neutral-600 bg-neutral-800' : 'border-black'}`}>
                <TabsTrigger value="ai" className={`data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white font-bold py-2 rounded-none border-r-2 ${isDarkMode ? 'border-neutral-600 text-neutral-300' : 'border-black'}`}>
                  <Sparkles className="w-4 h-4 mr-1" /> AI
                </TabsTrigger>
                <TabsTrigger value="ref" className={`data-[state=active]:bg-[#A78BFA] data-[state=active]:text-white font-bold py-2 rounded-none border-r-2 ${isDarkMode ? 'border-neutral-600 text-neutral-300' : 'border-black'}`}>
                  <Camera className="w-4 h-4 mr-1" /> Ref
                </TabsTrigger>
                <TabsTrigger value="stock" className={`data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-black font-bold py-2 rounded-none border-r-2 ${isDarkMode ? 'border-neutral-600 text-neutral-300' : 'border-black'}`}>
                  <Search className="w-4 h-4 mr-1" /> Stock
                </TabsTrigger>
                <TabsTrigger value="upload" className={`data-[state=active]:bg-[#FDE047] data-[state=active]:text-black font-bold py-2 rounded-none ${isDarkMode ? 'text-neutral-300' : ''}`}>
                  <Upload className="w-4 h-4 mr-1" /> Upload
                </TabsTrigger>
              </TabsList>

              <TabsContent value="ai" className="mt-3 space-y-2">
                <Input placeholder="Describe background..." value={bgAiPrompt} onChange={(e) => setBgAiPrompt(e.target.value)} className={`border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-400' : 'border-black'}`} />
                <StyledSelect value={bgAiProvider} onValueChange={setBgAiProvider} testId="bg-ai-provider" placeholder="AI Provider">
                  <SelectItem value="openai" className="font-medium">OpenAI GPT Image</SelectItem>
                  <SelectItem value="gemini" className="font-medium">Gemini Nano Banana</SelectItem>
                </StyledSelect>
                <button onClick={handleBgAIGenerate} disabled={backgroundImageLoading || !bgAiPrompt.trim()} className="neo-button-primary w-full disabled:opacity-50 flex items-center justify-center gap-2">
                  {backgroundImageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate Background
                </button>
              </TabsContent>

              <TabsContent value="ref" className="mt-3 space-y-2">
                {!refImage ? (
                  <label className={`cursor-pointer flex flex-col items-center justify-center py-4 border-2 transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-600 hover:bg-neutral-700' : 'border-black hover:bg-gray-50'}`}>
                    <Camera className={`w-6 h-6 mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-neutral-500'}`} />
                    <span className={`text-xs ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>Upload Reference Photo</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onloadend = () => setRefImage(reader.result);
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }} />
                  </label>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <img src={refImage} alt="Reference" className={`w-full h-20 object-cover border-2 ${isDarkMode ? 'border-neutral-600' : 'border-black'}`} />
                      <button onClick={() => setRefImage(null)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px] font-bold">X</button>
                    </div>
                    <Input placeholder="Extra instructions (optional)" value={refExtraPrompt} onChange={(e) => setRefExtraPrompt(e.target.value)} className={`border-2 text-xs ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-400' : 'border-black'}`} />
                    <StyledSelect value={refProvider} onValueChange={setRefProvider} testId="ref-ai-provider" placeholder="AI Provider">
                      <SelectItem value="openai" className="font-medium">OpenAI GPT Image</SelectItem>
                      <SelectItem value="gemini" className="font-medium">Gemini Nano Banana</SelectItem>
                    </StyledSelect>
                    <button onClick={() => { if (generateBackgroundFromReference) generateBackgroundFromReference(refImage, refProvider, refExtraPrompt); }} disabled={backgroundImageLoading} className="neo-button-primary w-full disabled:opacity-50 flex items-center justify-center gap-2">
                      {backgroundImageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Recreate Background
                    </button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="stock" className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <Input placeholder="Search backgrounds..." value={bgStockQuery} onChange={(e) => setBgStockQuery(e.target.value)} className={`flex-1 border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-400' : 'border-black'}`} onKeyDown={(e) => e.key === "Enter" && handleBgStockSearch()} />
                  <button onClick={handleBgStockSearch} disabled={bgStockLoading} className="neo-button-secondary px-4">
                    {bgStockLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </button>
                </div>
                {bgStockImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-1 max-h-24 overflow-y-auto">
                    {bgStockImages.map((img, i) => (
                      <button key={i} onClick={() => selectBackgroundStockImage(img.url)} className={`border-2 overflow-hidden hover:opacity-80 ${isDarkMode ? 'border-neutral-600' : 'border-black'}`}>
                        <img src={img.thumbnail} alt={img.alt} className="w-full h-12 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="upload" className="mt-3">
                <label className={`cursor-pointer flex flex-col items-center justify-center py-4 border-2 transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-600 hover:bg-neutral-700' : 'border-black hover:bg-gray-50'}`}>
                  <Upload className={`w-6 h-6 mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-neutral-500'}`} />
                  <span className={`font-bold text-xs ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Upload Background</span>
                  <input type="file" accept="image/*" onChange={handleBgFileUpload} className="hidden" />
                </label>
              </TabsContent>
            </Tabs>

            {hasBackground && (
              <button onClick={clearBackground} className={`mt-2 w-full py-2 border-2 font-bold text-red-500 text-xs flex items-center justify-center gap-1 ${isDarkMode ? 'border-neutral-600 hover:bg-red-900/30' : 'border-black hover:bg-red-50'}`}>
                <Trash2 className="w-3 h-3" /> Clear Background Image
              </button>
            )}

            {/* Background Fit Mode — controls how a non-matching aspect ratio
                image is fit into the template canvas. Defaults to Cover for
                back-compat. Contain shows the whole image with side bars,
                Stretch distorts to fit exactly. */}
            {hasBackground && setBackgroundImageFit && (
              <div className="mt-2">
                <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Background Fit</Label>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { id: 'cover', label: 'Cover', desc: 'Fill canvas, may crop' },
                    { id: 'contain', label: 'Fit', desc: 'Show whole image' },
                    { id: 'stretch', label: 'Stretch', desc: 'Distort to fit' },
                  ].map(m => (
                    <button
                      key={m.id}
                      data-testid={`bg-fit-${m.id}`}
                      onClick={() => setBackgroundImageFit(m.id)}
                      title={m.desc}
                      className={`py-1.5 text-[10px] font-bold border-2 ${
                        (backgroundImageFit || 'cover') === m.id
                          ? 'bg-fuchsia-500 text-white border-fuchsia-600'
                          : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                      }`}
                    >{m.label}</button>
                  ))}
                </div>
                <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                  Use <b>Fit</b> to show the entire image without cropping.
                </p>

                {/* Background Zoom — pinch the uploaded background image in
                    or out so it fits the template visually. 1.00× = the
                    default size chosen by the Fit Mode above. Range 0.25× → 4×. */}
                <div className="mt-3">
                  <div className="flex items-center justify-between">
                    <Label className={`font-bold text-[10px] ${isDarkMode ? 'text-neutral-300' : ''}`}>
                      Background Zoom
                    </Label>
                    <span className={`text-[10px] font-mono ${isDarkMode ? 'text-fuchsia-400' : 'text-fuchsia-600'}`}>
                      {(typeof backgroundImageScale === 'number' ? backgroundImageScale : 1).toFixed(2)}×
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <button
                      data-testid="bg-zoom-out"
                      onClick={() => setBackgroundImageScale && setBackgroundImageScale(Math.max(0.25, ((typeof backgroundImageScale === 'number' ? backgroundImageScale : 1) - 0.1)))}
                      className={`flex-shrink-0 w-6 h-6 text-[12px] font-bold border-2 ${isDarkMode ? 'border-neutral-600 text-neutral-200 hover:bg-neutral-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                      title="Zoom out"
                    >−</button>
                    <input
                      type="range"
                      min="0.25"
                      max="4"
                      step="0.05"
                      value={typeof backgroundImageScale === 'number' ? backgroundImageScale : 1}
                      onChange={(e) => setBackgroundImageScale && setBackgroundImageScale(parseFloat(e.target.value))}
                      data-testid="bg-zoom-slider"
                      className="flex-1 accent-fuchsia-500"
                    />
                    <button
                      data-testid="bg-zoom-in"
                      onClick={() => setBackgroundImageScale && setBackgroundImageScale(Math.min(4, ((typeof backgroundImageScale === 'number' ? backgroundImageScale : 1) + 0.1)))}
                      className={`flex-shrink-0 w-6 h-6 text-[12px] font-bold border-2 ${isDarkMode ? 'border-neutral-600 text-neutral-200 hover:bg-neutral-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                      title="Zoom in"
                    >+</button>
                    <button
                      data-testid="bg-zoom-reset"
                      onClick={() => setBackgroundImageScale && setBackgroundImageScale(1.0)}
                      className={`flex-shrink-0 px-1.5 h-6 text-[9px] font-bold border-2 ${isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                      title="Reset to 1×"
                    >1×</button>
                  </div>
                </div>
              </div>
            )}

            {/* Live Caller Animated Background — OWNER-ONLY. Plays only inside the
                Live Bingo Caller (not on this canvas). For best effect, set
                Background Color to transparent so the animation shows through. */}
            {isOwner && (
            <div className={`mt-4 pt-3 border-t-2 ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
              <Label className={`font-semibold text-sm mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                Live Caller Background <span className="text-[10px] font-normal text-fuchsia-500">(animation)</span>
              </Label>
              <p className={`text-[10px] mb-2 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                Plays muted on loop only inside the <b>Live Bingo Caller</b>. Upload your own MP4 / WebM clip, or paste a direct video URL. <i>Tip: clear the Background Color above so the video shows through.</i>
              </p>

              {/* Upload + URL */}
              <div className="flex gap-1 mb-2">
                <label className={`flex-1 cursor-pointer text-center py-1.5 border-2 text-[10px] font-bold transition-colors ${isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-black text-black hover:bg-gray-100'}`}>
                  Upload Video
                  <input type="file" accept="video/mp4,video/webm,video/quicktime,video/*" className="hidden"
                    onChange={(e) => handleCallerBgVideoUpload && handleCallerBgVideoUpload(e.target.files?.[0])}
                    data-testid="caller-bg-upload" />
                </label>
              </div>

              {/* AI Generate (Sora 2 text-to-video) */}
              <div className={`mb-2 p-2 border-2 ${isDarkMode ? 'border-fuchsia-700 bg-fuchsia-950/30' : 'border-fuchsia-300 bg-fuchsia-50'}`}>
                <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-fuchsia-300' : 'text-fuchsia-700'}`}>
                  ✨ AI Generate Animation
                </Label>
                <textarea
                  data-testid="ai-video-prompt"
                  value={aiVideoPrompt || ''}
                  onChange={(e) => setAiVideoPrompt && setAiVideoPrompt(e.target.value)}
                  placeholder="e.g., golden confetti slowly falling on black background"
                  rows={2}
                  disabled={aiVideoLoading}
                  className={`w-full px-2 py-1 border text-[10px] mb-1 resize-none ${isDarkMode ? 'bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500' : 'bg-white border-gray-300 text-black placeholder:text-gray-400'} ${aiVideoLoading ? 'opacity-50' : ''}`}
                />
                <button
                  data-testid="ai-video-generate"
                  onClick={() => generateAiCallerVideo && generateAiCallerVideo()}
                  disabled={aiVideoLoading || !(aiVideoPrompt && aiVideoPrompt.trim().length >= 3)}
                  className={`w-full py-1.5 text-[10px] font-bold transition-colors ${
                    aiVideoLoading
                      ? 'bg-neutral-600 text-neutral-300 cursor-wait'
                      : 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white disabled:bg-neutral-700 disabled:text-neutral-500 disabled:cursor-not-allowed'
                  }`}
                >
                  {aiVideoLoading
                    ? `Generating… ${typeof aiVideoElapsed === 'number' && aiVideoElapsed > 0 ? `${aiVideoElapsed}s` : '0s'} (2–5 min, please wait)`
                    : 'Generate 4-sec MP4 (Sora 2)'}
                </button>
                <p className={`text-[9px] mt-1 leading-tight ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                  Uses your Universal Key (~$0.10–$0.40/clip). Backgrounds work best with simple prompts and a dark background.
                </p>
              </div>
              <div className="flex gap-1 mb-2">
                <input
                  type="text"
                  placeholder="…or paste direct .mp4 URL + Enter"
                  defaultValue=""
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const v = e.currentTarget.value.trim();
                      if (/^https?:\/\//.test(v)) { setCallerBgVideoSrc(v); e.currentTarget.value = ''; }
                      else if (v) alert('URL must start with http:// or https://');
                    }
                  }}
                  className={`flex-1 px-2 py-1 border-2 text-[10px] ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-500' : 'bg-white border-black text-black placeholder:text-gray-400'}`}
                  data-testid="caller-bg-url"
                />
              </div>

              {/* OBS Mode toggle — hoisted OUT of the video-src conditional so
                  streamers can turn it on/off at any time, not just after
                  pasting a caller background video. Turning ON doubles the
                  canvas backing store (2× supersample) for crisp OBS captures;
                  turning OFF restores the pre-OBS byte-for-byte behavior. */}
              <label className={`mt-1 mb-2 flex items-start gap-2 p-2 border-2 cursor-pointer select-none ${isDarkMode ? 'border-indigo-800 bg-indigo-950/30 hover:bg-indigo-950/50' : 'border-indigo-300 bg-indigo-50 hover:bg-indigo-100'}`}>
                <input
                  type="checkbox"
                  data-testid="obs-mode-toggle"
                  checked={!!obsMode}
                  onChange={(e) => setObsMode && setObsMode(e.target.checked)}
                  className="mt-0.5 w-3 h-3 accent-indigo-500"
                />
                <div className="flex-1">
                  <div className={`font-bold text-[10px] ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                    OBS Mode
                  </div>
                  <p className={`text-[9px] mt-0.5 leading-snug ${isDarkMode ? 'text-indigo-400/80' : 'text-indigo-600'}`}>
                    For streamers: renders the board canvas at 2× resolution so figure images look sharp in OBS captures. Also cuts video-decode CPU ~50% (background animation plays at half speed) and forces GPU layer. Turn OFF for regular preview.
                  </p>
                </div>
              </label>

              {callerBgVideoSrc && (
                <>
                  <div className="mb-2">
                    <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                      Opacity: {callerBgVideoOpacity}%
                    </Label>
                    <Slider
                      data-testid="caller-bg-opacity"
                      value={[callerBgVideoOpacity || 60]}
                      onValueChange={([v]) => setCallerBgVideoOpacity(v)}
                      min={10} max={100} step={5}
                    />
                  </div>
                  <div className="flex gap-1 mb-2">
                    {[
                      { id: 'cover',   label: 'Cover' },
                      { id: 'contain', label: 'Fit' },
                      { id: 'fill',    label: 'Stretch' },
                    ].map(o => (
                      <button key={o.id}
                        data-testid={`caller-bg-fit-${o.id}`}
                        onClick={() => setCallerBgVideoFit(o.id)}
                        className={`flex-1 px-1 py-1 text-[10px] font-bold border-2 transition-colors ${
                          callerBgVideoFit === o.id
                            ? 'bg-fuchsia-500 text-black border-fuchsia-500'
                            : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >{o.label}</button>
                    ))}
                  </div>
                  <button
                    onClick={() => setCallerBgVideoSrc('')}
                    data-testid="caller-bg-clear"
                    className={`w-full py-1.5 border-2 font-bold text-red-500 text-[10px] flex items-center justify-center gap-1 ${isDarkMode ? 'border-neutral-600 hover:bg-red-900/30' : 'border-black hover:bg-red-50'}`}
                  >
                    <Trash2 className="w-3 h-3" /> Remove Animation
                  </button>

                  {/* Save current animation to owner library */}
                  <div className={`mt-2 p-2 border-2 ${isDarkMode ? 'border-emerald-800 bg-emerald-950/30' : 'border-emerald-300 bg-emerald-50'}`}>
                    <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                      💾 Save to Library
                    </Label>
                    <button
                      data-testid="caller-bg-save"
                      onClick={() => saveCallerVideo && saveCallerVideo()}
                      disabled={savedCallerVideoBusy || !callerBgVideoSrc.startsWith('data:')}
                      title={!callerBgVideoSrc.startsWith('data:') ? 'Only uploaded or AI-generated clips can be saved (not external URLs).' : ''}
                      className={`w-full py-1.5 text-[10px] font-bold transition-colors ${
                        savedCallerVideoBusy
                          ? 'bg-neutral-600 text-neutral-300 cursor-wait'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-neutral-700 disabled:text-neutral-500 disabled:cursor-not-allowed'
                      }`}
                    >
                      {savedCallerVideoBusy ? 'Saving…' : 'Save Current Animation'}
                    </button>
                    <p className={`text-[9px] mt-1 leading-tight ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                      Stored in your account so you can reuse without re-uploading or re-generating.
                    </p>
                  </div>
                </>
              )}

              {/* Saved Library list */}
              {Array.isArray(savedCallerVideos) && savedCallerVideos.length > 0 && (
                <div className={`mt-2 p-2 border-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900/50' : 'border-gray-300 bg-gray-50'}`}>
                  <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                    📚 My Saved Animations ({savedCallerVideos.length})
                  </Label>
                  <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="saved-caller-bg-list">
                    {savedCallerVideos.map((v) => (
                      <div key={v.id} className={`flex items-center gap-1 p-1 border ${isDarkMode ? 'border-neutral-700' : 'border-gray-200'}`}>
                        <button
                          data-testid={`saved-caller-bg-load-${v.id}`}
                          onClick={() => loadCallerVideo && loadCallerVideo(v.id)}
                          className={`flex-1 text-left px-1 py-0.5 text-[10px] truncate hover:underline ${isDarkMode ? 'text-fuchsia-300' : 'text-fuchsia-700'}`}
                          title={`Load "${v.name}" (${Math.round((v.size_bytes || 0) / 1024)} KB)`}
                        >
                          {v.name}
                        </button>
                        <button
                          data-testid={`saved-caller-bg-delete-${v.id}`}
                          onClick={() => {
                            if (window.confirm(`Delete saved animation "${v.name}"?`)) {
                              deleteCallerVideo && deleteCallerVideo(v.id);
                            }
                          }}
                          className="text-red-500 hover:text-red-700 px-1"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}
              </>)}
          </section>

          {/* STEP 2: Overlay Images */}
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={2} title="Overlay Images" icon={Image}  stepKey="step-overlay" description="Add logos, mascots, or photos that float on top of the boards." />
              {!collapsedSteps.has("step-overlay") && (<>
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
              Add image cutouts over the background. Use PNG with transparency for best results.
            </p>
            
            <Tabs defaultValue="ai" className="w-full">
              <TabsList className={`grid w-full grid-cols-4 border-2 rounded-none h-auto p-0 ${isDarkMode ? 'border-neutral-600 bg-neutral-800' : 'border-black'}`}>
                <TabsTrigger value="ai" className={`data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-black font-bold py-2 rounded-none border-r-2 text-xs ${isDarkMode ? 'border-neutral-600 text-neutral-300' : 'border-black'}`}>
                  <Sparkles className="w-3.5 h-3.5 mr-0.5" /> AI
                </TabsTrigger>
                <TabsTrigger value="stock" className={`data-[state=active]:bg-[#FDE047] data-[state=active]:text-black font-bold py-2 rounded-none border-r-2 text-xs ${isDarkMode ? 'border-neutral-600 text-neutral-300' : 'border-black'}`}>
                  <Search className="w-3.5 h-3.5 mr-0.5" /> Stock
                </TabsTrigger>
                <TabsTrigger value="upload" className={`data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white font-bold py-2 rounded-none border-r-2 text-xs ${isDarkMode ? 'border-neutral-600 text-neutral-300' : 'border-black'}`}>
                  <Upload className="w-3.5 h-3.5 mr-0.5" /> Upload
                </TabsTrigger>
                <TabsTrigger value="saved" className={`data-[state=active]:bg-purple-500 data-[state=active]:text-white font-bold py-2 rounded-none text-xs ${isDarkMode ? 'text-neutral-300' : ''}`}>
                  <BookmarkCheck className="w-3.5 h-3.5 mr-0.5" /> Saved
                </TabsTrigger>
              </TabsList>

              <TabsContent value="ai" className="mt-3 space-y-2">
                {/* Static / Animated toggle — Animated mode is OWNER-ONLY
                    because fal.ai image-to-video costs real money per call.
                    Non-owners only see the static AI panel (same as before). */}
                {isOwner ? (
                <div className="flex gap-1">
                  {[
                    { id: 'static', label: 'Static Image' },
                    { id: 'animated', label: 'Animated Video' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      data-testid={`overlay-ai-mode-${opt.id}`}
                      onClick={() => setOverlayAiMode && setOverlayAiMode(opt.id)}
                      className={`flex-1 py-1.5 text-[10px] font-bold border-2 transition-colors ${
                        (overlayAiMode || 'static') === opt.id
                          ? 'bg-fuchsia-500 text-white border-fuchsia-600'
                          : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-400 text-gray-600 hover:bg-gray-100'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
                ) : null}

                {(!isOwner || (overlayAiMode || 'static') === 'static') ? (
                  <>
                    <Input placeholder="Describe overlay image..." value={overlayAiPrompt} onChange={(e) => setOverlayAiPrompt(e.target.value)} className={`border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-400' : 'border-black'}`} />
                    <StyledSelect value={overlayAiProvider} onValueChange={setOverlayAiProvider} testId="overlay-ai-provider" placeholder="AI Provider">
                      <SelectItem value="openai" className="font-medium">OpenAI GPT Image</SelectItem>
                      <SelectItem value="gemini" className="font-medium">Gemini Nano Banana</SelectItem>
                    </StyledSelect>
                    <button onClick={handleOverlayAIGenerate} disabled={overlayImageLoading || !overlayAiPrompt.trim()} className="neo-button-secondary w-full disabled:opacity-50 flex items-center justify-center gap-2">
                      {overlayImageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Add Overlay
                    </button>
                  </>
                ) : (
                  <>
                    {/* Animated Image → Video (fal.ai Kling) */}
                    <div className={`p-2 border-2 ${isDarkMode ? 'border-fuchsia-700 bg-fuchsia-950/30' : 'border-fuchsia-300 bg-fuchsia-50'}`}>
                      <Label className={`font-bold text-[10px] block mb-2 ${isDarkMode ? 'text-fuchsia-300' : 'text-fuchsia-700'}`}>
                        🎬 AI Image-to-Video Overlay
                      </Label>
                      {!animatedSourceImage ? (
                        <label className={`block w-full p-2 border-2 border-dashed cursor-pointer text-center text-[10px] font-bold transition-colors ${
                          isDarkMode ? 'border-neutral-600 text-neutral-400 hover:border-fuchsia-500 hover:text-fuchsia-400' : 'border-gray-400 text-gray-600 hover:border-fuchsia-600 hover:text-fuchsia-700'
                        }`}>
                          <Upload className="w-3.5 h-3.5 mx-auto mb-0.5" />
                          Upload source image
                          <input
                            type="file"
                            accept="image/*"
                            data-testid="animated-overlay-source-upload"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = (ev) => setAnimatedSourceImage && setAnimatedSourceImage(ev.target.result);
                              reader.readAsDataURL(file);
                              e.target.value = '';
                            }}
                            className="hidden"
                          />
                        </label>
                      ) : (
                        <div className="flex items-center gap-2 mb-2">
                          <img src={animatedSourceImage} alt="src" className="w-12 h-12 object-contain bg-white/10 border border-fuchsia-400/30" />
                          <button
                            data-testid="animated-overlay-clear-source"
                            onClick={() => setAnimatedSourceImage && setAnimatedSourceImage(null)}
                            className={`text-[10px] px-2 py-1 border ${isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-400 text-gray-600 hover:bg-gray-100'}`}
                          >Change</button>
                        </div>
                      )}
                      <textarea
                        data-testid="animated-overlay-prompt"
                        value={animatedOverlayPrompt || ''}
                        onChange={(e) => setAnimatedOverlayPrompt && setAnimatedOverlayPrompt(e.target.value)}
                        placeholder="What should the image be doing? (e.g. 'spin slowly, glow, seamless loop')"
                        rows={2}
                        disabled={animatedOverlayLoading}
                        className={`w-full px-2 py-1 border text-[10px] mb-1 resize-none ${isDarkMode ? 'bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500' : 'bg-white border-gray-300 text-black placeholder:text-gray-400'} ${animatedOverlayLoading ? 'opacity-50' : ''}`}
                      />
                      {/* Duration */}
                      <div className="flex gap-1 mb-1">
                        {[5, 10].map(d => (
                          <button
                            key={d}
                            data-testid={`animated-overlay-duration-${d}`}
                            onClick={() => setAnimatedOverlayDuration && setAnimatedOverlayDuration(d)}
                            disabled={animatedOverlayLoading}
                            className={`flex-1 py-1 text-[10px] font-bold border transition-colors ${
                              (animatedOverlayDuration || 5) === d
                                ? 'bg-fuchsia-500 text-white border-fuchsia-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-400 text-gray-600 hover:bg-gray-100'
                            } ${animatedOverlayLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >{d}s</button>
                        ))}
                      </div>
                      <button
                        data-testid="animated-overlay-generate-btn"
                        onClick={generateAnimatedOverlay}
                        disabled={animatedOverlayLoading || !animatedSourceImage || !(animatedOverlayPrompt || '').trim()}
                        className={`neo-button-secondary w-full disabled:opacity-50 flex items-center justify-center gap-1 text-[10px] py-1.5`}
                      >
                        {animatedOverlayLoading
                          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating (60-180s)…{animatedOverlayProgress ? ` ${animatedOverlayProgress}` : ''}</>
                          : <><Sparkles className="w-3.5 h-3.5" /> Generate Animated Overlay</>}
                      </button>
                      <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-fuchsia-200/70' : 'text-fuchsia-700/70'}`}>
                        Uses fal.ai Kling. ~$0.18 per 5s / $0.36 per 10s. Result is added to your Overlays.
                      </p>
                    </div>

                    {/* Emote Presets (Phase 2) */}
                    <div>
                      <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Emote Presets — One-Click Animations</Label>
                      <div className="grid grid-cols-2 gap-1">
                        {[
                          { id: 'pulse',     label: '💗 Pulse',     prompt: 'pulsing gently with a soft glow, seamless loop, transparent background' },
                          { id: 'spin',      label: '🌀 Spin',      prompt: 'rotating slowly 360 degrees around its center, seamless loop, no other motion' },
                          { id: 'bounce',    label: '⬆️ Bounce',     prompt: 'bouncing up and down smoothly, seamless loop, subtle motion' },
                          { id: 'shake',     label: '〰️ Shake',     prompt: 'shaking left and right rapidly with anticipation, seamless loop' },
                          { id: 'glow',      label: '✨ Glow',       prompt: 'glowing brighter and dimmer with magical sparkles around it, seamless loop' },
                          { id: 'flame',     label: '🔥 Flame',     prompt: 'on fire with flickering golden flames rising from it, seamless loop' },
                          { id: 'rainbow',   label: '🌈 Rainbow',   prompt: 'cycling through rainbow color hues smoothly, seamless loop, subtle' },
                          { id: 'confetti',  label: '🎉 Confetti',  prompt: 'colorful confetti bursting outward from behind the image, seamless loop' },
                        ].map(em => (
                          <button
                            key={em.id}
                            data-testid={`animated-overlay-preset-${em.id}`}
                            onClick={() => {
                              if (setAnimatedOverlayPrompt) setAnimatedOverlayPrompt(em.prompt);
                            }}
                            disabled={animatedOverlayLoading}
                            title={em.prompt}
                            className={`py-1 text-[10px] font-bold border transition-colors text-left px-1.5 ${
                              isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-fuchsia-900/30' : 'border-gray-400 text-gray-700 hover:bg-fuchsia-50'
                            } ${animatedOverlayLoading ? 'opacity-50' : ''}`}
                          >{em.label}</button>
                        ))}
                      </div>
                      <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                        Click a preset to load its prompt — then upload your image and Generate.
                      </p>
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="stock" className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <Input placeholder="Search overlays..." value={overlayStockQuery} onChange={(e) => setOverlayStockQuery(e.target.value)} className={`flex-1 border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-400' : 'border-black'}`} onKeyDown={(e) => e.key === "Enter" && handleOverlayStockSearch()} />
                  <button onClick={handleOverlayStockSearch} disabled={overlayStockLoading} className="neo-button-secondary px-4">
                    {overlayStockLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </button>
                </div>
                {overlayStockImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-1 max-h-24 overflow-y-auto">
                    {overlayStockImages.map((img, i) => (
                      <button key={i} onClick={() => selectOverlayStockImage(img.url)} className={`border-2 overflow-hidden hover:opacity-80 ${isDarkMode ? 'border-neutral-600' : 'border-black'}`}>
                        <img src={img.thumbnail} alt={img.alt} className="w-full h-12 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="upload" className="mt-3">
                <label className={`cursor-pointer flex flex-col items-center justify-center py-4 border-2 transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-600 hover:bg-neutral-700' : 'border-black hover:bg-gray-50'}`}>
                  <Upload className={`w-6 h-6 mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-neutral-500'}`} />
                  <span className={`font-bold text-xs ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Upload Overlay (PNG for cutouts)</span>
                  <input type="file" accept="image/*" onChange={handleOverlayFileUpload} className="hidden" />
                </label>
              </TabsContent>

              <TabsContent value="saved" className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                    {savedOverlays.length} saved
                  </span>
                  <button
                    data-testid="saved-overlays-refresh"
                    onClick={() => loadSavedOverlays && loadSavedOverlays()}
                    className={`px-2 py-0.5 text-[10px] font-bold border ${isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'} rounded`}
                    title="Refetch saved overlays from server"
                  >
                    ↻ Refresh
                  </button>
                </div>
                {savedOverlays.length === 0 ? (
                  <p className={`text-xs text-center py-4 ${isDarkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                    No saved overlays yet. Use the save button on any overlay below to add it here.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
                    {savedOverlays.map((overlay) => (
                      <div key={overlay.id} className="relative group">
                        <button
                          data-testid={`load-saved-overlay-${overlay.id}`}
                          onClick={() => addOverlayImage(overlay.src)}
                          className={`w-full border-2 overflow-hidden hover:opacity-80 transition-opacity ${isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}
                        >
                          <img src={overlay.src} alt={overlay.name} className="w-full h-16 object-contain bg-black/10" />
                        </button>
                        <button
                          data-testid={`delete-saved-overlay-${overlay.id}`}
                          onClick={() => deleteSavedOverlay(overlay.id)}
                          className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center font-bold"
                        >
                          ✕
                        </button>
                        <p className={`text-[8px] font-bold truncate mt-0.5 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{overlay.name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Overlay Images List with Opacity Control */}
            {overlayImages.length > 0 && (
              <div className="mt-3 space-y-2">
                <button
                  type="button"
                  data-testid="toggle-overlay-list-btn"
                  onClick={() => toggleStep('step-overlay-list')}
                  className={`w-full flex items-center gap-2 text-left py-1 ${isDarkMode ? 'hover:bg-neutral-800/50' : 'hover:bg-gray-100'} transition-colors`}
                  title={collapsedSteps.has('step-overlay-list') ? 'Show overlay list' : 'Hide overlay list'}
                >
                  {collapsedSteps.has('step-overlay-list')
                    ? <ChevronRight className={`w-3.5 h-3.5 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`} />
                    : <ChevronDown className={`w-3.5 h-3.5 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`} />
                  }
                  <Label className={`font-semibold text-xs cursor-pointer flex-1 ${isDarkMode ? 'text-neutral-300' : ''}`}>
                    Overlays ({overlayImages.length}) - Click to {collapsedSteps.has('step-overlay-list') ? 'expand' : 'collapse'}
                  </Label>
                </button>
                {!collapsedSteps.has('step-overlay-list') && (
                <div className="space-y-2">
                  {overlayImages.map((img, index) => (
                    <div 
                      key={img.id} 
                      onClick={() => setSelectedOverlayId(img.id)}
                      className={`p-2 border-2 cursor-pointer ${
                        selectedOverlayId === img.id 
                          ? 'border-[#FF6B6B] bg-[#FF6B6B]/10' 
                          : isDarkMode ? 'border-neutral-700 hover:border-neutral-600' : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2 min-w-0">
                        <img src={img.src} alt="" className="w-8 h-8 object-cover flex-shrink-0" />
                        <span className={`flex-1 min-w-0 truncate text-xs font-medium ${isDarkMode ? 'text-neutral-300' : ''}`}>Overlay {index + 1}</span>
                        <button
                          type="button"
                          data-testid={`save-overlay-${img.id}`}
                          onClick={(e) => { e.stopPropagation(); saveOverlayToLibrary(img.src, `Overlay ${index + 1}`); }}
                          className="flex-shrink-0 text-purple-500 text-xs hover:text-purple-700 font-bold"
                          title="Save to Library"
                        >
                          <BookmarkCheck className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          data-testid={`duplicate-overlay-${img.id}`}
                          onClick={(e) => { e.stopPropagation(); duplicateOverlay && duplicateOverlay(img.id); }}
                          className="flex-shrink-0 text-emerald-500 text-xs hover:text-emerald-700 font-bold"
                          title="Duplicate overlay"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          data-testid={`delete-overlay-${img.id}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => { e.stopPropagation(); removeOverlayImage(img.id); }}
                          className="flex-shrink-0 text-red-500 text-xs hover:text-red-700"
                        >✕</button>
                      </div>

                      {/* Animated overlay — Download MP4 for OBS */}
                      {img.isAnimated && img.videoSrc && (
                        <div className="px-2 pb-2">
                          <a
                            href={img.videoSrc}
                            download={`animated-overlay-${img.id}.mp4`}
                            data-testid={`download-animated-overlay-${img.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className={`block w-full text-center py-1 text-[10px] font-bold border-2 transition-colors ${
                              isDarkMode ? 'border-fuchsia-500 text-fuchsia-300 hover:bg-fuchsia-900/30' : 'border-fuchsia-600 text-fuchsia-700 hover:bg-fuchsia-50'
                            }`}
                            title="Download this animated overlay as an MP4 file for use in OBS"
                          >⬇ Download MP4 for OBS</a>
                        </div>
                      )}
                      
                      {selectedOverlayId === img.id && (
                        <div className="pt-2 border-t border-gray-300 space-y-3">
                          {/* Opacity */}
                          <div>
                            <Label className={`text-xs ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                              Opacity: {img.opacity || 100}%
                            </Label>
                            <Slider
                              value={[img.opacity || 100]}
                              onValueChange={([val]) => updateOverlayOpacity(img.id, val)}
                              min={0}
                              max={100}
                              step={5}
                              className="mt-1"
                            />
                          </div>
                          {/* Rotate + Flip — rotation 0–360° plus Flip H / V toggles. */}
                          <div onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between">
                              <Label className={`text-xs ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                Rotate: {img.rotation || 0}°
                              </Label>
                              <button
                                data-testid={`overlay-rotate-reset-${img.id}`}
                                onClick={() => updateOverlayTransform && updateOverlayTransform(img.id, { rotation: 0 })}
                                className={`text-[9px] font-bold underline ${isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                                title="Reset rotation"
                              >
                                Reset
                              </button>
                            </div>
                            <Slider
                              data-testid={`overlay-rotate-${img.id}`}
                              value={[img.rotation || 0]}
                              onValueChange={([val]) => updateOverlayTransform(img.id, { rotation: val })}
                              min={0}
                              max={360}
                              step={1}
                              className="mt-1"
                            />
                            <div className="flex gap-1 mt-2">
                              <button
                                data-testid={`overlay-flip-h-${img.id}`}
                                onClick={() => updateOverlayTransform(img.id, { flipH: !img.flipH })}
                                className={`flex-1 py-1 text-[10px] font-bold border-2 transition-colors ${
                                  img.flipH
                                    ? 'bg-[#FF6B6B] text-white border-[#FF6B6B]'
                                    : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                }`}
                                title="Flip horizontal (mirror)"
                              >
                                ⇋ Flip H
                              </button>
                            </div>
                          </div>
                          {/* Per-overlay Shadow */}
                          <div onClick={(e) => e.stopPropagation()}>
                            <Label className={`text-xs ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>Shadow</Label>
                            <div className="flex gap-1 mt-1">
                              {[
                                { id: 'none', label: 'None' },
                                { id: 'light', label: 'Light' },
                                { id: 'medium', label: 'Medium' },
                                { id: 'heavy', label: 'Heavy' },
                              ].map(opt => {
                                const active = (img.shadow || 'none') === opt.id;
                                return (
                                  <button key={opt.id}
                                    data-testid={`overlay-shadow-${opt.id}-${img.id}`}
                                    onClick={() => updateOverlayShadow && updateOverlayShadow(img.id, { shadow: opt.id })}
                                    className={`flex-1 px-1 py-1 text-[9px] font-bold border-2 transition-colors ${
                                      active
                                        ? 'bg-[#FF6B6B] text-white border-[#FF6B6B]'
                                        : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                    }`}
                                  >{opt.label}</button>
                                );
                              })}
                            </div>
                            {(img.shadow && img.shadow !== 'none') && (
                              <div className={`mt-2 pl-2 border-l-2 ${isDarkMode ? 'border-neutral-700' : 'border-gray-200'} space-y-2`}>
                                <div>
                                  <Label className={`text-[10px] ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                    Shadow X: {(img.shadowOffsetX != null ? img.shadowOffsetX : 2) > 0 ? '+' : ''}{img.shadowOffsetX != null ? img.shadowOffsetX : 2}%
                                  </Label>
                                  <Slider
                                    data-testid={`overlay-shadow-offset-x-${img.id}`}
                                    value={[img.shadowOffsetX != null ? img.shadowOffsetX : 2]}
                                    onValueChange={([val]) => updateOverlayShadow(img.id, { shadowOffsetX: val })}
                                    min={-20}
                                    max={20}
                                    step={1}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label className={`text-[10px] ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                    Shadow Y: {(img.shadowOffsetY != null ? img.shadowOffsetY : 2) > 0 ? '+' : ''}{img.shadowOffsetY != null ? img.shadowOffsetY : 2}%
                                  </Label>
                                  <Slider
                                    data-testid={`overlay-shadow-offset-y-${img.id}`}
                                    value={[img.shadowOffsetY != null ? img.shadowOffsetY : 2]}
                                    onValueChange={([val]) => updateOverlayShadow(img.id, { shadowOffsetY: val })}
                                    min={-20}
                                    max={20}
                                    step={1}
                                    className="mt-1"
                                  />
                                </div>
                                <button
                                  data-testid={`overlay-shadow-offset-reset-${img.id}`}
                                  onClick={() => updateOverlayShadow(img.id, { shadowOffsetX: 2, shadowOffsetY: 2 })}
                                  className={`text-[9px] font-bold underline ${isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                                >
                                  Reset position
                                </button>
                              </div>
                            )}
                          </div>
                          {/* Outline — silhouette stroke around the image's
                              non-transparent pixels. Works on transparent PNGs
                              and rectangular JPGs alike. */}
                          <div onClick={(e) => e.stopPropagation()}>
                            <label className={`flex items-center gap-2 cursor-pointer select-none mb-1.5`}>
                              <input
                                type="checkbox"
                                data-testid={`overlay-outline-enabled-${img.id}`}
                                checked={!!img.outlineEnabled}
                                onChange={(e) => updateOverlayOutline && updateOverlayOutline(img.id, { outlineEnabled: e.target.checked })}
                                className="w-3 h-3 accent-[#FF6B6B]"
                              />
                              <span className={`text-[11px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Outline</span>
                            </label>
                            {img.outlineEnabled && (
                              <div className={`pl-5 space-y-2 border-l-2 ${isDarkMode ? 'border-neutral-700' : 'border-gray-200'}`}>
                                <div>
                                  <Label className={`text-[10px] ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                                    Thickness: {img.outlineThickness != null ? img.outlineThickness : 6}px
                                  </Label>
                                  <Slider
                                    data-testid={`overlay-outline-thickness-${img.id}`}
                                    value={[img.outlineThickness != null ? img.outlineThickness : 6]}
                                    onValueChange={([val]) => updateOverlayOutline(img.id, { outlineThickness: val })}
                                    min={1}
                                    max={40}
                                    step={1}
                                    className="mt-1"
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <Label className={`text-[10px] ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>Color</Label>
                                  <input
                                    type="color"
                                    data-testid={`overlay-outline-color-${img.id}`}
                                    value={img.outlineColor || '#000000'}
                                    onChange={(e) => updateOverlayOutline(img.id, { outlineColor: e.target.value })}
                                    className="w-8 h-7 cursor-pointer border-2 border-black p-0"
                                  />
                                  <input
                                    type="text"
                                    value={img.outlineColor || '#000000'}
                                    onChange={(e) => updateOverlayOutline(img.id, { outlineColor: e.target.value })}
                                    className={`flex-1 text-[10px] font-mono px-1.5 py-1 border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'border-black'}`}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          {/* Render on top of boards toggle */}
                          <label
                            onClick={(e) => e.stopPropagation()}
                            className={`flex items-start gap-2 p-1.5 border-2 cursor-pointer select-none ${
                              isDarkMode ? 'border-fuchsia-800 bg-fuchsia-950/30 hover:bg-fuchsia-950/50' : 'border-fuchsia-300 bg-fuchsia-50 hover:bg-fuchsia-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              data-testid={`overlay-above-boards-${img.id}`}
                              checked={!!img.aboveBoards}
                              onChange={(e) => updateOverlayAboveBoards && updateOverlayAboveBoards(img.id, e.target.checked)}
                              className="mt-0.5 w-3 h-3 accent-fuchsia-500"
                            />
                            <div className="flex-1">
                              <div className={`font-bold text-[10px] ${isDarkMode ? 'text-fuchsia-300' : 'text-fuchsia-700'}`}>
                                Show on top of Boards
                              </div>
                              <p className={`text-[9px] mt-0.5 leading-snug ${isDarkMode ? 'text-fuchsia-400/80' : 'text-fuchsia-600'}`}>
                                Renders this overlay above the boards. Markup / drawings still appear on top of it.
                              </p>
                            </div>
                          </label>
                          {/* Image Cutout — two modes: subject (AI, isolates main figure)
                              and color (edge flood-fill, keeps text + props, only removes
                              the actual background color). */}
                          <div className="grid grid-cols-2 gap-1">
                            <button
                              data-testid={`overlay-cutout-subject-${img.id}`}
                              onClick={(e) => { e.stopPropagation(); removeOverlayBackground(img.id, 'subject'); }}
                              disabled={cutoutLoading}
                              title="AI subject detection — isolates the main figure. Removes text, props, and other secondary elements."
                              className={`py-2 text-[10px] font-bold border-2 flex items-center justify-center gap-1 transition-colors ${
                                isDarkMode ? 'border-neutral-600 text-white hover:bg-purple-900/30' : 'border-black hover:bg-purple-50'
                              } ${cutoutLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {cutoutLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />}
                              <span className="truncate">Cutout (Subject)</span>
                            </button>
                            <button
                              data-testid={`overlay-cutout-color-${img.id}`}
                              onClick={(e) => { e.stopPropagation(); removeOverlayBackground(img.id, 'color'); }}
                              disabled={cutoutLoading}
                              title="Removes ONLY the dominant background color from the edges. Keeps text, props, and any non-edge content."
                              className={`py-2 text-[10px] font-bold border-2 flex items-center justify-center gap-1 transition-colors ${
                                isDarkMode ? 'border-emerald-600 text-emerald-300 hover:bg-emerald-900/30' : 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'
                              } ${cutoutLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {cutoutLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />}
                              <span className="truncate">Cutout (Color)</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}
            {/* Global Overlay Shadow removed — shadow is now per-overlay,
                configured inside each overlay's expanded panel above. */}
              </>)}
          </section>

          {/* STEP 3: Boards */}
          {gameMode === 'bingo' ? (
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={3} title="BINGO Boards" icon={Grid3X3}  stepKey="step-bingo-boards" description="Create and arrange BINGO boards: count, layout, header & cell colors, fonts." />
              {!collapsedSteps.has("step-bingo-boards") && (<>
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
              All boards auto-fit to page. Drag to reposition individually.
            </p>

            {/* Board Count */}
            <div className="mb-4">
              <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                Number of Boards: {boardCount}
              </Label>
              <div className="flex items-center gap-2">
                <Slider
                  data-testid="board-count-slider"
                  value={[boardCount]}
                  onValueChange={([val]) => handleBoardCountChange(val)}
                  min={0}
                  max={100}
                  step={1}
                  className="flex-1"
                />
                <Input
                  data-testid="board-count-input"
                  type="number"
                  value={boardCount}
                  onChange={(e) => handleBoardCountChange(parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                  className={`w-16 text-center border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'border-black'}`}
                />
              </div>
            </div>

            {/* Board Layout Configurator */}
            {boardCount > 0 && (() => {
              const dims = boardLayout || getDefaultGridDims(boardCount);
              const cols = dims.cols || 1;
              const rows = dims.rows || 1;
              const disabled = boardLayout?.disabled || new Set();
              const totalSlots = cols * rows;
              const enabledCount = totalSlots - disabled.size;

              const toggleCell = (r, c) => {
                const key = `${r}-${c}`;
                const newDisabled = new Set(disabled);
                if (newDisabled.has(key)) {
                  newDisabled.delete(key);
                } else {
                  // Don't allow disabling if it would leave fewer slots than board count
                  if (enabledCount <= boardCount) return;
                  newDisabled.add(key);
                }
                applyBoardLayout({ cols, rows, disabled: newDisabled });
              };

              const addRow = () => {
                applyBoardLayout({ cols, rows: rows + 1, disabled: new Set(disabled) });
              };
              const removeRow = () => {
                if (rows <= 1) return;
                const newDisabled = new Set();
                disabled.forEach(k => { const [r] = k.split('-').map(Number); if (r < rows - 1) newDisabled.add(k); });
                const newRows = rows - 1;
                const newEnabled = (newRows * cols) - newDisabled.size;
                if (newEnabled < boardCount) return;
                applyBoardLayout({ cols, rows: newRows, disabled: newDisabled });
              };
              const addCol = () => {
                applyBoardLayout({ cols: cols + 1, rows, disabled: new Set(disabled) });
              };
              const removeCol = () => {
                if (cols <= 1) return;
                const newDisabled = new Set();
                disabled.forEach(k => { const [, c] = k.split('-').map(Number); if (c < cols - 1) newDisabled.add(k); });
                const newCols = cols - 1;
                const newEnabled = (rows * newCols) - newDisabled.size;
                if (newEnabled < boardCount) return;
                applyBoardLayout({ cols: newCols, rows, disabled: newDisabled });
              };
              const resetLayout = () => applyBoardLayout(null);

              // Cell size scales down for large grids
              const cellPx = Math.max(14, Math.min(28, 160 / Math.max(cols, rows)));

              return (
                <div className={`mb-4 p-3 border-2 rounded ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-black bg-blue-50/40'}`} data-testid="board-layout-configurator">
                  <div className="flex items-center gap-2 mb-2">
                    <Grid3X3 className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} strokeWidth={2.5} />
                    <Label className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>
                      Board Layout
                    </Label>
                    <span className={`ml-auto text-[10px] font-mono ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>{cols}×{rows}</span>
                  </div>
                  <p className={`text-[10px] mb-2 ${isDarkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                    Click cells to toggle board positions. Boards fill enabled slots.
                  </p>

                  {/* Grid */}
                  <div className="flex justify-center mb-2">
                    <div className="inline-grid gap-[2px]" style={{ gridTemplateColumns: `repeat(${cols}, ${cellPx}px)` }}>
                      {Array.from({ length: rows }, (_, r) =>
                        Array.from({ length: cols }, (_, c) => {
                          const key = `${r}-${c}`;
                          const isDisabled = disabled.has(key);
                          // Calculate board index for this slot
                          let slotIndex = 0;
                          let found = false;
                          for (let ri = 0; ri <= r; ri++) {
                            for (let ci = 0; ci < (ri === r ? c + 1 : cols); ci++) {
                              if (!disabled.has(`${ri}-${ci}`)) {
                                if (ri === r && ci === c) { found = true; break; }
                                slotIndex++;
                              }
                            }
                            if (found) break;
                          }
                          const hasBoard = !isDisabled && slotIndex < boardCount;

                          return (
                            <button
                              key={key}
                              data-testid={`layout-cell-${r}-${c}`}
                              onClick={() => toggleCell(r, c)}
                              className={`rounded-sm border transition-all ${
                                isDisabled
                                  ? isDarkMode ? 'bg-neutral-800 border-neutral-700 opacity-30' : 'bg-gray-200 border-gray-300 opacity-40'
                                  : hasBoard
                                    ? isDarkMode ? 'bg-blue-600 border-blue-500 shadow-sm shadow-blue-500/30' : 'bg-blue-500 border-blue-600 shadow-sm'
                                    : isDarkMode ? 'bg-neutral-700 border-neutral-600' : 'bg-white border-gray-400'
                              }`}
                              style={{ width: cellPx, height: cellPx * 1.2 }}
                              title={isDisabled ? 'Empty slot (click to enable)' : hasBoard ? `Board #${slotIndex + 1}` : 'Extra slot'}
                            />
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Grid controls */}
                  <div className="flex items-center justify-center gap-1">
                    <button data-testid="layout-remove-col" onClick={removeCol} className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'}`} title="Remove column">Col −</button>
                    <button data-testid="layout-add-col" onClick={addCol} className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'}`} title="Add column">Col +</button>
                    <span className={`mx-1 text-[10px] ${isDarkMode ? 'text-neutral-600' : 'text-neutral-300'}`}>|</span>
                    <button data-testid="layout-remove-row" onClick={removeRow} className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'}`} title="Remove row">Row −</button>
                    <button data-testid="layout-add-row" onClick={addRow} className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'}`} title="Add row">Row +</button>
                    <span className={`mx-1 text-[10px] ${isDarkMode ? 'text-neutral-600' : 'text-neutral-300'}`}>|</span>
                    <button data-testid="layout-reset" onClick={resetLayout} className={`px-1.5 py-0.5 text-[10px] font-bold rounded border text-orange-500 ${isDarkMode ? 'border-neutral-700 hover:bg-neutral-800' : 'border-gray-300 hover:bg-orange-50'}`} title="Reset to auto layout">Reset</button>
                  </div>
                </div>
              );
            })()}

            {/* Board Opacity */}
            <div className="mb-4">
              <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                Board Opacity: {boardOpacity}%
              </Label>
              <Slider
                data-testid="board-opacity-slider"
                value={[boardOpacity]}
                onValueChange={([val]) => setBoardOpacity(val)}
                min={5}
                max={100}
                step={5}
              />
            </div>

            {/* Transparent Board Toggle */}
            <div className="flex items-center gap-3 mb-2">
              <Switch
                data-testid="transparent-boards-toggle"
                checked={transparentBoards}
                onCheckedChange={setTransparentBoards}
                className="data-[state=checked]:bg-[#4ECDC4]"
              />
              <Label className={`font-medium text-sm ${isDarkMode ? 'text-neutral-300' : ''}`}>
                Transparent Boards
              </Label>
            </div>

            {transparentBoards && (
              <div className="mb-4 ml-8 space-y-2">
                <div className="flex items-center gap-3">
                  <Switch
                    data-testid="transparent-keep-header-toggle"
                    checked={transparentKeepHeader}
                    onCheckedChange={setTransparentKeepHeader}
                    className="data-[state=checked]:bg-amber-500"
                  />
                  <Label className={`font-medium text-xs ${isDarkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    Keep Header Colored
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    data-testid="transparent-keep-freespace-toggle"
                    checked={transparentKeepFreeSpace}
                    onCheckedChange={setTransparentKeepFreeSpace}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                  <Label className={`font-medium text-xs ${isDarkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    Keep Free Space Colored
                  </Label>
                </div>
              </div>
            )}

            {boardCount > 0 && (
              <>
                {/* Board Size - Resize All */}
                <div className={`mb-4 p-3 border-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-black bg-[#FDE047]/20'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Maximize2 className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-black'}`} strokeWidth={2.5} />
                    <Label className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>
                      Resize All Boards
                    </Label>
                  </div>
                  <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                    Board Size: {Math.round(currentBoardWidth)}px
                  </Label>
                  <Slider
                    data-testid="board-size-slider"
                    value={[currentBoardWidth]}
                    onValueChange={([val]) => handleManualBoardResize(val)}
                    min={20}
                    max={500}
                    step={2}
                  />
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                    All boards resize together in unison
                  </p>
                </div>

                {/* Move Boards — column / row unison drag for BINGO. Shares
                    the same `loteriaMoveMode` state as the Loteria sidebar
                    so users learn the behavior once. When Vertical is
                    selected, dragging any board moves every other board in
                    the same column with it. Horizontal does the same for
                    rows. */}
                <div className={`mb-4 p-3 border-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-black bg-[#FDE047]/20'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Move className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-black'}`} strokeWidth={2.5} />
                    <Label className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>
                      Move Boards
                    </Label>
                  </div>
                  <div className="flex gap-1.5">
                    <button data-testid="bingo-move-mode-individual"
                      onClick={() => setLoteriaMoveMode('individual')}
                      className={`flex-1 px-2 py-1.5 text-[10px] font-bold border-2 transition-colors ${
                        loteriaMoveMode === 'individual'
                          ? 'bg-amber-500 text-black border-amber-500'
                          : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                      }`}>
                      Individual
                    </button>
                    <button data-testid="bingo-move-mode-horizontal"
                      onClick={() => setLoteriaMoveMode('horizontal')}
                      className={`flex-1 px-2 py-1.5 text-[10px] font-bold border-2 transition-colors ${
                        loteriaMoveMode === 'horizontal'
                          ? 'bg-amber-500 text-black border-amber-500'
                          : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                      }`}>
                      Horizontal
                    </button>
                    <button data-testid="bingo-move-mode-vertical"
                      onClick={() => setLoteriaMoveMode('vertical')}
                      className={`flex-1 px-2 py-1.5 text-[10px] font-bold border-2 transition-colors ${
                        loteriaMoveMode === 'vertical'
                          ? 'bg-amber-500 text-black border-amber-500'
                          : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                      }`}>
                      Vertical
                    </button>
                  </div>
                </div>

                {/* Board Style */}
                <div className="mb-4">
                  <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Board Style</Label>
                  <StyledSelect value={boardStyle} onValueChange={setBoardStyle} testId="board-style-select" placeholder="Style">
                    {boardStyles.map((style) => (
                      <SelectItem key={style.id} value={style.id} className="font-medium">{style.label}</SelectItem>
                    ))}
                  </StyledSelect>
                </div>

                {/* Colors */}
                <div className="space-y-3">
                  <ColorPicker value={headerColor} onChange={setHeaderColor} label="Header (BINGO) BG" testId="header-color-picker" />
                  <ColorPicker value={headerLetterColor} onChange={setHeaderLetterColor} label="Header Letters" testId="header-letter-color-picker" />
                  <ColorPicker value={cellColor} onChange={setCellColor} label="Cell Background" testId="cell-color-picker" />
                  <ColorPicker value={boardColor} onChange={setBoardColor} label="Board Border" testId="board-color-picker" />
                  <ColorPicker value={textColor} onChange={setTextColor} label="Text Color" testId="text-color-picker" />
                </div>

                {/* Multi-Color Rows */}
                <div className="mt-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Switch
                      data-testid="multi-color-rows-toggle"
                      checked={multiColorRows}
                      onCheckedChange={(checked) => {
                        setMultiColorRows(checked);
                        if (checked && boardRowColors.length === 0) {
                          const dims = boardLayout || getDefaultGridDims(boardCount);
                          const rows = dims.rows || 1;
                          setBoardRowColors(Array.from({ length: rows }, () => ({
                            headerColor, headerLetterColor, cellColor, boardColor, textColor
                          })));
                        }
                      }}
                      className="data-[state=checked]:bg-[#FF6B6B]"
                    />
                    <Label className={`font-medium text-sm ${isDarkMode ? 'text-neutral-300' : ''}`}>
                      Multi-Color Rows
                    </Label>
                  </div>

                  {multiColorRows && boardRowColors.length > 0 && (
                    <div className={`space-y-3 p-2 border rounded ${isDarkMode ? 'border-neutral-700 bg-neutral-900/50' : 'border-gray-300 bg-gray-50'}`}>
                      {boardRowColors.map((rowCol, rowIdx) => (
                        <div key={rowIdx}>
                          <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                            Row {rowIdx + 1}
                          </Label>
                          <div className="grid grid-cols-5 gap-1">
                            {[
                              { key: 'headerColor', label: 'Hdr BG' },
                              { key: 'headerLetterColor', label: 'Hdr Txt' },
                              { key: 'cellColor', label: 'Cell' },
                              { key: 'boardColor', label: 'Border' },
                              { key: 'textColor', label: 'Text' },
                            ].map(({ key, label }) => (
                              <div key={key} className="flex flex-col items-center gap-0.5">
                                <input
                                  data-testid={`row-${rowIdx}-${key}`}
                                  type="color"
                                  value={rowCol[key]}
                                  onChange={(e) => {
                                    const updated = [...boardRowColors];
                                    updated[rowIdx] = { ...updated[rowIdx], [key]: e.target.value };
                                    setBoardRowColors(updated);
                                  }}
                                  className="w-6 h-6 border-0 p-0 cursor-pointer rounded"
                                />
                                <span className={`text-[7px] font-bold leading-none ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>{label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Font */}
                <div className="mt-4">
                  <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Font Style</Label>
                  <StyledSelect value={boardFont} onValueChange={setBoardFont} testId="board-font-select" placeholder="Font">
                    {fontOptions.map((font) => (
                      <SelectItem key={font.id} value={font.id} className="font-medium" style={{ fontFamily: font.family }}>{font.label}</SelectItem>
                    ))}
                  </StyledSelect>
                </div>

                <button
                  data-testid="regenerate-boards-btn"
                  onClick={regenerateBoards}
                  className={`mt-4 w-full py-2 border-2 font-bold text-sm flex items-center justify-center gap-2 ${
                    isDarkMode ? 'border-neutral-600 text-white hover:bg-neutral-800' : 'border-black hover:bg-gray-100'
                  }`}
                >
                  <RefreshCw className="w-4 h-4" /> Regenerate Numbers
                </button>
              </>
            )}
              </>)}
          </section>
          ) : (
          /* STEP 3: Loteria Boards */
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={3} title="Loteria Boards" icon={Grid3X3}  stepKey="step-loteria-boards" description="Create Loteria boards: 4×4 grid, deck pack, multi-color rows/columns, Last-Card-Standing, Connecting-Image neighbors." />
              {!collapsedSteps.has("step-loteria-boards") && (<>
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
              Upload figures, choose grid size, and auto-fit boards to page.
            </p>

            {/* Grid Size Selector */}
            <div className="mb-4">
              <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                Grid Size
              </Label>
              <div className="grid grid-cols-4 gap-1">
                {[3,'3x4','4x3','3x5','5x3',4,'4x5','5x4',5,'3x6','6x3','5x6','6x5',6,'6x7','7x6','5x7','7x5',7,8,9,10,11,12,13,14,15,16,17,18,19,20,23,25,27].map(size => {
                  const label = typeof size === 'string' ? size : `${size}x${size}`;
                  const isSelected = String(loteriaGridSize) === String(size);
                  return (
                  <button key={size} data-testid={`loteria-grid-${size}`}
                    onClick={() => { setLoteriaGridSize(size); }}
                    className={`py-1 text-[9px] font-black rounded border-2 transition-colors ${
                      isSelected
                        ? isDarkMode ? 'bg-amber-600 border-amber-500 text-white' : 'bg-amber-500 border-amber-600 text-white'
                        : isDarkMode ? 'border-neutral-700 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                    }`}>{label}</button>
                  );
                })}
              </div>

              {/* Row/Column Repeat Mode (all grid sizes) */}
              <div className="mt-2">
                <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Images Per Row/Column</Label>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { id: 'none', label: 'Unique' },
                    { id: 'doubles', label: 'Doubles' },
                    { id: 'triples', label: 'Triples' },
                    { id: 'centerDoubles', label: 'Doubles (Center)' },
                    { id: 'centerTriples', label: 'Triples (Center)' },
                  ].map(opt => (
                    <button key={opt.id} data-testid={`loteria-repeat-${opt.id}`}
                      onClick={() => setLoteriaRepeatMode(opt.id)}
                      className={`px-1 py-1 text-[10px] font-bold leading-tight border-2 transition-colors ${
                        loteriaRepeatMode === opt.id
                          ? 'bg-amber-500 text-black border-amber-500'
                          : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {(loteriaRepeatMode === 'centerDoubles' || loteriaRepeatMode === 'centerTriples') && (
                  <div className="mt-2">
                    <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Center Cluster Direction</Label>
                    <div className="grid grid-cols-4 gap-1">
                      {[
                        { id: 'vertical',   label: 'Vertical',   icon: '|' },
                        { id: 'horizontal', label: 'Horizontal', icon: '—' },
                        { id: 'slantRight', label: 'Slant ↘',    icon: '↘' },
                        { id: 'slantLeft',  label: 'Slant ↙',    icon: '↙' },
                      ].map(opt => (
                        <button key={opt.id} data-testid={`loteria-center-${opt.id}`}
                          onClick={() => setLoteriaCenterOrientation(opt.id)}
                          title={opt.label}
                          className={`px-1 py-1 text-[11px] font-bold border-2 transition-colors ${
                            loteriaCenterOrientation === opt.id
                              ? 'bg-amber-500 text-black border-amber-500'
                              : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                          }`}>
                          {opt.icon}
                        </button>
                      ))}
                    </div>
                    <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                      Each board gets a unique focal image — no repeats across the template.
                    </p>
                  </div>
                )}

                {/* Doubles/Triples Highlight — rings each run of matching
                    figures (horizontal + vertical). Border-only. Shown only
                    when a repeat mode is active. */}
                {loteriaRepeatMode !== 'none' && (
                  <div className="mt-3" data-testid="loteria-repeat-highlight-section">
                    <button
                      type="button"
                      data-testid="loteria-repeat-highlight-toggle"
                      onClick={() => setLoteriaRepeatHighlight(!loteriaRepeatHighlight)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold transition-colors ${loteriaRepeatHighlight ? 'bg-cyan-600 text-white' : (isDarkMode ? 'bg-neutral-800 text-neutral-300' : 'bg-neutral-200 text-black')}`}
                    >
                      <span>Highlight {loteriaRepeatMode === 'triples' || loteriaRepeatMode === 'centerTriples' ? 'Triples' : loteriaRepeatMode === 'doubles' || loteriaRepeatMode === 'centerDoubles' ? 'Doubles' : 'Repeats'}</span>
                      <span className={`ml-2 inline-flex h-4 w-7 items-center rounded-full transition-colors ${loteriaRepeatHighlight ? 'bg-white/90' : 'bg-neutral-500/50'}`}>
                        <span className={`h-3 w-3 rounded-full bg-cyan-600 transition-transform ${loteriaRepeatHighlight ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                      </span>
                    </button>
                    {loteriaRepeatHighlight && (
                      <div className="mt-2" data-testid="loteria-repeat-highlight-colors">
                        <span className={`text-[11px] font-semibold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>Ring color</span>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {['#00E5FF', '#FF1493', '#FF0000', '#FF7A00', '#FFD400', '#39FF14', '#2979FF', '#9C27B0', '#000000', '#FFFFFF'].map((c) => (
                            <button
                              key={c}
                              type="button"
                              data-testid={`loteria-repeat-highlight-color-${c.replace('#', '')}`}
                              onClick={() => setLoteriaRepeatHighlightColor(c)}
                              title={c}
                              className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${(loteriaRepeatHighlightColor || '').toUpperCase() === c ? 'border-cyan-400 ring-2 ring-cyan-400/50 scale-110' : (isDarkMode ? 'border-neutral-600' : 'border-neutral-300')}`}
                              style={{ backgroundColor: c }}
                            />
                          ))}
                          <input
                            type="color"
                            value={loteriaRepeatHighlightColor}
                            data-testid="loteria-repeat-highlight-color-picker"
                            onChange={(e) => setLoteriaRepeatHighlightColor(e.target.value)}
                            className="w-6 h-6 border-0 p-0 cursor-pointer rounded"
                            title="Custom color"
                          />
                        </div>
                        <div className="mt-2">
                          <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            Ring Thickness: {loteriaRepeatHighlightThickness}%
                          </Label>
                          <Slider
                            data-testid="loteria-repeat-highlight-thickness-slider"
                            min={20} max={400} step={10}
                            value={[loteriaRepeatHighlightThickness]}
                            onValueChange={([v]) => setLoteriaRepeatHighlightThickness(v)}
                            className="my-1"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mb-4">
              <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                Figure Pack
              </Label>
              <div className={`grid grid-cols-3 border-2 overflow-hidden ${isDarkMode ? 'border-neutral-700' : 'border-black'}`}>
                <button
                  data-testid="loteria-deck-original"
                  onClick={() => setLoteriaDeck('original')}
                  className={`py-2 px-1 text-[11px] font-bold leading-tight transition-colors ${
                    loteriaDeck === 'original'
                      ? (isDarkMode ? 'bg-[#FFD700] text-black' : 'bg-black text-white')
                      : (isDarkMode ? 'bg-neutral-900 text-neutral-300 hover:bg-neutral-800' : 'bg-white text-black hover:bg-gray-100')
                  }`}
                >Original<br/>(My Uploads)</button>
                <button
                  data-testid="loteria-deck-enhanced"
                  onClick={() => setLoteriaDeck('enhanced')}
                  className={`py-2 px-1 text-[11px] font-bold leading-tight transition-colors border-l-2 ${isDarkMode ? 'border-neutral-700' : 'border-black'} ${
                    loteriaDeck === 'enhanced'
                      ? (isDarkMode ? 'bg-[#FFD700] text-black' : 'bg-black text-white')
                      : (isDarkMode ? 'bg-neutral-900 text-neutral-300 hover:bg-neutral-800' : 'bg-white text-black hover:bg-gray-100')
                  }`}
                >Enhanced<br/>(Built-in)</button>
                <button
                  data-testid="loteria-deck-square"
                  onClick={() => setLoteriaDeck('square')}
                  className={`py-2 px-1 text-[11px] font-bold leading-tight transition-colors border-l-2 ${isDarkMode ? 'border-neutral-700' : 'border-black'} ${
                    loteriaDeck === 'square'
                      ? (isDarkMode ? 'bg-[#FFD700] text-black' : 'bg-black text-white')
                      : (isDarkMode ? 'bg-neutral-900 text-neutral-300 hover:bg-neutral-800' : 'bg-white text-black hover:bg-gray-100')
                  }`}
                >Square<br/>Images</button>
              </div>
              {loteriaDeck === 'enhanced' && (
                <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                  54-card deck in standard Lotería order. Auto-markup, Caller, and all features work normally.
                </p>
              )}
              {loteriaDeck === 'square' && (
                <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                  54-card deck with bold square artwork. Auto-markup, Caller, and all features work normally.
                </p>
              )}
            </div>
            <div className="mb-4">
              <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                Figures ({loteriaFigures.length})
              </Label>
              {loteriaFigures.length > 0 && (
                <div className="grid grid-cols-6 gap-1 max-h-40 overflow-y-auto">
                  {loteriaFigures.map(fig => (
                    <div key={fig.id}>
                      <img src={fig.src} alt={fig.name} className="w-full aspect-square object-cover rounded border border-neutral-700" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Number of Boards */}
            <div className="mb-4">
              <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                Number of Boards: {loteriaBoardCount}
              </Label>
              <div className="flex gap-2">
                <Slider data-testid="loteria-board-count-slider"
                  min={0} max={100} step={1} value={[loteriaBoardCount]}
                  onValueChange={([v]) => handleLoteriaBoardCountChange(v)} className="flex-1" />
                <input data-testid="loteria-board-count-input" type="number" min={0} max={200}
                  value={loteriaBoardCount} onChange={(e) => handleLoteriaBoardCountChange(parseInt(e.target.value) || 0)}
                  className={`w-14 px-2 py-1 text-sm text-center font-bold border-2 rounded ${isDarkMode ? 'bg-neutral-900 border-neutral-700 text-white' : 'border-black'}`} />
              </div>
            </div>

            {/* One board a different grid size */}
            {loteriaBoardCount > 1 && (
              <div className="mb-4" data-testid="loteria-oneoff-section">
                <button
                  type="button"
                  data-testid="loteria-oneoff-toggle"
                  onClick={() => setLoteriaOneOffEnabled(!loteriaOneOffEnabled)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold transition-colors ${loteriaOneOffEnabled ? 'bg-amber-600 text-white' : (isDarkMode ? 'bg-neutral-800 text-neutral-300' : 'bg-neutral-200 text-black')}`}
                >
                  <span>Make one board a different size</span>
                  <span className={`ml-2 inline-flex h-4 w-7 items-center rounded-full transition-colors ${loteriaOneOffEnabled ? 'bg-white/90' : 'bg-neutral-500/50'}`}>
                    <span className={`h-3 w-3 rounded-full bg-amber-600 transition-transform ${loteriaOneOffEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                  </span>
                </button>
                {loteriaOneOffEnabled && (
                  <div className="mt-2 space-y-2">
                    <div>
                      <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                        Which board? (Board #{loteriaOneOffBoardIndex + 1})
                      </Label>
                      <input
                        data-testid="loteria-oneoff-board-input"
                        type="number" min={1} max={loteriaBoardCount}
                        value={loteriaOneOffBoardIndex + 1}
                        onChange={(e) => {
                          const n = parseInt(e.target.value) || 1;
                          const clamped = Math.max(1, Math.min(loteriaBoardCount, n));
                          setLoteriaOneOffBoardIndex(clamped - 1);
                        }}
                        className={`w-16 px-2 py-1 text-sm text-center font-bold border-2 rounded ${isDarkMode ? 'bg-neutral-900 border-neutral-700 text-white' : 'border-black'}`}
                      />
                    </div>
                    <div>
                      <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                        Its grid size
                      </Label>
                      <div className="grid grid-cols-4 gap-1">
                        {[3,'3x4','4x3','3x5','5x3',4,'4x5','5x4',5,'3x6','6x3','5x6','6x5',6,'6x7','7x6','5x7','7x5',7,8,9,10,11,12,13,14,15,16].map(size => {
                          const label = typeof size === 'string' ? size : `${size}x${size}`;
                          const isSel = String(loteriaOneOffGridSize) === String(size);
                          return (
                            <button key={`oneoff-${size}`} data-testid={`loteria-oneoff-grid-${size}`}
                              onClick={() => setLoteriaOneOffGridSize(size)}
                              className={`py-1 text-[9px] font-black rounded border-2 transition-colors ${
                                isSel
                                  ? isDarkMode ? 'bg-amber-600 border-amber-500 text-white' : 'bg-amber-500 border-amber-600 text-white'
                                  : isDarkMode ? 'border-neutral-700 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                              }`}>{label}</button>
                          );
                        })}
                      </div>
                    </div>
                    {/* Independent numbering for this one board */}
                    <div className="pt-1 border-t border-dashed border-amber-500/40">
                      <button
                        type="button"
                        data-testid="loteria-oneoff-numbering-toggle"
                        onClick={() => setLoteriaOneOffNumberingEnabled(!loteriaOneOffNumberingEnabled)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold transition-colors ${loteriaOneOffNumberingEnabled ? 'bg-amber-600 text-white' : (isDarkMode ? 'bg-neutral-800 text-neutral-300' : 'bg-neutral-200 text-black')}`}
                      >
                        <span>Number this board separately</span>
                        <span className={`ml-2 inline-flex h-4 w-7 items-center rounded-full transition-colors ${loteriaOneOffNumberingEnabled ? 'bg-white/90' : 'bg-neutral-500/50'}`}>
                          <span className={`h-3 w-3 rounded-full bg-amber-600 transition-transform ${loteriaOneOffNumberingEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                        </span>
                      </button>
                      {loteriaOneOffNumberingEnabled && (
                        <div className="mt-2 space-y-2">
                          <div className="grid grid-cols-2 gap-1">
                            <button
                              type="button"
                              data-testid="loteria-oneoff-rownums-toggle"
                              onClick={() => setLoteriaOneOffShowRowNums(!loteriaOneOffShowRowNums)}
                              className={`py-1.5 text-[10px] font-black rounded border-2 transition-colors ${loteriaOneOffShowRowNums ? 'bg-amber-500 border-amber-600 text-white' : (isDarkMode ? 'border-neutral-700 text-neutral-400' : 'border-gray-300')}`}
                            >Vertical (rows)</button>
                            <button
                              type="button"
                              data-testid="loteria-oneoff-colnums-toggle"
                              onClick={() => setLoteriaOneOffShowColNums(!loteriaOneOffShowColNums)}
                              className={`py-1.5 text-[10px] font-black rounded border-2 transition-colors ${loteriaOneOffShowColNums ? 'bg-amber-500 border-amber-600 text-white' : (isDarkMode ? 'border-neutral-700 text-neutral-400' : 'border-gray-300')}`}
                            >Horizontal (cols)</button>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className={`font-bold text-[10px] ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Start at</Label>
                            <input
                              data-testid="loteria-oneoff-startnum-input"
                              type="number" min={1} max={9999}
                              value={loteriaOneOffStartingNum}
                              onChange={(e) => setLoteriaOneOffStartingNum(Math.max(1, parseInt(e.target.value) || 1))}
                              className={`w-16 px-2 py-1 text-sm text-center font-bold border-2 rounded ${isDarkMode ? 'bg-neutral-900 border-neutral-700 text-white' : 'border-black'}`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Doubles/Triples on ONE board (row + column), independent of the
                global repeat mode. Grid capped to <= 54 cells. */}
            {loteriaBoardCount > 0 && (
              <div className="mb-4" data-testid="loteria-single-repeat-section">
                <button
                  type="button"
                  data-testid="loteria-single-repeat-toggle"
                  onClick={() => setLoteriaSingleRepeatEnabled(!loteriaSingleRepeatEnabled)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold transition-colors ${loteriaSingleRepeatEnabled ? 'bg-amber-600 text-white' : (isDarkMode ? 'bg-neutral-800 text-neutral-300' : 'bg-neutral-200 text-black')}`}
                >
                  <span>Doubles/Triples on one board</span>
                  <span className={`ml-2 inline-flex h-4 w-7 items-center rounded-full transition-colors ${loteriaSingleRepeatEnabled ? 'bg-white/90' : 'bg-neutral-500/50'}`}>
                    <span className={`h-3 w-3 rounded-full bg-amber-600 transition-transform ${loteriaSingleRepeatEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                  </span>
                </button>
                {loteriaSingleRepeatEnabled && (
                  <div className="mt-2 space-y-2">
                    <p className={`text-[9px] ${isDarkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                      One board shows a single image repeated across a row AND a column. Max 54 images — no larger grids allowed.
                    </p>
                    {loteriaBoardCount > 1 && (
                      <div>
                        <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                          Which board? (Board #{loteriaSingleRepeatBoardIndex + 1})
                        </Label>
                        <input
                          data-testid="loteria-single-repeat-board-input"
                          type="number" min={1} max={loteriaBoardCount}
                          value={loteriaSingleRepeatBoardIndex + 1}
                          onChange={(e) => {
                            const n = parseInt(e.target.value) || 1;
                            const clamped = Math.max(1, Math.min(loteriaBoardCount, n));
                            setLoteriaSingleRepeatBoardIndex(clamped - 1);
                          }}
                          className={`w-16 px-2 py-1 text-sm text-center font-bold border-2 rounded ${isDarkMode ? 'bg-neutral-900 border-neutral-700 text-white' : 'border-black'}`}
                        />
                      </div>
                    )}
                    <div>
                      <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                        Repeat
                      </Label>
                      <div className="grid grid-cols-2 gap-1">
                        {[
                          { id: 'doubles', label: 'Doubles' },
                          { id: 'triples', label: 'Triples' },
                        ].map(opt => (
                          <button key={opt.id} data-testid={`loteria-single-repeat-mode-${opt.id}`}
                            onClick={() => setLoteriaSingleRepeatMode(opt.id)}
                            className={`py-1.5 text-[10px] font-black rounded border-2 transition-colors ${
                              loteriaSingleRepeatMode === opt.id
                                ? isDarkMode ? 'bg-amber-600 border-amber-500 text-white' : 'bg-amber-500 border-amber-600 text-white'
                                : isDarkMode ? 'border-neutral-700 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                            }`}>{opt.label}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                        Its grid size (max 54 images)
                      </Label>
                      <div className="grid grid-cols-4 gap-1">
                        {[3,'3x4','4x3','3x5','5x3',4,'4x5','5x4',5,'3x6','6x3','5x6','6x5',6,'6x7','7x6','5x7','7x5',7].map(size => {
                          const label = typeof size === 'string' ? size : `${size}x${size}`;
                          const isSel = String(loteriaSingleRepeatGridSize) === String(size);
                          return (
                            <button key={`singlerep-${size}`} data-testid={`loteria-single-repeat-grid-${size}`}
                              onClick={() => setLoteriaSingleRepeatGridSize(size)}
                              className={`py-1 text-[9px] font-black rounded border-2 transition-colors ${
                                isSel
                                  ? isDarkMode ? 'bg-amber-600 border-amber-500 text-white' : 'bg-amber-500 border-amber-600 text-white'
                                  : isDarkMode ? 'border-neutral-700 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                              }`}>{label}</button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Board Opacity */}
            {loteriaBoardCount > 0 && (
              <>
                <div className="mb-3">
                  <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                    Board Opacity: {boardOpacity}%
                  </Label>
                  <Slider min={10} max={100} step={1} value={[boardOpacity]}
                    onValueChange={([v]) => setBoardOpacity(v)} className="my-2" />
                </div>

                {/* Regenerate Layout */}
                <button data-testid="loteria-regenerate-btn" onClick={regenerateLoteriaBoards}
                  className={`w-full py-2 text-xs font-bold rounded border-2 transition-colors ${
                    isDarkMode ? 'border-amber-600 text-amber-400 hover:bg-amber-600/20' : 'border-amber-500 text-amber-600 hover:bg-amber-50'
                  }`}>Shuffle Figures on Boards</button>

                {/* Reshuffle Images - like Bingo's Regenerate Numbers */}
                <button
                  data-testid="loteria-reshuffle-btn"
                  onClick={reshuffleLoteriaImages}
                  className={`mt-2 w-full py-2 border-2 font-bold text-sm flex items-center justify-center gap-2 ${
                    isDarkMode ? 'border-neutral-600 text-white hover:bg-neutral-800' : 'border-black hover:bg-gray-100'
                  }`}
                >
                  <RefreshCw className="w-4 h-4" /> Reshuffle Images
                </button>
              </>
            )}

            {/* Board Layout (first, before resize) */}
            {loteriaBoardCount > 0 && (() => {
              const dims = boardLayout || getDefaultGridDims(loteriaBoardCount);
              const cols = dims.cols || 1;
              const rows = dims.rows || 1;
              const disabled = boardLayout?.disabled || new Set();
              const totalSlots = cols * rows;
              const enabledCount = totalSlots - disabled.size;

              const toggleCell = (r, c) => {
                const key = `${r}-${c}`;
                const newDisabled = new Set(disabled);
                if (newDisabled.has(key)) newDisabled.delete(key);
                else {
                  if (enabledCount <= loteriaBoardCount) return;
                  newDisabled.add(key);
                }
                applyBoardLayout({ cols, rows, disabled: newDisabled });
              };
              const addRow = () => applyBoardLayout({ cols, rows: rows + 1, disabled: new Set(disabled) });
              const removeRow = () => {
                if (rows <= 1) return;
                const nd = new Set(); disabled.forEach(k => { const [r] = k.split('-').map(Number); if (r < rows - 1) nd.add(k); });
                if ((rows - 1) * cols - nd.size < loteriaBoardCount) return;
                applyBoardLayout({ cols, rows: rows - 1, disabled: nd });
              };
              const addCol = () => applyBoardLayout({ cols: cols + 1, rows, disabled: new Set(disabled) });
              const removeCol = () => {
                if (cols <= 1) return;
                const nd = new Set(); disabled.forEach(k => { const [,c] = k.split('-').map(Number); if (c < cols - 1) nd.add(k); });
                if (rows * (cols - 1) - nd.size < loteriaBoardCount) return;
                applyBoardLayout({ cols: cols - 1, rows, disabled: nd });
              };

              const cellPx = Math.max(14, Math.min(28, 160 / Math.max(cols, rows)));
              return (
                <div className={`mt-4 p-3 border-2 rounded ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-black bg-amber-50/40'}`} data-testid="loteria-layout-configurator">
                  <div className="flex items-center gap-2 mb-2">
                    <Grid3X3 className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} strokeWidth={2.5} />
                    <Label className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>Board Layout</Label>
                    <span className={`ml-auto text-[10px] font-mono ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>{cols}x{rows}</span>
                  </div>
                  <div className="flex justify-center mb-2">
                    <div className="inline-grid gap-[2px]" style={{ gridTemplateColumns: `repeat(${cols}, ${cellPx}px)` }}>
                      {Array.from({ length: rows }, (_, r) =>
                        Array.from({ length: cols }, (_, c) => {
                          const key = `${r}-${c}`;
                          const isDisabled = disabled.has(key);
                          let slotIndex = 0, found = false;
                          for (let ri = 0; ri <= r; ri++) {
                            for (let ci = 0; ci < (ri === r ? c + 1 : cols); ci++) {
                              if (!disabled.has(`${ri}-${ci}`)) {
                                if (ri === r && ci === c) { found = true; break; }
                                slotIndex++;
                              }
                            }
                            if (found) break;
                          }
                          const hasBoard = !isDisabled && slotIndex < loteriaBoardCount;
                          return (
                            <button key={key} data-testid={`loteria-layout-cell-${r}-${c}`} onClick={() => toggleCell(r, c)}
                              className={`rounded-sm border transition-all ${isDisabled
                                ? isDarkMode ? 'bg-neutral-800 border-neutral-700 opacity-30' : 'bg-gray-200 border-gray-300 opacity-40'
                                : hasBoard
                                  ? isDarkMode ? 'bg-amber-600 border-amber-500 shadow-sm shadow-amber-500/30' : 'bg-amber-500 border-amber-600'
                                  : isDarkMode ? 'bg-neutral-700 border-neutral-600' : 'bg-white border-gray-400'
                              }`} style={{ width: cellPx, height: cellPx }} />
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={removeCol} className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'}`}>Col -</button>
                    <button onClick={addCol} className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'}`}>Col +</button>
                    <span className={`mx-1 text-[10px] ${isDarkMode ? 'text-neutral-600' : 'text-neutral-300'}`}>|</span>
                    <button onClick={removeRow} className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'}`}>Row -</button>
                    <button onClick={addRow} className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'}`}>Row +</button>
                  </div>
                </div>
              );
            })()}

            {/* Board Shape UI removed — the Figure Pack selector already
                drives the shape (Square deck → square cells, Original/Enhanced
                → narrow). The underlying state is kept for save/load. */}

            {/* Resize All Boards */}
            {loteriaBoardCount > 0 && (
              <div className={`mt-4 mb-4 p-3 border-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-black bg-[#FDE047]/20'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Maximize2 className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-black'}`} strokeWidth={2.5} />
                  <Label className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>
                    Resize All Boards
                  </Label>
                </div>
                <Label className={`text-xs mb-2 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                  Board Size: {Math.round(currentBoardWidth)}px
                </Label>
                <Slider
                  data-testid="loteria-board-size-slider"
                  value={[currentBoardWidth]}
                  onValueChange={([val]) => handleManualBoardResize(val)}
                  min={20}
                  max={500}
                  step={2}
                />
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                  All boards resize together in unison
                </p>
              </div>
            )}

            {/* Move Mode */}
            {loteriaBoardCount > 0 && (
              <div className={`mb-4 p-3 border-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-black bg-[#FDE047]/20'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Move className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-black'}`} strokeWidth={2.5} />
                  <Label className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>
                    Move Boards
                  </Label>
                </div>
                <div className="flex gap-1.5">
                  <button data-testid="move-mode-individual"
                    onClick={() => setLoteriaMoveMode('individual')}
                    className={`flex-1 px-2 py-1.5 text-[10px] font-bold border-2 transition-colors ${
                      loteriaMoveMode === 'individual'
                        ? 'bg-amber-500 text-black border-amber-500'
                        : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                    }`}>
                    Individual
                  </button>
                  <button data-testid="move-mode-horizontal"
                    onClick={() => setLoteriaMoveMode('horizontal')}
                    className={`flex-1 px-2 py-1.5 text-[10px] font-bold border-2 transition-colors ${
                      loteriaMoveMode === 'horizontal'
                        ? 'bg-amber-500 text-black border-amber-500'
                        : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                    }`}>
                    Horizontal
                  </button>
                  <button data-testid="move-mode-vertical"
                    onClick={() => setLoteriaMoveMode('vertical')}
                    className={`flex-1 px-2 py-1.5 text-[10px] font-bold border-2 transition-colors ${
                      loteriaMoveMode === 'vertical'
                        ? 'bg-amber-500 text-black border-amber-500'
                        : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                    }`}>
                    Vertical
                  </button>
                </div>
              </div>
            )}

            {/* Neighbors Boards */}
            {loteriaBoardCount > 1 && (
              <div className="mb-4">
                <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Neighbors Boards</Label>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input type="checkbox" checked={neighborsEnabled} onChange={(e) => setNeighborsEnabled(e.target.checked)}
                    data-testid="neighbors-enabled" className="w-4 h-4 accent-amber-500" />
                  <span className={`text-xs font-bold ${isDarkMode ? 'text-neutral-300' : ''}`}>Enable Connecting Images</span>
                </label>
                {neighborsEnabled && (
                  <div className="space-y-2 pl-1">
                    {/* Quad mode toggle — when on, replaces the pair-based layout
                        with a single image bridging 4 boards in a 2x2 arrangement. */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!neighborsQuadEnabled}
                        onChange={(e) => setNeighborsQuadEnabled && setNeighborsQuadEnabled(e.target.checked)}
                        data-testid="neighbors-quad-enabled"
                        className="w-4 h-4 accent-amber-500"
                      />
                      <span className={`text-[11px] font-bold ${isDarkMode ? 'text-neutral-300' : ''}`}>
                        Quad mode (joins 4 boards in a 2×2 layout)
                      </span>
                    </label>
                    {neighborsQuadEnabled ? (
                      <div>
                        <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Cells per Board</span>
                        <div className="flex gap-1">
                          {[1, 2].map(n => (
                            <button key={n} data-testid={`neighbors-quad-cells-${n}`}
                              onClick={() => setNeighborsQuadCellsPerBoard && setNeighborsQuadCellsPerBoard(n)}
                              className={`flex-1 py-1 text-[10px] font-bold border-2 transition-colors ${
                                (neighborsQuadCellsPerBoard || 1) === n
                                  ? 'bg-amber-500 text-black border-amber-600'
                                  : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                              }`}>{n} {n === 1 ? 'cell' : 'cells'}</button>
                          ))}
                        </div>
                        <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                          {(neighborsQuadCellsPerBoard || 1) === 1
                            ? 'Image spans 1 cell from each of the 4 boards (2×2 cells total).'
                            : 'Image spans 2×2 cells from each of the 4 boards (4×4 cells total).'}
                        </p>
                      </div>
                    ) : (
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Cells per Board</span>
                      <div className="flex gap-1">
                        {[2, 3].map(n => (
                          <button key={n} data-testid={`neighbors-rows-${n}`}
                            onClick={() => setNeighborsRowsPerBoard && setNeighborsRowsPerBoard(n)}
                            className={`flex-1 py-1 text-[10px] font-bold border-2 transition-colors ${
                              (neighborsRowsPerBoard || 2) === n
                                ? 'bg-amber-500 text-black border-amber-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>{n} cells</button>
                        ))}
                      </div>
                      <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                        How many cells (rows) each board contributes to the connecting image. Total covered = {((neighborsRowsPerBoard || 2) * 2)} cells per pair.
                      </p>
                    </div>
                    )}
                    {!neighborsQuadEnabled && (
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Direction</span>
                      <div className="flex gap-1">
                        {[
                          { id: 'horizontal', label: 'Side-by-Side' },
                          { id: 'vertical', label: 'Top/Bottom' },
                        ].map(d => (
                          <button key={d.id} data-testid={`neighbors-direction-${d.id}`}
                            onClick={() => setNeighborsDirection && setNeighborsDirection(d.id)}
                            title={d.id === 'horizontal' ? 'Connecting image between left/right boards' : 'Connecting image between top/bottom boards'}
                            className={`flex-1 py-1 text-[10px] font-bold border-2 transition-colors ${
                              (neighborsDirection || 'horizontal') === d.id
                                ? 'bg-amber-500 text-black border-amber-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>{d.label}</button>
                        ))}
                      </div>
                      <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                        {(neighborsDirection || 'horizontal') === 'vertical'
                          ? 'Connects each board with the board directly underneath.'
                          : 'Connects each board with the board next to it horizontally.'}
                      </p>
                      {/* Vertical Pair Numbering — re-numbers board badges as
                          (top of pair 1 = 1, bottom = 2, top of pair 2 = 3,
                          bottom = 4, ...) column-major within each row-pair,
                          continuing across row-pairs. Only available with
                          vertical direction. */}
                      {(neighborsDirection || 'horizontal') === 'vertical' && (
                        <label
                          className={`mt-2 flex items-center gap-2 cursor-pointer p-2 border-2 ${
                            neighborsVerticalPairNumbering
                              ? 'bg-amber-500/10 border-amber-500'
                              : isDarkMode ? 'border-neutral-700 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid="neighbors-vertical-pair-numbering-label"
                        >
                          <input
                            type="checkbox"
                            checked={!!neighborsVerticalPairNumbering}
                            onChange={(e) => setNeighborsVerticalPairNumbering && setNeighborsVerticalPairNumbering(e.target.checked)}
                            className="w-4 h-4 accent-amber-500"
                            data-testid="neighbors-vertical-pair-numbering-toggle"
                          />
                          <div className="flex-1">
                            <span className={`text-[10px] font-bold block ${isDarkMode ? 'text-neutral-200' : 'text-black'}`}>
                              Vertical Pair Numbering
                            </span>
                            <span className={`text-[9px] ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                              Numbers each pair top-then-bottom (1, 2, 3, 4, …) moving across columns within each row-pair.
                            </span>
                          </div>
                        </label>
                      )}
                      {/* Badge alignment along the top/bottom edge — Left,
                          Middle, or Right. Only shown when VPN is ON. The top
                          board badge sits on the TOP edge of the top board,
                          the bottom board badge on the BOTTOM edge of the
                          bottom board, both aligned to this choice. */}
                      {(neighborsDirection || 'horizontal') === 'vertical' && !!neighborsVerticalPairNumbering && !pairStackNumbersEnabled && (
                        <div className="mt-2">
                          <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            Badge Position (Top / Bottom Edge)
                          </span>
                          <div className="flex gap-1">
                            {[
                              { id: 'left', label: 'Left' },
                              { id: 'center', label: 'Middle' },
                              { id: 'right', label: 'Right' },
                            ].map(a => (
                              <button
                                key={a.id}
                                data-testid={`vertical-pair-badge-align-${a.id}`}
                                onClick={() => setVerticalPairBadgeAlign && setVerticalPairBadgeAlign(a.id)}
                                className={`flex-1 py-1 text-[10px] font-bold border-2 transition-colors ${
                                  (verticalPairBadgeAlign || 'center') === a.id
                                    ? 'bg-amber-500 text-black border-amber-600'
                                    : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                }`}
                              >{a.label}</button>
                            ))}
                          </div>
                          <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                            #1 sits on top of the top board, #2 on the bottom of the bottom board.
                          </p>
                        </div>
                      )}
                      {/* Stack pair numbers above the TOP board — renders the
                          two pair numbers vertically stacked (top board's number
                          on top, bottom board's just below) centered above the
                          top edge. Movable as a group via X/Y sliders. Vertical
                          direction only. */}
                      {(neighborsDirection || 'horizontal') === 'vertical' && (
                        <div className="mt-2">
                          <label
                            className={`flex items-center gap-2 cursor-pointer p-2 border-2 ${
                              pairStackNumbersEnabled
                                ? 'bg-amber-500/10 border-amber-500'
                                : isDarkMode ? 'border-neutral-700 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-50'
                            }`}
                            data-testid="pair-stack-numbers-label"
                          >
                            <input
                              type="checkbox"
                              checked={!!pairStackNumbersEnabled}
                              onChange={(e) => setPairStackNumbersEnabled && setPairStackNumbersEnabled(e.target.checked)}
                              className="w-4 h-4 accent-amber-500"
                              data-testid="pair-stack-numbers-toggle"
                            />
                            <div className="flex-1">
                              <span className={`text-[10px] font-bold block ${isDarkMode ? 'text-neutral-200' : 'text-black'}`}>
                                Stack Numbers Above Board
                              </span>
                              <span className={`text-[9px] ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                                Both pair numbers stacked (#1 on top, #2 below) centered above the top edge.
                              </span>
                            </div>
                          </label>
                          {pairStackNumbersEnabled && (
                            <div className="mt-2 space-y-2">
                              <div>
                                <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                                  Move Left / Right: {pairStackOffsetX || 0}%
                                </span>
                                <Slider
                                  data-testid="pair-stack-offset-x"
                                  value={[pairStackOffsetX || 0]}
                                  onValueChange={([v]) => setPairStackOffsetX && setPairStackOffsetX(v)}
                                  min={-100} max={100} step={1}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                                  Move Up / Down: {pairStackOffsetY || 0}%
                                </span>
                                <Slider
                                  data-testid="pair-stack-offset-y"
                                  value={[pairStackOffsetY || 0]}
                                  onValueChange={([v]) => setPairStackOffsetY && setPairStackOffsetY(v)}
                                  min={-100} max={100} step={1}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    )}
                    {/* Custom Vertical Pairs — structured rows. Each pair
                        has Board A #, Board B #, and a Gap slider that
                        controls vertical separation in canvas pixels. The
                        layout effect in BingoGenerator snaps board B to sit
                        exactly `gap` px below board A, then the connecting
                        image renders between them. Add multiple pairs with
                        the + button. Live-updates as you type — no Enter
                        / blur required. */}
                    {!neighborsQuadEnabled && (
                    <div className={`mt-2 p-2 border ${isDarkMode ? 'border-neutral-700 bg-neutral-900/30' : 'border-gray-300 bg-gray-50'}`}>
                      <Label className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                        Custom Vertical Pairs
                      </Label>
                      <div className="space-y-1.5">
                        {(customVerticalPairs || []).map((pair, idx) => {
                          const updatePair = (patch) => {
                            const next = [...(customVerticalPairs || [])];
                            next[idx] = { ...next[idx], ...patch };
                            setCustomVerticalPairs && setCustomVerticalPairs(next);
                          };
                          const removePair = () => {
                            const next = (customVerticalPairs || []).filter((_, i) => i !== idx);
                            setCustomVerticalPairs && setCustomVerticalPairs(next);
                          };
                          return (
                            <div key={idx} className={`p-1.5 border ${isDarkMode ? 'border-neutral-700 bg-neutral-800/50' : 'border-gray-200 bg-white'}`}>
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min={1}
                                  data-testid={`custom-pair-a-${idx}`}
                                  defaultValue={pair.a || ''}
                                  onBlur={(e) => {
                                    const v = parseInt(e.target.value, 10) || 0;
                                    if (v !== (pair.a || 0)) updatePair({ a: v });
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                  placeholder="1"
                                  className={`w-12 text-[10px] font-mono px-1 py-0.5 border ${isDarkMode ? 'bg-neutral-900 border-neutral-600 text-white' : 'border-gray-300'}`}
                                />
                                <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-500' : 'text-gray-400'}`}>↕</span>
                                <input
                                  type="number"
                                  min={1}
                                  data-testid={`custom-pair-b-${idx}`}
                                  defaultValue={pair.b || ''}
                                  onBlur={(e) => {
                                    const v = parseInt(e.target.value, 10) || 0;
                                    if (v !== (pair.b || 0)) updatePair({ b: v });
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                  placeholder="2"
                                  className={`w-12 text-[10px] font-mono px-1 py-0.5 border ${isDarkMode ? 'bg-neutral-900 border-neutral-600 text-white' : 'border-gray-300'}`}
                                />
                                <button
                                  data-testid={`custom-pair-remove-${idx}`}
                                  onClick={removePair}
                                  title="Remove pair"
                                  className={`ml-auto w-5 h-5 flex items-center justify-center text-[12px] font-black border ${isDarkMode ? 'border-neutral-600 text-neutral-400 hover:bg-red-600/30 hover:text-red-400' : 'border-gray-300 text-gray-400 hover:bg-red-100 hover:text-red-600'}`}
                                >
                                  ×
                                </button>
                              </div>
                              <div className="mt-1">
                                <Label className={`text-[9px] ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                                  Vertical Gap: {pair.gap != null ? pair.gap : 20}px
                                </Label>
                                <Slider
                                  data-testid={`custom-pair-gap-${idx}`}
                                  value={[pair.gap != null ? pair.gap : 20]}
                                  onValueChange={([val]) => updatePair({ gap: val })}
                                  min={-300}
                                  max={300}
                                  step={1}
                                  className="mt-1"
                                />
                              </div>
                              {/* Offset X/Y — move the entire pair (both
                                  boards) within the canvas without affecting
                                  any other boards in the layout. Range
                                  tightened to ±400 for finer slider control. */}
                              <div className="mt-1">
                                <Label className={`text-[9px] ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                                  Offset X: {pair.offsetX != null ? pair.offsetX : 0}px
                                </Label>
                                <Slider
                                  data-testid={`custom-pair-offset-x-${idx}`}
                                  value={[pair.offsetX != null ? pair.offsetX : 0]}
                                  onValueChange={([val]) => updatePair({ offsetX: val })}
                                  min={-400}
                                  max={400}
                                  step={1}
                                  className="mt-1"
                                />
                              </div>
                              <div className="mt-1">
                                <Label className={`text-[9px] ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                                  Offset Y: {pair.offsetY != null ? pair.offsetY : 0}px
                                </Label>
                                <Slider
                                  data-testid={`custom-pair-offset-y-${idx}`}
                                  value={[pair.offsetY != null ? pair.offsetY : 0]}
                                  onValueChange={([val]) => updatePair({ offsetY: val })}
                                  min={-400}
                                  max={400}
                                  step={1}
                                  className="mt-1"
                                />
                              </div>
                              <button
                                data-testid={`custom-pair-reset-pos-${idx}`}
                                onClick={() => updatePair({ offsetX: 0, offsetY: 0 })}
                                className={`mt-1 text-[9px] font-bold underline ${isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                              >
                                Reset position
                              </button>
                              {/* Board # badge position override — applies
                                  ONLY to the two boards in this pair.
                                  Percent of board width / height. Lets the
                                  user nudge the number badge onto / off the
                                  boards independently of the global setting. */}
                              <div className={`mt-1.5 pt-1.5 border-t border-dashed ${isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}>
                                <Label className={`text-[9px] font-bold block ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                                  Board # Position (this pair only)
                                </Label>
                                <Label className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                                  Badge Shift X: {pair.badgeShiftX != null ? pair.badgeShiftX : 0}%
                                </Label>
                                <Slider
                                  data-testid={`custom-pair-badge-x-${idx}`}
                                  value={[pair.badgeShiftX != null ? pair.badgeShiftX : 0]}
                                  onValueChange={([val]) => updatePair({ badgeShiftX: val })}
                                  min={-100}
                                  max={100}
                                  step={1}
                                  className="mt-1"
                                />
                                <Label className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                                  Badge Shift Y: {pair.badgeShiftY != null ? pair.badgeShiftY : 0}%
                                </Label>
                                <Slider
                                  data-testid={`custom-pair-badge-y-${idx}`}
                                  value={[pair.badgeShiftY != null ? pair.badgeShiftY : 0]}
                                  onValueChange={([val]) => updatePair({ badgeShiftY: val })}
                                  min={-100}
                                  max={100}
                                  step={1}
                                  className="mt-1"
                                />
                                <button
                                  data-testid={`custom-pair-reset-badge-${idx}`}
                                  onClick={() => updatePair({ badgeShiftX: 0, badgeShiftY: 0 })}
                                  className={`mt-1 text-[9px] font-bold underline ${isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                                >
                                  Reset badge position
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        data-testid="custom-pair-add"
                        onClick={() => {
                          const next = [...(customVerticalPairs || []), { a: 0, b: 0, gap: 20, offsetX: 0, offsetY: 0 }];
                          setCustomVerticalPairs && setCustomVerticalPairs(next);
                        }}
                        className={`mt-1.5 w-full py-1 text-[10px] font-bold border-2 border-dashed transition-colors ${
                          isDarkMode ? 'border-neutral-600 text-neutral-400 hover:border-amber-500 hover:text-amber-400' : 'border-gray-300 text-gray-500 hover:border-amber-600 hover:text-amber-700'
                        }`}
                      >
                        + Add Pair
                      </button>
                      <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                        Board 2 snaps below Board 1 with the chosen vertical gap. Drag Offset X / Y to move the whole pair anywhere on the canvas.
                      </p>
                    </div>
                    )}
                    {!neighborsQuadEnabled && (
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Position</span>
                      <div className="flex gap-1">
                        {['top', 'middle', 'bottom'].map(pos => (
                          <button key={pos} data-testid={`neighbors-pos-${pos}`}
                            onClick={() => setNeighborsPosition(pos)}
                            className={`flex-1 py-1 text-[10px] font-bold capitalize border-2 transition-colors ${
                              neighborsPosition === pos
                                ? 'bg-amber-500 text-black border-amber-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>{pos}</button>
                        ))}
                      </div>
                    </div>
                    )}
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Shape</span>
                      <div className="grid grid-cols-5 gap-1">
                        {['square', 'circle', 'diamond', 'heart', 'flower'].map(sh => (
                          <button key={sh} data-testid={`neighbors-shape-${sh}`}
                            onClick={() => setNeighborsShape(sh)}
                            title={sh.charAt(0).toUpperCase() + sh.slice(1)}
                            className={`py-1 text-[10px] font-bold capitalize border-2 transition-colors ${
                              (neighborsShape || 'square') === sh
                                ? 'bg-amber-500 text-black border-amber-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>{sh}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Highlight Color</span>
                      <div className="flex items-center gap-2">
                        <input type="color" value={neighborsHighlightColor} onChange={(e) => setNeighborsHighlightColor(e.target.value)}
                          className="w-8 h-8 cursor-pointer rounded border-2 border-gray-600" />
                        <span className={`text-[10px] font-mono ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{neighborsHighlightColor}</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Outline Thickness</span>
                        <span className={`text-[10px] font-mono ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{neighborsBorderThickness}px</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="15"
                        value={neighborsBorderThickness}
                        onChange={(e) => setNeighborsBorderThickness(parseInt(e.target.value, 10))}
                        data-testid="neighbors-border-thickness"
                        className="w-full accent-amber-500"
                      />
                    </div>
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Outline Style</span>
                      <div className="grid grid-cols-3 gap-1">
                        {['sharp', 'rounded', 'soft'].map(st => (
                          <button key={st} data-testid={`neighbors-outline-${st}`}
                            onClick={() => setNeighborsOutlineStyle(st)}
                            title={st.charAt(0).toUpperCase() + st.slice(1)}
                            className={`py-1 text-[10px] font-bold capitalize border-2 transition-colors ${
                              (neighborsOutlineStyle || 'sharp') === st
                                ? 'bg-amber-500 text-black border-amber-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>{st}</button>
                        ))}
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!neighborsShadowEnabled}
                        onChange={(e) => setNeighborsShadowEnabled(e.target.checked)}
                        data-testid="neighbors-shadow-enabled"
                        className="w-4 h-4 accent-amber-500"
                      />
                      <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : ''}`}>Drop Shadow</span>
                    </label>
                    {/* Custom Connecting Image — upload replaces the loteria
                        figure card for every neighbor pair. Independent size
                        + alpha-silhouette outline (color + width). Uses all
                        the existing loteria neighbor geometry (cells covered,
                        direction, quad mode, position). */}
                    <div className={`mt-2 p-2 rounded border ${isDarkMode ? 'border-neutral-800 bg-neutral-900/60' : 'border-amber-200 bg-amber-50'}`}>
                      <Label className={`font-bold text-[11px] mb-2 block ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Custom Connecting Image</Label>
                      <div className="flex items-center gap-2 mb-2">
                        <label
                          data-testid="neighbors-custom-image-upload"
                          title={neighborsCustomImage ? 'Uploaded — click to replace' : 'Upload PNG/JPG to use instead of a loteria figure'}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold border-2 cursor-pointer transition-colors ${
                            neighborsCustomImage
                              ? 'bg-amber-500 text-black border-amber-600'
                              : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {neighborsCustomImage ? 'Uploaded ✓ — replace' : 'Upload image'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = () => setNeighborsCustomImage && setNeighborsCustomImage(reader.result);
                              reader.readAsDataURL(file);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        {neighborsCustomImage && (
                          <button
                            type="button"
                            data-testid="neighbors-custom-image-clear"
                            onClick={() => setNeighborsCustomImage && setNeighborsCustomImage(null)}
                            className={`px-2 py-1 text-[10px] font-bold border-2 transition-colors ${
                              isDarkMode ? 'border-red-500/50 text-red-300 hover:bg-red-500/20' : 'border-red-300 text-red-600 hover:bg-red-50'
                            }`}
                          >Clear</button>
                        )}
                      </div>
                      {neighborsCustomImage && (
                        <>
                          <div className="mb-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Image Size</span>
                              <span className={`text-[10px] font-mono ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{neighborsCustomImageSize != null ? neighborsCustomImageSize : 100}%</span>
                            </div>
                            <input
                              data-testid="neighbors-custom-image-size"
                              type="range" min={20} max={200} step={5}
                              value={neighborsCustomImageSize != null ? neighborsCustomImageSize : 100}
                              onChange={(e) => setNeighborsCustomImageSize && setNeighborsCustomImageSize(parseInt(e.target.value, 10))}
                              className="w-full accent-amber-500"
                            />
                          </div>
                          <div className="mb-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Image Outline Width</span>
                              <span className={`text-[10px] font-mono ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{neighborsCustomOutlineWidth || 0}px</span>
                            </div>
                            <input
                              data-testid="neighbors-custom-outline-width"
                              type="range" min={0} max={20} step={1}
                              value={neighborsCustomOutlineWidth || 0}
                              onChange={(e) => setNeighborsCustomOutlineWidth && setNeighborsCustomOutlineWidth(parseInt(e.target.value, 10))}
                              className="w-full accent-amber-500"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              data-testid="neighbors-custom-outline-color"
                              value={neighborsCustomOutlineColor || '#000000'}
                              onChange={(e) => setNeighborsCustomOutlineColor && setNeighborsCustomOutlineColor(e.target.value)}
                              className="w-6 h-6 border-0 p-0 cursor-pointer rounded"
                            />
                            <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Image Outline Color</span>
                          </div>
                          <p className={`text-[9px] mt-2 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                            Uses the same cells / direction / quad / shape settings above. Outline traces the alpha edges of the uploaded image.
                          </p>
                        </>
                      )}
                    </div>
                    {!neighborsQuadEnabled && (
                    <div className="pt-1 border-t border-dashed border-gray-500/30">
                      <span className={`text-[10px] font-bold block mt-2 mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Pair Spacing</span>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Horizontal: {neighborsPairShiftX || 0}px</span>
                      </div>
                      <input
                        type="range"
                        min="-300"
                        max="300"
                        step="1"
                        value={neighborsPairShiftX || 0}
                        onChange={(e) => setNeighborsPairShiftX(parseInt(e.target.value, 10))}
                        data-testid="neighbors-pair-shift-x"
                        className="w-full accent-amber-500"
                      />
                    </div>
                    )}
                    {!neighborsQuadEnabled && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Vertical: {neighborsPairShiftY || 0}px</span>
                      </div>
                      <input
                        type="range"
                        min="-300"
                        max="300"
                        step="1"
                        value={neighborsPairShiftY || 0}
                        onChange={(e) => setNeighborsPairShiftY(parseInt(e.target.value, 10))}
                        data-testid="neighbors-pair-shift-y"
                        className="w-full accent-amber-500"
                      />
                    </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Last Card Standing */}
            {loteriaBoardCount > 0 && (
              <div className="mb-4">
                <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Last Card Standing</Label>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input type="checkbox" checked={!!lastCardEnabled}
                    onChange={(e) => setLastCardEnabled(e.target.checked)}
                    data-testid="lastcard-enabled" className="w-4 h-4 accent-amber-500" />
                  <span className={`text-xs font-bold ${isDarkMode ? 'text-neutral-300' : ''}`}>Enable Focal Card (1 per board)</span>
                </label>

                {lastCardEnabled && (
                  <div className="space-y-2 pl-1">
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Position</span>
                      <div className="grid grid-cols-2 gap-1">
                        {[
                          { id: 'top-left', label: 'Top Left' },
                          { id: 'top-right', label: 'Top Right' },
                          { id: 'bottom-left', label: 'Bot Left' },
                          { id: 'bottom-right', label: 'Bot Right' },
                          { id: 'center', label: 'Center' },
                        ].map(c => (
                          <button key={c.id} data-testid={`lastcard-corner-${c.id}`}
                            onClick={() => setLastCardCorner(c.id)}
                            className={`py-1 text-[10px] font-bold border-2 ${
                              (lastCardCorner || 'top-left') === c.id
                                ? 'bg-amber-500 text-black border-amber-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>{c.label}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Cells Covered</span>
                      <div className="grid grid-cols-5 gap-1">
                        {[2, 3, 4, 6, 9].map(n => (
                          <button key={n} data-testid={`lastcard-size-${n}`}
                            onClick={() => setLastCardSize(n)}
                            className={`py-1 text-[10px] font-bold border-2 ${
                              (lastCardSize || 2) === n
                                ? 'bg-amber-500 text-black border-amber-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>{n}</button>
                        ))}
                      </div>
                    </div>
                    {/* Last Card Scale — make the focal image bigger or smaller
                        than its underlying cell coverage. 100% = original. */}
                    <div>
                      <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                        Card Scale: {lastCardScale != null ? lastCardScale : 100}%
                      </Label>
                      <Slider
                        data-testid="lastcard-scale-slider"
                        value={[lastCardScale != null ? lastCardScale : 100]}
                        onValueChange={([v]) => setLastCardScale && setLastCardScale(v)}
                        min={50} max={200} step={5}
                      />
                      <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                        Below 100% shrinks; above 100% makes the focal card overflow its cells.
                      </p>
                    </div>
                    {lastCardSize !== 4 && lastCardSize !== 9 && (
                      <div>
                        <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Orientation</span>
                        <div className="grid grid-cols-2 gap-1">
                          {[{ id: 'vertical', label: 'Vertical' }, { id: 'horizontal', label: 'Horizontal' }].map(o => (
                            <button key={o.id} data-testid={`lastcard-orientation-${o.id}`}
                              onClick={() => setLastCardOrientation(o.id)}
                              className={`py-1 text-[10px] font-bold border-2 ${
                                (lastCardOrientation || 'vertical') === o.id
                                  ? 'bg-amber-500 text-black border-amber-600'
                                  : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                              }`}>{o.label}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Shape</span>
                      <div className="grid grid-cols-5 gap-1">
                        {['square', 'circle', 'diamond', 'heart', 'flower'].map(sh => (
                          <button key={sh} data-testid={`lastcard-shape-${sh}`}
                            onClick={() => setLastCardShape(sh)}
                            className={`py-1 text-[10px] font-bold capitalize border-2 ${
                              (lastCardShape || 'square') === sh
                                ? 'bg-amber-500 text-black border-amber-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>{sh}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Highlight Color</span>
                      <div className="flex items-center gap-2">
                        <input type="color" value={lastCardHighlightColor} onChange={(e) => setLastCardHighlightColor(e.target.value)}
                          className="w-8 h-8 cursor-pointer rounded border-2 border-gray-600" />
                        <span className={`text-[10px] font-mono ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{lastCardHighlightColor}</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Outline Thickness</span>
                        <span className={`text-[10px] font-mono ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{lastCardBorderThickness}px</span>
                      </div>
                      <input type="range" min="0" max="15" value={lastCardBorderThickness || 0}
                        onChange={(e) => setLastCardBorderThickness(parseInt(e.target.value, 10))}
                        data-testid="lastcard-border-thickness" className="w-full accent-amber-500" />
                    </div>
                    <div>
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Outline Style</span>
                      <div className="grid grid-cols-3 gap-1">
                        {['sharp', 'rounded', 'soft'].map(st => (
                          <button key={st} data-testid={`lastcard-outline-${st}`}
                            onClick={() => setLastCardOutlineStyle(st)}
                            className={`py-1 text-[10px] font-bold capitalize border-2 ${
                              (lastCardOutlineStyle || 'sharp') === st
                                ? 'bg-amber-500 text-black border-amber-600'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>{st}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Tilt</span>
                        <span className={`text-[10px] font-mono ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{lastCardTilt || 0}°</span>
                      </div>
                      <input type="range" min="-30" max="30" value={lastCardTilt || 0}
                        onChange={(e) => setLastCardTilt(parseInt(e.target.value, 10))}
                        data-testid="lastcard-tilt" className="w-full accent-amber-500" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Offset X</span>
                        <span className={`text-[10px] font-mono ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{lastCardOffsetX || 0}px</span>
                      </div>
                      <input type="range" min="-300" max="300" value={lastCardOffsetX || 0}
                        onChange={(e) => setLastCardOffsetX(parseInt(e.target.value, 10))}
                        data-testid="lastcard-offset-x" className="w-full accent-amber-500" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Offset Y</span>
                        <span className={`text-[10px] font-mono ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{lastCardOffsetY || 0}px</span>
                      </div>
                      <input type="range" min="-300" max="300" value={lastCardOffsetY || 0}
                        onChange={(e) => setLastCardOffsetY(parseInt(e.target.value, 10))}
                        data-testid="lastcard-offset-y" className="w-full accent-amber-500" />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!lastCardShadowEnabled}
                        onChange={(e) => setLastCardShadowEnabled(e.target.checked)}
                        data-testid="lastcard-shadow-enabled" className="w-4 h-4 accent-amber-500" />
                      <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : ''}`}>Drop Shadow</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Border / Grid Colors — Multi-Color Rows & Columns at template level */}
            {loteriaBoardCount > 0 && (() => {
              // TEMPLATE-level rows / columns (the layout of boards on the canvas).
              // Each board on a given template row/column gets its OUTER BORDER
              // and INNER GRID lines drawn in the chosen color, so the template
              // visually groups boards by row or column.
              const tDims = boardLayout || getDefaultGridDims(loteriaBoardCount);
              const tplRows = tDims.rows || 1;
              const tplCols = tDims.cols || 1;
              const updateRowColor = (i, c) => {
                const next = [...loteriaRowColors];
                while (next.length < tplRows) next.push('#FFFFFF');
                next[i] = c;
                setLoteriaRowColors(next);
              };
              const updateColColor = (i, c) => {
                const next = [...loteriaColColors];
                while (next.length < tplCols) next.push('#FFFFFF');
                next[i] = c;
                setLoteriaColColors(next);
              };
              const modes = [
                { id: 'checkerboard', label: 'Off' },
                { id: 'rows',         label: 'Rows' },
                { id: 'columns',      label: 'Columns' },
                { id: 'rowStripes',   label: 'Row Stripes' },
                { id: 'colStripes',   label: 'Col Stripes' },
              ];
              return (
                <div className="mb-4">
                  <Label className={`font-semibold text-sm mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                    Multi-Color Borders
                  </Label>
                  <p className={`text-[10px] mb-2 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                    Color each board's <b>border + grid lines</b> by its position in the template grid ({tplCols}×{tplRows}). Vertical = Columns, Horizontal = Rows.
                  </p>
                  {/* Mode toggle */}
                  <div className="grid grid-cols-5 gap-1 mb-2">
                    {modes.map(m => (
                      <button
                        key={m.id}
                        data-testid={`loteria-color-mode-${m.id}`}
                        onClick={() => setLoteriaColorMode(m.id)}
                        className={`py-1 px-1 text-[10px] font-bold leading-tight border-2 transition-colors ${
                          loteriaColorMode === m.id
                            ? (isDarkMode ? 'bg-[#FFD700] text-black border-[#FFD700]' : 'bg-black text-white border-black')
                            : (isDarkMode ? 'bg-neutral-900 text-neutral-300 border-neutral-700 hover:bg-neutral-800' : 'bg-white text-black border-gray-300 hover:bg-gray-100')
                        }`}
                      >{m.label}</button>
                    ))}
                  </div>
                  {/* Per-row / per-col / stripe color pickers */}
                  <div className="mt-3 space-y-1.5">
                    {loteriaColorMode === 'checkerboard' && (
                      <p className={`text-[10px] italic ${isDarkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                        Off — borders use the default Border &amp; Grid colors below.
                      </p>
                    )}
                    {loteriaColorMode === 'rows' && (
                      <div className="flex flex-wrap gap-1">
                        {Array.from({ length: tplRows }).map((_, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <input type="color" value={loteriaRowColors[i] || '#FFFFFF'}
                              data-testid={`loteria-row-color-${i}`}
                              onChange={(e) => updateRowColor(i, e.target.value)}
                              className="w-6 h-6 border-0 p-0 cursor-pointer rounded" />
                            <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>R{i + 1}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {loteriaColorMode === 'columns' && (
                      <div className="flex flex-wrap gap-1">
                        {Array.from({ length: tplCols }).map((_, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <input type="color" value={loteriaColColors[i] || '#FFFFFF'}
                              data-testid={`loteria-col-color-${i}`}
                              onChange={(e) => updateColColor(i, e.target.value)}
                              className="w-6 h-6 border-0 p-0 cursor-pointer rounded" />
                            <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>C{i + 1}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(loteriaColorMode === 'rowStripes' || loteriaColorMode === 'colStripes') && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {[0, 1].map(i => (
                          <div key={i} className="flex items-center gap-1">
                            <input type="color" value={loteriaStripeColors[i] || '#FFFFFF'}
                              data-testid={`loteria-stripe-color-${i}`}
                              onChange={(e) => {
                                const next = [...loteriaStripeColors];
                                while (next.length < 2) next.push('#FFFFFF');
                                next[i] = e.target.value;
                                setLoteriaStripeColors(next);
                              }}
                              className="w-6 h-6 border-0 p-0 cursor-pointer rounded" />
                            <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>{i === 0 ? 'A' : 'B'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Board Style */}
            {loteriaBoardCount > 0 && (
              <div className="mb-4">
                <Label className={`font-semibold text-sm mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Board Style</Label>
                <StyledSelect value={loteriaBorderStyle} onValueChange={setLoteriaBorderStyle} testId="loteria-board-style-select" placeholder="Style">
                  {boardStyles.map((style) => (
                    <SelectItem key={style.id} value={style.id} className="font-medium">{style.label}</SelectItem>
                  ))}
                </StyledSelect>

                {/* Border Color */}
                <div className="mt-3">
                  <div className="flex items-center gap-2">
                    <input type="color" value={loteriaBorderColor} data-testid="loteria-border-color-picker"
                      onChange={(e) => setLoteriaBorderColor(e.target.value)}
                      className="w-6 h-6 border-0 p-0 cursor-pointer rounded" />
                    <span className={`text-xs font-bold ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Border Color</span>
                  </div>
                </div>

                {/* Grid Color */}
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <input type="color" value={loteriaGridColor} data-testid="loteria-grid-color-picker"
                      onChange={(e) => setLoteriaGridColor(e.target.value)}
                      className="w-6 h-6 border-0 p-0 cursor-pointer rounded" />
                    <span className={`text-xs font-bold ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Grid Color</span>
                  </div>
                </div>

                {/* Center Highlight — decorative ring around the center block.
                    Only offered for square 4x4 (2×2) and 5x5 (3×3) grids. */}
                {(String(loteriaGridSize) === '4' || String(loteriaGridSize) === '5') && (
                  <div className="mt-3" data-testid="loteria-center-highlight-section">
                    <button
                      type="button"
                      data-testid="loteria-center-highlight-toggle"
                      onClick={() => setLoteriaCenterHighlight(!loteriaCenterHighlight)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold transition-colors ${loteriaCenterHighlight ? 'bg-fuchsia-600 text-white' : (isDarkMode ? 'bg-neutral-800 text-neutral-300' : 'bg-neutral-200 text-black')}`}
                    >
                      <span>Highlight Center {String(loteriaGridSize) === '4' ? '(2×2)' : '(3×3)'}</span>
                      <span className={`ml-2 inline-flex h-4 w-7 items-center rounded-full transition-colors ${loteriaCenterHighlight ? 'bg-white/90' : 'bg-neutral-500/50'}`}>
                        <span className={`h-3 w-3 rounded-full bg-fuchsia-600 transition-transform ${loteriaCenterHighlight ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                      </span>
                    </button>
                    {loteriaCenterHighlight && (
                      <div className="mt-2" data-testid="loteria-center-highlight-colors">
                        <span className={`text-[11px] font-semibold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>Ring color</span>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {['#FF1493', '#FF0000', '#FF7A00', '#FFD400', '#39FF14', '#00E5FF', '#2979FF', '#9C27B0', '#000000', '#FFFFFF'].map((c) => (
                            <button
                              key={c}
                              type="button"
                              data-testid={`loteria-center-highlight-color-${c.replace('#', '')}`}
                              onClick={() => setLoteriaCenterHighlightColor(c)}
                              title={c}
                              className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${(loteriaCenterHighlightColor || '').toUpperCase() === c ? 'border-fuchsia-400 ring-2 ring-fuchsia-400/50 scale-110' : (isDarkMode ? 'border-neutral-600' : 'border-neutral-300')}`}
                              style={{ backgroundColor: c }}
                            />
                          ))}
                          <input
                            type="color"
                            value={loteriaCenterHighlightColor}
                            data-testid="loteria-center-highlight-color-picker"
                            onChange={(e) => setLoteriaCenterHighlightColor(e.target.value)}
                            className="w-6 h-6 border-0 p-0 cursor-pointer rounded"
                            title="Custom color"
                          />
                        </div>
                        <div className="mt-2">
                          <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            Ring Thickness: {loteriaCenterHighlightThickness}%
                          </Label>
                          <Slider
                            data-testid="loteria-center-highlight-thickness-slider"
                            min={20} max={400} step={10}
                            value={[loteriaCenterHighlightThickness]}
                            onValueChange={([v]) => setLoteriaCenterHighlightThickness(v)}
                            className="my-1"
                          />
                        </div>
                        <div className="mt-2">
                          <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            Move Left / Right: {loteriaCenterHighlightOffsetX > 0 ? `+${loteriaCenterHighlightOffsetX}` : loteriaCenterHighlightOffsetX}
                          </Label>
                          <Slider
                            data-testid="loteria-center-highlight-offset-x-slider"
                            min={-1} max={1} step={1}
                            value={[loteriaCenterHighlightOffsetX]}
                            onValueChange={([v]) => setLoteriaCenterHighlightOffsetX(v)}
                            className="my-1"
                          />
                        </div>
                        <div className="mt-2">
                          <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            Move Up / Down: {loteriaCenterHighlightOffsetY > 0 ? `+${loteriaCenterHighlightOffsetY}` : loteriaCenterHighlightOffsetY}
                          </Label>
                          <Slider
                            data-testid="loteria-center-highlight-offset-y-slider"
                            min={-1} max={1} step={1}
                            value={[loteriaCenterHighlightOffsetY]}
                            onValueChange={([v]) => setLoteriaCenterHighlightOffsetY(v)}
                            className="my-1"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Outer Border Thickness */}
                <div className="mt-3">
                  <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                    Outer Border: {loteriaBorderWidth}%
                  </Label>
                  <Slider
                    data-testid="loteria-border-width-slider"
                    min={20} max={300} step={10}
                    value={[loteriaBorderWidth]}
                    onValueChange={([v]) => setLoteriaBorderWidth(v)}
                    className="my-2"
                  />
                </div>

                {/* Grid Lines Thickness */}
                <div className="mt-2">
                  <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                    Grid Lines: {loteriaGridWidth}%
                  </Label>
                  <Slider
                    data-testid="loteria-grid-width-slider"
                    min={0} max={300} step={10}
                    value={[loteriaGridWidth]}
                    onValueChange={([v]) => setLoteriaGridWidth(v)}
                    className="my-2"
                  />
                </div>

                {/* Board Shadow */}
                <div className="mt-3">
                  <Label className={`font-bold text-xs mb-2 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Board Shadow</Label>
                  <div className="flex gap-1">
                    {[
                      { id: 'none', label: 'None' },
                      { id: 'light', label: 'Light' },
                      { id: 'medium', label: 'Medium' },
                      { id: 'heavy', label: 'Heavy' },
                    ].map(opt => (
                      <button key={opt.id} data-testid={`loteria-shadow-${opt.id}`}
                        onClick={() => setLoteriaBoardShadow(opt.id)}
                        className={`flex-1 px-2 py-1.5 text-[10px] font-bold border-2 transition-colors ${
                          loteriaBoardShadow === opt.id
                            ? 'bg-amber-500 text-black border-amber-500'
                            : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Board Spacing */}
                <div className="mt-3">
                  <Label className={`font-bold text-xs mb-2 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Board Spacing</Label>
                  <div className="space-y-2">
                    <div>
                      <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Horizontal: {loteriaGapH}px</span>
                      <Slider data-testid="loteria-gap-h-slider" min={0} max={100} step={1} value={[loteriaGapH]}
                        onValueChange={([v]) => setLoteriaGapH(v)} className="my-1" />
                    </div>
                    <div>
                      <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Vertical: {loteriaGapV}px</span>
                      <Slider data-testid="loteria-gap-v-slider" min={0} max={100} step={1} value={[loteriaGapV]}
                        onValueChange={([v]) => setLoteriaGapV(v)} className="my-1" />
                    </div>
                  </div>
                </div>
              </div>
            )}
              </>)}
          </section>
          )}

          {/* STEP 4: Free Space (BINGO only) / Loteria Numbering */}
          {gameMode === 'bingo' ? (
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={4} title="Free Space" icon={Edit3}  stepKey="step-free-space" description="Customize the center FREE space: text, color, opacity." />
              {!collapsedSteps.has("step-free-space") && (<>
            
            <div className="flex items-center gap-3 mb-3">
              <Switch checked={showFreeSpaceEditor} onCheckedChange={setShowFreeSpaceEditor} className="data-[state=checked]:bg-[#FF6B6B]" />
              <Label className={`font-medium text-sm ${isDarkMode ? 'text-neutral-300' : ''}`}>Custom Text</Label>
            </div>
            
            {showFreeSpaceEditor && (
              <Input placeholder="Free space text..." value={freeSpaceText} onChange={(e) => setFreeSpaceText(e.target.value)} className={`mb-3 border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-400' : 'border-black'}`} />
            )}

            <div className="flex items-center gap-3 mb-3">
              <Switch checked={freeSpaceNumbering} onCheckedChange={setFreeSpaceNumbering} className="data-[state=checked]:bg-[#4ECDC4]" />
              <Label className={`font-medium text-sm flex items-center gap-1 ${isDarkMode ? 'text-neutral-300' : ''}`}>
                <Hash className="w-3 h-3" /> Number Sequence (#1, #2...)
              </Label>
            </div>

            <div className="mb-3">
              <ColorPicker value={freeSpaceColor} onChange={setFreeSpaceColor} label="Free Space BG" testId="freespace-color-picker" />
            </div>

            <div>
              <ColorPicker value={freeSpaceTextColor} onChange={setFreeSpaceTextColor} label="Free Space Text" testId="freespace-text-color-picker" />
            </div>

            {/* Free Space Opacity */}
            <div className="mt-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                Free Space Opacity: {freeSpaceOpacity}%
              </Label>
              <Slider
                data-testid="freespace-opacity-slider"
                min={0} max={100} step={1}
                value={[freeSpaceOpacity]}
                onValueChange={([v]) => setFreeSpaceOpacity(v)}
                className="my-2"
              />
            </div>

            {/* Free Space Text Effect */}
            <div className="mt-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Text Effect</Label>
              <div className="grid grid-cols-3 gap-1">
                {[
                  { id: 'none', label: 'None' },
                  { id: 'outline', label: 'Outline' },
                  { id: 'white-outline', label: 'White Outline' },
                  { id: 'neon', label: 'Neon' },
                  { id: 'shadow', label: 'Shadow' },
                  { id: 'glow', label: 'Glow' },
                ].map(fx => (
                  <button key={fx.id} data-testid={`freespace-fx-${fx.id}`}
                    onClick={() => setFreeSpaceTextEffect(fx.id)}
                    className={`py-1 text-[9px] font-bold border-2 transition-colors ${
                      (freeSpaceTextEffect || 'none') === fx.id
                        ? 'bg-[#4ECDC4] text-black border-[#4ECDC4]'
                        : isDarkMode ? 'border-neutral-700 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                    }`}>{fx.label}</button>
                ))}
              </div>
            </div>

            {/* Free Space Text Thickness */}
            {freeSpaceTextEffect && freeSpaceTextEffect !== 'none' && (
              <div className="mt-3">
                <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                  Effect Thickness: {freeSpaceTextThickness || 2}
                </Label>
                <Slider
                  data-testid="freespace-text-thickness"
                  value={[freeSpaceTextThickness || 2]}
                  onValueChange={([v]) => setFreeSpaceTextThickness(v)}
                  min={1} max={10} step={1}
                />
              </div>
            )}
              </>)}
          </section>
          ) : (
          /* STEP 4: Loteria Numbering */
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={4} title="Board Numbering" icon={Hash}  stepKey="step-board-numbering" description="Toggle board number badges + position (12 slots) + font, color, outline." />
              {!collapsedSteps.has("step-board-numbering") && (<>

            {/* Board Number on Top */}
            <div className="flex items-center gap-3 mb-2">
              <Switch data-testid="loteria-show-board-num" checked={loteriaNumbering.showBoardNum}
                onCheckedChange={(v) => setLoteriaNumbering(prev => ({ ...prev, showBoardNum: v }))}
                className="data-[state=checked]:bg-amber-500" />
              <Label className={`font-medium text-xs ${isDarkMode ? 'text-neutral-300' : ''}`}>Board Number</Label>
            </div>

            {/* Board Number Position — 4 sides × 3 sub-slots = 12 total placements.
                Rendered as 4 labelled rows (Top / Bottom / Left / Right) so users
                can pick e.g. "Top Right" vs "Top Center" at a glance. */}
            {loteriaNumbering.showBoardNum && (
              <div className="mb-3 ml-1">
                <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Position</Label>
                <div className="space-y-1">
                  {[
                    { side: 'Top',    options: [{ id: 'top-left',     label: 'Left'   }, { id: 'top',           label: 'Middle' }, { id: 'top-right',    label: 'Right'  }] },
                    { side: 'Bottom', options: [{ id: 'bottom-left',  label: 'Left'   }, { id: 'bottom',        label: 'Middle' }, { id: 'bottom-right', label: 'Right'  }] },
                    { side: 'Left',   options: [{ id: 'left-top',     label: 'Top'    }, { id: 'left',          label: 'Middle' }, { id: 'left-bottom',  label: 'Bottom' }] },
                    { side: 'Right',  options: [{ id: 'right-top',    label: 'Top'    }, { id: 'right',         label: 'Middle' }, { id: 'right-bottom', label: 'Bottom' }] },
                  ].map(row => (
                    <div key={row.side} className="flex items-center gap-1">
                      <span className={`w-12 text-[9px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{row.side}</span>
                      <div className="flex gap-1 flex-1">
                        {row.options.map(pos => (
                          <button key={pos.id} data-testid={`loteria-board-num-pos-${pos.id}`}
                            onClick={() => setLoteriaNumbering(prev => ({ ...prev, boardNumPosition: pos.id }))}
                            className={`flex-1 px-1 py-1 text-[10px] font-bold border-2 transition-colors ${
                              (loteriaNumbering.boardNumPosition || 'top') === pos.id
                                ? 'bg-amber-500 text-black border-amber-500'
                                : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}>
                            {pos.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Row Numbers Left */}
            <div className="flex items-center gap-3 mb-3">
              <Switch data-testid="loteria-show-row-nums" checked={loteriaNumbering.showRowNums}
                onCheckedChange={(v) => setLoteriaNumbering(prev => ({ ...prev, showRowNums: v }))}
                className="data-[state=checked]:bg-amber-500" />
              <Label className={`font-medium text-xs ${isDarkMode ? 'text-neutral-300' : ''}`}>Row Numbers (Left)</Label>
            </div>

            {/* Column Numbers Bottom */}
            <div className="flex items-center gap-3 mb-3">
              <Switch data-testid="loteria-show-col-nums" checked={loteriaNumbering.showColNums}
                onCheckedChange={(v) => setLoteriaNumbering(prev => ({ ...prev, showColNums: v }))}
                className="data-[state=checked]:bg-amber-500" />
              <Label className={`font-medium text-xs ${isDarkMode ? 'text-neutral-300' : ''}`}>Column Numbers (Bottom)</Label>
            </div>

            {/* Badge Shape */}
            <div className="mb-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Badge Shape</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'circle', label: 'Circle' },
                  { id: 'square', label: 'Square' },
                  { id: 'banner', label: 'Banner' },
                  { id: 'diamond', label: 'Diamond' },
                  { id: 'star', label: 'Star' },
                  { id: 'hexagon', label: 'Hexagon' },
                ].map(shape => (
                  <button key={shape.id} data-testid={`loteria-badge-shape-${shape.id}`}
                    onClick={() => setLoteriaNumbering(prev => ({ ...prev, badgeShape: shape.id }))}
                    className={`px-3 py-1 text-[10px] font-black rounded border-2 transition-colors ${
                      loteriaNumbering.badgeShape === shape.id
                        ? isDarkMode ? 'bg-amber-600 border-amber-500 text-white' : 'bg-amber-500 border-amber-600 text-white'
                        : isDarkMode ? 'border-neutral-700 text-neutral-400' : 'border-gray-300'
                    }`}>{shape.label}</button>
                ))}
                {/* Uploaded image as badge background — file picker doubles as
                    the selector: clicking the label opens the picker; picking
                    an image switches the shape to 'image' and stores the data
                    URL on loteriaNumbering.badgeImage. Badge size + number
                    distance sliders continue to work exactly like any other
                    shape option. */}
                <label
                  data-testid="loteria-badge-shape-image"
                  className={`px-3 py-1 text-[10px] font-black rounded border-2 transition-colors cursor-pointer inline-flex items-center gap-1 ${
                    loteriaNumbering.badgeShape === 'image'
                      ? isDarkMode ? 'bg-amber-600 border-amber-500 text-white' : 'bg-amber-500 border-amber-600 text-white'
                      : isDarkMode ? 'border-neutral-700 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                  }`}
                  title={loteriaNumbering.badgeImage ? 'Uploaded — click to replace' : 'Upload PNG/JPG to use as the board-number background'}
                >
                  {loteriaNumbering.badgeImage ? 'Custom ✓' : 'Custom'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="loteria-badge-image-input"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        setLoteriaNumbering(prev => ({ ...prev, badgeShape: 'image', badgeImage: reader.result }));
                      };
                      reader.readAsDataURL(file);
                      // Allow re-picking the same file later.
                      e.target.value = '';
                    }}
                  />
                </label>
                {loteriaNumbering.badgeImage && (
                  <button
                    type="button"
                    data-testid="loteria-badge-image-clear"
                    onClick={() => setLoteriaNumbering(prev => ({ ...prev, badgeImage: null, badgeShape: prev.badgeShape === 'image' ? 'circle' : prev.badgeShape }))}
                    className={`px-2 py-1 text-[10px] font-black rounded border-2 transition-colors ${isDarkMode ? 'border-red-800 text-red-300 hover:bg-red-950' : 'border-red-400 text-red-600 hover:bg-red-50'}`}
                    title="Remove uploaded badge image"
                  >Clear</button>
                )}
              </div>
            </div>

            {/* Custom Badge Number sliders — only visible when the Custom
                (uploaded image) shape is active. Gives the user separate
                control of number size + XY placement over the stamp. */}
            {loteriaNumbering.badgeShape === 'image' && (
              <div className={`mb-3 p-2 rounded border ${isDarkMode ? 'border-neutral-800 bg-neutral-900/60' : 'border-amber-200 bg-amber-50'}`}>
                <Label className={`font-bold text-[11px] mb-2 block ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Custom — Image & Number</Label>
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Image Size</span>
                    <span className={`text-[10px] font-mono ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{loteriaNumbering.badgeCustomImageSize != null ? loteriaNumbering.badgeCustomImageSize : 100}%</span>
                  </div>
                  <input
                    data-testid="loteria-badge-custom-image-size"
                    type="range" min={20} max={300} step={5}
                    value={loteriaNumbering.badgeCustomImageSize != null ? loteriaNumbering.badgeCustomImageSize : 100}
                    onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeCustomImageSize: parseInt(e.target.value) }))}
                    className="w-full accent-amber-500"
                  />
                </div>
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Number Size</span>
                    <span className={`text-[10px] font-mono ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{loteriaNumbering.badgeCustomNumberSize != null ? loteriaNumbering.badgeCustomNumberSize : 100}%</span>
                  </div>
                  <input
                    data-testid="loteria-badge-custom-number-size"
                    type="range" min={20} max={200} step={5}
                    value={loteriaNumbering.badgeCustomNumberSize != null ? loteriaNumbering.badgeCustomNumberSize : 100}
                    onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeCustomNumberSize: parseInt(e.target.value) }))}
                    className="w-full accent-amber-500"
                  />
                </div>
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Number X (horizontal)</span>
                    <span className={`text-[10px] font-mono ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{loteriaNumbering.badgeCustomNumberShiftX || 0}%</span>
                  </div>
                  <input
                    data-testid="loteria-badge-custom-number-shift-x"
                    type="range" min={-50} max={50} step={1}
                    value={loteriaNumbering.badgeCustomNumberShiftX || 0}
                    onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeCustomNumberShiftX: parseInt(e.target.value) }))}
                    className="w-full accent-amber-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Number Y (vertical)</span>
                    <span className={`text-[10px] font-mono ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{loteriaNumbering.badgeCustomNumberShiftY || 0}%</span>
                  </div>
                  <input
                    data-testid="loteria-badge-custom-number-shift-y"
                    type="range" min={-50} max={50} step={1}
                    value={loteriaNumbering.badgeCustomNumberShiftY || 0}
                    onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeCustomNumberShiftY: parseInt(e.target.value) }))}
                    className="w-full accent-amber-500"
                  />
                </div>
                {/* Custom Image outline — draws a rectangular stroke around
                    the uploaded image. Independent width slider + color so
                    users can frame their custom badge without touching the
                    global badge outline (which is disabled for shape=image). */}
                <div className="mt-3 pt-2 border-t border-amber-400/30">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Image Outline Width</span>
                    <span className={`text-[10px] font-mono ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{loteriaNumbering.badgeCustomOutlineWidth || 0}px</span>
                  </div>
                  <input
                    data-testid="loteria-badge-custom-outline-width"
                    type="range" min={0} max={20} step={1}
                    value={loteriaNumbering.badgeCustomOutlineWidth || 0}
                    onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeCustomOutlineWidth: parseInt(e.target.value) }))}
                    className="w-full accent-amber-500"
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="color"
                      data-testid="loteria-badge-custom-outline-color"
                      value={loteriaNumbering.badgeCustomOutlineColor || '#000000'}
                      onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeCustomOutlineColor: e.target.value }))}
                      className="w-6 h-6 border-0 p-0 cursor-pointer rounded"
                    />
                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Image Outline Color</span>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="loteria-badge-custom-number-reset"
                  onClick={() => setLoteriaNumbering(prev => ({ ...prev, badgeCustomImageSize: 100, badgeCustomNumberSize: 100, badgeCustomNumberShiftX: 0, badgeCustomNumberShiftY: 0 }))}
                  className={`mt-2 text-[9px] font-bold underline ${isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                >Reset image + number size + position</button>
              </div>
            )}

            {/* Starting Number */}
            <div className="mb-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Starting Number</Label>
              <Input data-testid="loteria-starting-num" type="number" min={1} max={999}
                value={loteriaNumbering.startingNum || 1}
                onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, startingNum: parseInt(e.target.value) || 1 }))}
                className={`w-24 text-sm ${isDarkMode ? 'bg-neutral-900 border-neutral-700 text-white' : 'border-black'}`}
              />
            </div>

            {/* Badge Colors */}
            <div className="mb-3 space-y-2">
              <div className="flex items-center gap-2">
                <input type="color" value={loteriaNumbering.badgeBgColor} data-testid="loteria-badge-bg-color"
                  onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeBgColor: e.target.value }))}
                  className="w-6 h-6 border-0 p-0 cursor-pointer rounded" />
                <span className={`text-xs font-bold ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Badge Background</span>
              </div>
              <div className="flex items-center gap-2">
                <input type="color" value={loteriaNumbering.badgeTextColor} data-testid="loteria-badge-text-color"
                  onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeTextColor: e.target.value }))}
                  className="w-6 h-6 border-0 p-0 cursor-pointer rounded" />
                <span className={`text-xs font-bold ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Badge Text</span>
              </div>
              <div className="flex items-center gap-2">
                <input type="color" value={loteriaNumbering.badgeOutlineColor || '#000000'} data-testid="loteria-badge-outline-color"
                  onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeOutlineColor: e.target.value }))}
                  className="w-6 h-6 border-0 p-0 cursor-pointer rounded" />
                <span className={`text-xs font-bold ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Badge Outline</span>
              </div>
            </div>

            {/* Badge Outline Thickness */}
            <div className="mb-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                Outline Thickness: {loteriaNumbering.badgeOutlineThickness || 0}px
              </Label>
              <Slider min={0} max={12} step={1}
                value={[loteriaNumbering.badgeOutlineThickness || 0]}
                onValueChange={([v]) => setLoteriaNumbering(prev => ({ ...prev, badgeOutlineThickness: v }))}
                data-testid="loteria-badge-outline-thickness" className="my-2" />
            </div>

            {/* Badge Size */}
            <div className="mb-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                Badge Size: {loteriaNumbering.badgeSize}%
              </Label>
              <Slider min={50} max={300} step={5} value={[loteriaNumbering.badgeSize]}
                onValueChange={([v]) => setLoteriaNumbering(prev => ({ ...prev, badgeSize: v }))} className="my-2" />
            </div>

            {/* Number Offset (distance from board) */}
            <div className="mb-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                Number Distance: {loteriaNumbering.badgeOffset}%
              </Label>
              <Slider
                data-testid="loteria-badge-offset-slider"
                min={0} max={200} step={5}
                value={[loteriaNumbering.badgeOffset]}
                onValueChange={([v]) => setLoteriaNumbering(prev => ({ ...prev, badgeOffset: v }))}
                className="my-2"
              />
              <p className={`text-[10px] ${isDarkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                0% = touching board, 200% = far from board
              </p>
            </div>

            {/* Free Position — manual XY shift relative to anchor slot.
                Lets you nudge the Board Number anywhere, including ONTO
                the board. Star almost-winner indicator follows automatically. */}
            <div className={`mb-3 p-2 border-2 ${isDarkMode ? 'border-cyan-800 bg-cyan-950/30' : 'border-cyan-300 bg-cyan-50'}`}>
              <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                Board Number Free Position
              </Label>
              <div className="mb-1">
                <Label className={`font-bold text-[10px] mb-0 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                  Shift X: {loteriaNumbering.badgeShiftX || 0}%
                </Label>
                <Slider
                  data-testid="loteria-badge-shift-x"
                  min={-100} max={100} step={1}
                  value={[loteriaNumbering.badgeShiftX || 0]}
                  onValueChange={([v]) => setLoteriaNumbering(prev => ({ ...prev, badgeShiftX: v }))}
                  className="my-1"
                />
              </div>
              <div className="mb-1">
                <Label className={`font-bold text-[10px] mb-0 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                  Shift Y: {loteriaNumbering.badgeShiftY || 0}%
                </Label>
                <Slider
                  data-testid="loteria-badge-shift-y"
                  min={-100} max={100} step={1}
                  value={[loteriaNumbering.badgeShiftY || 0]}
                  onValueChange={([v]) => setLoteriaNumbering(prev => ({ ...prev, badgeShiftY: v }))}
                  className="my-1"
                />
              </div>
              <div className="flex items-center justify-between">
                <p className={`text-[9px] ${isDarkMode ? 'text-cyan-400/80' : 'text-cyan-600'}`}>
                  Negative = up/left · Positive = down/right
                </p>
                {(loteriaNumbering.badgeShiftX || loteriaNumbering.badgeShiftY) ? (
                  <button
                    data-testid="loteria-badge-shift-reset"
                    onClick={() => setLoteriaNumbering(prev => ({ ...prev, badgeShiftX: 0, badgeShiftY: 0 }))}
                    className={`text-[9px] font-bold ${isDarkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-700 hover:text-cyan-900'}`}
                  >
                    ↺ Reset
                  </button>
                ) : null}
              </div>
            </div>

            {/* Tilt — rotate badge in place. Affects board + row + col badges
                so the look stays consistent across the template. */}
            <div className="mb-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>
                Tilt: {loteriaNumbering.badgeTilt || 0}°
              </Label>
              <Slider
                data-testid="loteria-badge-tilt-slider"
                min={-45} max={45} step={1}
                value={[loteriaNumbering.badgeTilt || 0]}
                onValueChange={([v]) => setLoteriaNumbering(prev => ({ ...prev, badgeTilt: v }))}
                className="my-2"
              />
              <p className={`text-[10px] ${isDarkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Negative = tilt left · Positive = tilt right
              </p>
            </div>

            {/* No Background on Numbers */}
            <div className="flex items-center gap-3 mb-3">
              <Switch
                data-testid="loteria-badge-no-bg"
                checked={loteriaNumbering.badgeNoBg}
                onCheckedChange={(v) => setLoteriaNumbering(prev => ({ ...prev, badgeNoBg: v }))}
                className="data-[state=checked]:bg-amber-500"
              />
              <Label className={`font-medium text-xs ${isDarkMode ? 'text-neutral-300' : ''}`}>No Background on Numbers</Label>
            </div>

            {/* Badge Font */}
            <div className="mb-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Badge Font</Label>
              <select data-testid="loteria-badge-font" value={loteriaNumbering.badgeFont}
                onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeFont: e.target.value }))}
                className={`w-full p-2 rounded border-2 text-sm ${isDarkMode ? 'bg-neutral-900 border-neutral-700 text-white' : 'border-black'}`}
                style={{ fontFamily: (fontOptions || []).find(f => f.family === loteriaNumbering.badgeFont || f.id === loteriaNumbering.badgeFont)?.family || 'inherit' }}>
                {/* Render every visible font option (system-only fonts are auto-hidden if not installed on the user's device). The option `value` IS the CSS family stack so ctx.font picks it up correctly. */}
                {(fontOptions || []).map(f => (
                  <option key={f.id} value={f.family} style={{ fontFamily: f.family }}>{f.label}</option>
                ))}
              </select>
            </div>

            {/* Badge Text Effect */}
            <div className="mb-3">
              <Label className={`font-bold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : 'text-black'}`}>Text Effect</Label>
              <div className="grid grid-cols-3 gap-1">
                {[
                  { id: 'none', label: 'None' },
                  { id: 'outline', label: 'Outline' },
                  { id: 'white-outline', label: 'White Outline' },
                  { id: 'neon', label: 'Neon' },
                  { id: 'shadow', label: 'Shadow' },
                  { id: 'glow', label: 'Glow' },
                ].map(fx => (
                  <button key={fx.id} data-testid={`badge-fx-${fx.id}`}
                    onClick={() => setLoteriaNumbering(prev => ({ ...prev, badgeTextEffect: fx.id }))}
                    className={`py-1 text-[9px] font-bold border-2 transition-colors ${
                      (loteriaNumbering.badgeTextEffect || 'none') === fx.id
                        ? 'bg-amber-500 text-black border-amber-500'
                        : isDarkMode ? 'border-neutral-700 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                    }`}>{fx.label}</button>
                ))}
              </div>
            </div>

            {/* Badge Text Thickness */}
            {loteriaNumbering.badgeTextEffect && loteriaNumbering.badgeTextEffect !== 'none' && (
              <div className="mb-3">
                <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                  Effect Thickness: {loteriaNumbering.badgeTextThickness || 2}
                </Label>
                <Slider
                  data-testid="badge-text-thickness"
                  value={[loteriaNumbering.badgeTextThickness || 2]}
                  onValueChange={([v]) => setLoteriaNumbering(prev => ({ ...prev, badgeTextThickness: v }))}
                  min={1} max={10} step={1}
                />
              </div>
            )}

            {/* Badge Letter Spacing — pulls digits closer or pushes them apart.
                Useful for multi-digit board numbers (e.g., "12", "100") where
                you want a tighter or looser look. */}
            <div className="mb-3">
              <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                Letter Spacing: {loteriaNumbering.badgeLetterSpacing || 0}px
              </Label>
              <Slider
                data-testid="badge-letter-spacing"
                value={[loteriaNumbering.badgeLetterSpacing || 0]}
                onValueChange={([v]) => setLoteriaNumbering(prev => ({ ...prev, badgeLetterSpacing: v }))}
                min={-5} max={20} step={1}
              />
              <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                Negative pulls digits together; positive spaces them apart.
              </p>
            </div>

            {/* Badge Font Outline Color — applies to Outline, Neon, and Glow effects.
                White Outline is intentionally fixed to white. Shadow uses a dark drop. */}
            {(loteriaNumbering.badgeTextEffect === 'outline' ||
              loteriaNumbering.badgeTextEffect === 'neon' ||
              loteriaNumbering.badgeTextEffect === 'glow') && (
              <div className="mb-3">
                <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                  Font Outline Color
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    data-testid="badge-text-outline-color"
                    value={loteriaNumbering.badgeTextOutlineColor || '#000000'}
                    onChange={(e) => setLoteriaNumbering(prev => ({ ...prev, badgeTextOutlineColor: e.target.value }))}
                    className="w-6 h-6 border-0 p-0 cursor-pointer rounded"
                  />
                  <span className={`text-[10px] ${isDarkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                    {loteriaNumbering.badgeTextOutlineColor || '#000000'}
                  </span>
                </div>
              </div>
            )}

            {/* Link badge font outline to Multi-Color Borders — when enabled,
                each board's number outline follows the row/column palette
                configured in Step 3's Multi-Color Borders. Works especially
                well with "No Background on Numbers" so white digits on light
                center images get a color-matched ring. */}
            {(loteriaColorMode === 'rows' || loteriaColorMode === 'columns' ||
              loteriaColorMode === 'rowStripes' || loteriaColorMode === 'colStripes') && (
              <div className={`mb-3 p-2 border-2 ${isDarkMode ? 'border-fuchsia-800 bg-fuchsia-950/30' : 'border-fuchsia-300 bg-fuchsia-50'}`}>
                <div className="flex items-center gap-2">
                  <Switch
                    data-testid="badge-outline-link-multicolor"
                    checked={!!loteriaNumbering.badgeTextOutlineLinked}
                    onCheckedChange={(checked) => setLoteriaNumbering(prev => ({ ...prev, badgeTextOutlineLinked: checked }))}
                    className="data-[state=checked]:bg-fuchsia-600"
                  />
                  <Label className={`font-bold text-[10px] ${isDarkMode ? 'text-fuchsia-300' : 'text-fuchsia-700'}`}>
                    Link outline to Multi-Color Borders
                  </Label>
                </div>
                <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-fuchsia-400/80' : 'text-fuchsia-600'}`}>
                  Each board's number outline adopts its row / column color. Tip: works great with <b>No Background on Numbers</b>.
                </p>
              </div>
            )}
              </>)}
          </section>
          )}

          {/* STEP 5: Custom Template Text — works for both BINGO & Loteria */}
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={5} title="Template Text" icon={Edit3} stepKey="step-template-text" description="Add a custom title or branding text on top of the boards. Drag to reposition. Paid users can save & reuse text bundles." />
              {!collapsedSteps.has("step-template-text") && (<>

            <Input
                  data-testid="template-text-input"
                  placeholder="Enter text (e.g., Christmas Bingo Night)..."
                  value={templateText}
                  onChange={(e) => setTemplateText(e.target.value)}
                  className={`mb-3 border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-400' : 'border-black'}`}
                />

                {/* Font */}
                <div className="mb-3">
                  <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Font</Label>
                  <select data-testid="template-text-font-select"
                    value={templateTextFont}
                    onChange={(e) => setTemplateTextFont(e.target.value)}
                    className={`w-full px-2 py-1.5 border-2 text-sm ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'bg-white border-black text-black'}`}
                    style={{ fontFamily: (fontOptions || []).find(f => f.id === templateTextFont)?.family || "'Outfit'" }}>
                    {(fontOptions || []).map(f => (
                      <option key={f.id} value={f.id} style={{ fontFamily: f.family }}>{f.label}</option>
                    ))}
                  </select>
                </div>

                {/* Fill color */}
                <div className="mb-3">
                  <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Fill Color</Label>
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {['#000000','#FFFFFF','#FF0000','#0000FF','#00AA00','#FF6600','#9900CC','#FF1493','#FFD700','#808080','#8B4513','#00CED1'].map(c => (
                      <button key={c} data-testid={`template-text-color-${c}`}
                        onClick={() => setTemplateTextColor(c)}
                        className={`w-6 h-6 rounded-full border-2 transition-transform ${templateTextColor === c ? 'scale-125 ring-2 ring-offset-1 ring-purple-500' : isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                  <input type="color" value={templateTextColor} onChange={(e) => setTemplateTextColor(e.target.value)}
                    data-testid="template-text-color-picker"
                    className="w-full h-7 border-2 cursor-pointer rounded" />
                </div>

                {/* Outline */}
                <div className="flex items-center gap-3 mb-2">
                  <Switch
                    data-testid="template-text-outline-enable"
                    checked={templateTextOutlineEnabled}
                    onCheckedChange={setTemplateTextOutlineEnabled}
                    className="data-[state=checked]:bg-[#FF6B6B]"
                  />
                  <Label className={`font-medium text-xs ${isDarkMode ? 'text-neutral-300' : ''}`}>Outline</Label>
                </div>

                {templateTextOutlineEnabled && (
                  <>
                    <div className="mb-2">
                      <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Outline Color</Label>
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        {['#FFFFFF','#000000','#FFD700','#FF0000','#0000FF','#FF1493'].map(c => (
                          <button key={c} data-testid={`template-text-outline-${c}`}
                            onClick={() => setTemplateTextOutlineColor(c)}
                            className={`w-6 h-6 rounded-full border-2 transition-transform ${templateTextOutlineColor === c ? 'scale-125 ring-2 ring-offset-1 ring-purple-500' : isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}
                            style={{ backgroundColor: c }} />
                        ))}
                      </div>
                      <input type="color" value={templateTextOutlineColor} onChange={(e) => setTemplateTextOutlineColor(e.target.value)}
                        data-testid="template-text-outline-picker"
                        className="w-full h-7 border-2 cursor-pointer rounded" />
                    </div>
                    <div className="mb-3">
                      <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                        Outline Thickness: {templateTextOutlineWidth}
                      </Label>
                      <Slider
                        data-testid="template-text-outline-width"
                        value={[templateTextOutlineWidth || 4]}
                        onValueChange={([v]) => setTemplateTextOutlineWidth(v)}
                        min={1} max={20} step={1}
                      />
                    </div>
                  </>
                )}

                {/* Size */}
                <div>
                  <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                    Text Size: {templateTextSize}px
                  </Label>
                  <Slider
                    data-testid="template-text-size"
                    value={[templateTextSize || 64]}
                    onValueChange={([v]) => setTemplateTextSize(v)}
                    min={6} max={200} step={1}
                  />
                </div>
                <div className="mt-3">
                  <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                    Letter Spacing: {templateTextLetterSpacing || 0}px
                  </Label>
                  <Slider
                    data-testid="template-text-letter-spacing"
                    value={[templateTextLetterSpacing || 0]}
                    onValueChange={([v]) => setTemplateTextLetterSpacing(v)}
                    min={-10} max={40} step={1}
                  />
                  <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                    Negative pulls letters together; positive pushes them apart.
                  </p>
                </div>
                <div className="mt-3">
                  <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                    Opacity: {templateTextOpacity == null ? 100 : templateTextOpacity}%
                  </Label>
                  <Slider
                    data-testid="template-text-opacity"
                    value={[templateTextOpacity == null ? 100 : templateTextOpacity]}
                    onValueChange={([v]) => setTemplateTextOpacity(v)}
                    min={0} max={100} step={5}
                  />
                </div>

            {/* Multiple Texts + Saved Library (paid + owner only). */}
            {isPaidSubscriber && (
              <div className={`mt-4 pt-3 border-t-2 ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className={`font-bold text-xs ${isDarkMode ? 'text-neutral-300' : ''}`}>
                    Additional Texts {Array.isArray(extraTemplateTexts) && extraTemplateTexts.length > 0 && `(${extraTemplateTexts.length})`}
                      </Label>
                      <button
                        data-testid="add-extra-text"
                        onClick={() => addExtraTemplateText && addExtraTemplateText()}
                        className={`px-2 py-0.5 text-[10px] font-bold border-2 ${isDarkMode ? 'border-emerald-600 text-emerald-300 hover:bg-emerald-900/30' : 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'}`}
                      >
                        + Add
                      </button>
                    </div>
                    <p className={`text-[9px] mb-2 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                      Each additional text can be moved independently. Drag on the canvas to reposition.
                    </p>
                    {Array.isArray(extraTemplateTexts) && extraTemplateTexts.map((t, idx) => (
                      <div key={t.id} className={`mb-2 p-2 border-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900/40' : 'border-gray-300 bg-gray-50'}`} data-testid={`extra-text-${idx}`}>
                        <div className="flex items-center gap-1 mb-1">
                          <input
                            type="text"
                            data-testid={`extra-text-input-${idx}`}
                            value={t.text}
                            onChange={(e) => updateExtraTemplateText(t.id, { text: e.target.value })}
                            placeholder="Text..."
                            className={`flex-1 px-1 py-0.5 text-[11px] border ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'border-gray-400'}`}
                          />
                          <button
                            data-testid={`extra-text-delete-${idx}`}
                            onClick={() => removeExtraTemplateText(t.id)}
                            className="text-red-500 hover:text-red-700 px-1"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-1 mb-1">
                          <select
                            value={t.font}
                            onChange={(e) => updateExtraTemplateText(t.id, { font: e.target.value })}
                            className={`text-[10px] p-0.5 border ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'border-gray-400'}`}
                            style={{ fontFamily: (fontOptions || []).find(f => f.id === t.font)?.family || "'Outfit'" }}
                          >
                            {(fontOptions || []).map(f => (
                              <option key={f.id} value={f.id} style={{ fontFamily: f.family }}>{f.label}</option>
                            ))}
                          </select>
                          <div className="flex items-center gap-1">
                            <input type="color" value={t.color} onChange={(e) => updateExtraTemplateText(t.id, { color: e.target.value })}
                              className="w-5 h-5 border-0 p-0 cursor-pointer rounded" title="Text color" />
                            <input type="color" value={t.outlineColor} onChange={(e) => updateExtraTemplateText(t.id, { outlineColor: e.target.value })}
                              className="w-5 h-5 border-0 p-0 cursor-pointer rounded" title="Outline color" />
                            <label className="flex items-center gap-0.5 text-[9px]">
                              <input type="checkbox" checked={t.outlineEnabled !== false}
                                onChange={(e) => updateExtraTemplateText(t.id, { outlineEnabled: e.target.checked })} />
                              outline
                            </label>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          <label className={`text-[9px] ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            Size: {t.size}
                            <input type="range" min={6} max={200} step={1}
                              value={t.size}
                              onChange={(e) => updateExtraTemplateText(t.id, { size: parseInt(e.target.value, 10) })}
                              className="w-full" />
                          </label>
                          <label className={`text-[9px] ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            Outline: {t.outlineWidth}
                            <input type="range" min={0} max={20} step={1}
                              value={t.outlineWidth}
                              onChange={(e) => updateExtraTemplateText(t.id, { outlineWidth: parseInt(e.target.value, 10) })}
                              className="w-full" />
                          </label>
                        </div>
                        <label className={`text-[9px] block mt-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                          Letter Spacing: {t.letterSpacing || 0}px
                          <input type="range" min={-10} max={40} step={1}
                            data-testid={`extra-text-letter-spacing-${idx}`}
                            value={t.letterSpacing || 0}
                            onChange={(e) => updateExtraTemplateText(t.id, { letterSpacing: parseInt(e.target.value, 10) })}
                            className="w-full" />
                        </label>
                        <label className={`text-[9px] block mt-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                          Opacity: {t.opacity == null ? 100 : t.opacity}%
                          <input type="range" min={0} max={100} step={5}
                            data-testid={`extra-text-opacity-${idx}`}
                            value={t.opacity == null ? 100 : t.opacity}
                            onChange={(e) => updateExtraTemplateText(t.id, { opacity: parseInt(e.target.value, 10) })}
                            className="w-full" />
                        </label>
                      </div>
                    ))}

                    {/* Saved Texts library */}
                    <div className={`mt-3 p-2 border-2 ${isDarkMode ? 'border-emerald-800 bg-emerald-950/30' : 'border-emerald-300 bg-emerald-50'}`}>
                      <Label className={`font-bold text-[10px] mb-1 block ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        💾 Saved Text Library
                      </Label>
                      <input
                        type="text"
                        data-testid="save-template-text-name"
                        value={saveTemplateTextName || ''}
                        onChange={(e) => setSaveTemplateTextName && setSaveTemplateTextName(e.target.value)}
                        placeholder="Bundle name (optional)…"
                        className={`w-full mb-1 px-1 py-0.5 text-[10px] border ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-500' : 'border-gray-400'}`}
                      />
                      <button
                        data-testid="save-template-text-bundle"
                        onClick={() => saveTemplateTextBundle && saveTemplateTextBundle(saveTemplateTextName)}
                        disabled={savedTemplateTextBusy}
                        className={`w-full py-1.5 text-[10px] font-bold mb-1 ${
                          savedTemplateTextBusy ? 'bg-neutral-600 text-neutral-300 cursor-wait' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        }`}
                      >
                        {savedTemplateTextBusy ? 'Saving…' : 'Save Current Texts'}
                      </button>
                      {Array.isArray(savedTemplateTexts) && savedTemplateTexts.length > 0 && (
                        <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="saved-template-text-list">
                          {savedTemplateTexts.map((b) => (
                            <div key={b.id} className={`flex items-center gap-1 p-1 border ${isDarkMode ? 'border-neutral-700' : 'border-gray-200'}`}>
                              <button
                                data-testid={`saved-template-text-load-${b.id}`}
                                onClick={() => loadTemplateTextBundle && loadTemplateTextBundle(b)}
                                className={`flex-1 text-left px-1 py-0.5 text-[10px] truncate hover:underline ${isDarkMode ? 'text-fuchsia-300' : 'text-fuchsia-700'}`}
                                title={`Load "${b.name}"`}
                              >
                                {b.name}
                              </button>
                              <button
                                data-testid={`saved-template-text-delete-${b.id}`}
                                onClick={() => {
                                  if (window.confirm(`Delete saved text bundle "${b.name}"?`)) {
                                    deleteTemplateTextBundle && deleteTemplateTextBundle(b.id);
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 px-1"
                                title="Delete"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>)}
          </section>

          {/* STEP 6: Markup */}
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={6} title="Markup" icon={Pencil}  stepKey="step-markup" description="Drawing tools (pen, line, arrow, oval) plus auto-mark size & opacity." />
              {!collapsedSteps.has("step-markup") && (<>

            {/* Tool selection */}
            <Label className={`font-semibold text-xs mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Drawing Tool</Label>
            <div className="grid grid-cols-4 gap-1.5 mb-4">
              {[
                { id: 'freehand', icon: Pencil, label: 'Draw' },
                { id: 'line', icon: Minus, label: 'Line' },
                { id: 'arrow', icon: ArrowRight, label: 'Arrow' },
                { id: 'double-arrow', icon: ArrowLeftRight, label: 'Double' },
                { id: 'circle', icon: Circle, label: 'Circle' },
                { id: 'square', icon: Square, label: 'Square' },
                { id: 'stamp', icon: Image, label: 'Stamp' },
              ].map(tool => (
                <button
                  key={tool.id}
                  data-testid={`markup-tool-${tool.id}`}
                  onClick={() => setActiveMarkupTool(activeMarkupTool === tool.id ? null : tool.id)}
                  className={`p-2 border-2 flex flex-col items-center gap-0.5 text-[10px] font-bold transition-colors ${
                    activeMarkupTool === tool.id
                      ? 'bg-purple-600 text-white border-purple-600'
                      : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-black hover:bg-gray-100'
                  }`}
                >
                  <tool.icon className="w-4 h-4" />
                  {tool.label}
                </button>
              ))}
              <button
                data-testid="markup-tool-none"
                onClick={() => setActiveMarkupTool(null)}
                title="Exit markup mode — return to moving boards, overlays, and emojis. Markup elements become locked while in this mode."
                className={`p-2 border-2 flex flex-col items-center gap-0.5 text-[10px] font-bold transition-colors ${
                  !activeMarkupTool
                    ? 'bg-neutral-700 text-white border-neutral-700'
                    : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-black hover:bg-gray-100'
                }`}
              >
                <Eraser className="w-4 h-4" />
                Off
              </button>
            </div>

            {/* Stamp tool — image uploader. Only visible when the Stamp tool
                is active. Click on a board to drop the image; resize/move the
                first one, then subsequent clicks on other boards auto-place at
                the same relative position + size. */}
            {activeMarkupTool === 'stamp' && (
              <div className={`mb-3 p-3 border-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900/30' : 'border-gray-300 bg-gray-50'}`}>
                <Label className={`font-bold text-xs block mb-2 ${isDarkMode ? 'text-neutral-200' : 'text-black'}`}>
                  Stamp Image
                </Label>
                {!markupStampImage ? (
                  <label className={`block w-full p-3 border-2 border-dashed cursor-pointer text-center text-xs font-bold transition-colors ${
                    isDarkMode ? 'border-neutral-600 text-neutral-400 hover:border-amber-500 hover:text-amber-400' : 'border-gray-400 text-gray-600 hover:border-amber-600 hover:text-amber-700'
                  }`}>
                    <Upload className="w-4 h-4 mx-auto mb-1" />
                    Upload image to use as stamp
                    <input
                      type="file"
                      accept="image/*"
                      data-testid="markup-stamp-upload"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => setMarkupStampImage(ev.target.result);
                        reader.readAsDataURL(file);
                        // Allow re-selecting the same file
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 p-2 border ${isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}>
                      <img src={markupStampImage} alt="stamp" className="w-12 h-12 object-contain bg-white/10" />
                      <span className={`flex-1 text-[10px] ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                        Click on a board to drop. Adjust size below — all stamps re-flow together.
                      </span>
                    </div>
                    {/* Stamp Size — single slider that resizes the first
                        stamp AND every replicated stamp in lockstep, anchored
                        at each stamp's center. Avoids needing to wrestle with
                        corner handles. Default 160px = same as the auto-place
                        default in CanvasEditor. */}
                    {(() => {
                      const currentSize = Math.round(markupStampTemplate?.width || 160);
                      return (
                        <div>
                          <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                            Stamp Size: {currentSize}px
                          </Label>
                          <Slider
                            data-testid="markup-stamp-size"
                            value={[currentSize]}
                            onValueChange={([val]) => resizeAllMarkupStamps && resizeAllMarkupStamps(val)}
                            min={20}
                            max={600}
                            step={2}
                          />
                        </div>
                      );
                    })()}
                    <button
                      data-testid="markup-stamp-clear"
                      onClick={() => setMarkupStampImage(null)}
                      className={`w-full py-1 text-[10px] font-bold border transition-colors ${
                        isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-400 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Remove stamp image
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Color */}
            <div className="mb-3">
              <ColorPicker value={markupColor} onChange={setMarkupColor} label="Color" testId="markup-color-picker" />
            </div>

            {/* Draw Thickness */}
            <div className="mb-3">
              <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Draw Thickness: {markupThickness}px</Label>
              <Slider data-testid="markup-thickness-slider" value={[markupThickness]} onValueChange={([val]) => setMarkupThickness(val)} min={1} max={50} step={1} />
            </div>

            {/* Eraser Thickness */}
            <div className="mb-3">
              <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Eraser Size: {eraserThickness}px</Label>
              <Slider data-testid="eraser-thickness-slider" value={[eraserThickness]} onValueChange={([val]) => setEraserThickness(val)} min={3} max={40} step={1} />
            </div>

            {/* Tip Style */}
            <div className="mb-3">
              <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Tip</Label>
              <div className="flex gap-2">
                {[{ id: 'round', icon: Circle, label: 'Round' }, { id: 'square', icon: Square, label: 'Square' }].map(tip => (
                  <button
                    key={tip.id}
                    data-testid={`markup-tip-${tip.id}`}
                    onClick={() => setMarkupTipStyle(tip.id)}
                    className={`flex-1 py-1.5 border-2 text-xs font-bold flex items-center justify-center gap-1 transition-colors ${
                      markupTipStyle === tip.id
                        ? 'bg-purple-600 text-white border-purple-600'
                        : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-black hover:bg-gray-100'
                    }`}
                  >
                    <tip.icon className="w-3 h-3" /> {tip.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Opacity — applies to NEW drawings, AND live-updates the selected markup. */}
            <div className="mb-3">
              <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                Opacity: {markupOpacity}%{selectedMarkupId ? ' — editing selected' : ''}
              </Label>
              <Slider data-testid="markup-opacity-slider" value={[markupOpacity]} onValueChange={([val]) => setMarkupOpacity(val)} min={10} max={100} step={5} />
            </div>

            {/* Selection action bar — only Delete remains (Duplicate removed per request) */}
            {selectedMarkupId && (
              <div className="mb-3">
                <button
                  data-testid="markup-delete-selected-btn"
                  onClick={deleteSelectedMarkup}
                  className={`w-full py-1.5 border-2 text-xs font-bold transition-colors ${
                    isDarkMode ? 'border-red-500 text-red-300 hover:bg-red-900/30' : 'border-red-500 text-red-500 hover:bg-red-50'
                  }`}
                  title="Delete the currently selected markup"
                >
                  Delete Selected
                </button>
              </div>
            )}

            {/* Clear */}
            <button
              data-testid="clear-markup-btn"
              onClick={clearMarkup}
              disabled={!markupElements || markupElements.length === 0}
              className={`w-full py-2 border-2 font-bold text-sm flex items-center justify-center gap-2 transition-colors ${
                isDarkMode ? 'border-neutral-600 text-red-400 hover:bg-red-900/30' : 'border-black text-red-500 hover:bg-red-50'
              } ${(!markupElements || markupElements.length === 0) ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              <Trash2 className="w-4 h-4" /> Clear All Markup ({markupElements?.length || 0})
            </button>

            {/* Auto-Markup */}
            <div className={`mt-5 pt-5 border-t-2 ${isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Label className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  Auto-Markup
                </Label>
                {!isPaidSubscriber && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500 text-black">PRO</span>}
              </div>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                {gameMode === 'loteria'
                  ? 'Type a figure name (e.g. Chalupa, Gallo) or number (e.g. 48, 1) and press Enter.'
                  : 'Type a BINGO call (e.g. B1, N35) and press Enter. Separate multiple with commas.'}
              </p>

              <AutoMarkupInput
                data-testid="auto-markup-input"
                placeholder={gameMode === 'loteria' ? 'e.g. Chalupa, 48, Gallo' : 'e.g. B5, I22, N35'}
                onSubmit={handleAutoMarkupSubmit}
                resetKey={autoMarkupResetKey}
                className={`mb-3 font-bold text-center text-lg uppercase ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'border-black'}`}
              />

              {/* Auto-Markup Size */}
              <div className="mb-3">
                <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Markup Size: {autoMarkupScale}%</Label>
                <Slider data-testid="auto-markup-scale-slider" value={[autoMarkupScale]} onValueChange={([val]) => setAutoMarkupScale(val)} min={50} max={300} step={10} />
              </div>

              {/* Called numbers display */}
              {calledNumbers && calledNumbers.length > 0 && (
                <div className={`p-3 border-2 mb-3 ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-gray-300 bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Label className={`text-xs font-bold ${isDarkMode ? 'text-neutral-300' : ''}`}>
                      Called ({calledNumbers.length})
                    </Label>
                    <button
                      data-testid="clear-called-numbers"
                      onClick={clearCalledNumbers}
                      className="text-red-500 hover:text-red-700 text-xs font-bold"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {calledNumbers.map((num, i) => (
                      <span
                        key={i}
                        data-testid={`called-pill-${i}`}
                        className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs font-bold border ${
                          isDarkMode ? 'border-neutral-600 bg-neutral-800 text-white' : 'border-black bg-white'
                        }`}
                      >
                        <span>{num}</span>
                        {removeCalledNumber && (
                          <button
                            type="button"
                            data-testid={`remove-called-${i}`}
                            onClick={() => removeCalledNumber(i)}
                            title={`Remove ${num} from Called history`}
                            className={`ml-0.5 w-4 h-4 inline-flex items-center justify-center rounded-sm leading-none text-[11px] font-black transition-colors ${
                              isDarkMode ? 'text-red-400 hover:bg-red-900/40 hover:text-red-300' : 'text-red-600 hover:bg-red-100 hover:text-red-700'
                            }`}
                            aria-label={`Remove ${num}`}
                          >×</button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
              </>)}
          </section>

          {/* STEP 6: Emojis */}
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={7} title="Emojis" icon={Smile}  stepKey="step-emojis" description="Drop reaction emojis on the canvas. Drag to reposition, scale per-emoji." />
              {!collapsedSteps.has("step-emojis") && (<>

            <Label className={`font-semibold text-xs mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Tap to place on canvas</Label>
            <div className="grid grid-cols-8 gap-1 mb-4">
              {EMOJI_OPTIONS && EMOJI_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  data-testid={`emoji-${opt.id}`}
                  onClick={() => addEmoji(opt.emoji)}
                  className={`p-1.5 border-2 text-lg hover:scale-110 transition-transform ${
                    isDarkMode ? 'border-neutral-700 hover:bg-neutral-800' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  title={opt.label}
                >
                  {opt.emoji}
                </button>
              ))}
            </div>

            {/* Selected emoji controls */}
            {selectedEmojiId && emojis && (() => {
              const selEmoji = emojis.find(e => e.id === selectedEmojiId);
              return selEmoji ? (
                <div className={`p-3 border-2 space-y-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900' : 'border-black bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : ''}`}>{selEmoji.emoji} Selected</span>
                    <button
                      data-testid="remove-selected-emoji"
                      onClick={() => removeEmoji(selEmoji.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <Label className={`text-xs font-semibold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>Size: {selEmoji.size}px</Label>
                    <Slider
                      data-testid="emoji-size-slider"
                      value={[selEmoji.size]}
                      onValueChange={([val]) => updateEmojiSize(selEmoji.id, val)}
                      min={16}
                      max={120}
                      step={2}
                      className="mt-1"
                    />
                  </div>
                </div>
              ) : null;
            })()}
              </>)}
          </section>

          {/* STEP 7: Filters */}
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={8} title="Filters" icon={SlidersHorizontal}  stepKey="step-filters" description="Adjust brightness, contrast, saturation, hue, blur, sepia." />
              {!collapsedSteps.has("step-filters") && (<>

            {/* Presets */}
            <Label className={`font-semibold text-xs mb-2 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Presets</Label>
            <div className="grid grid-cols-4 gap-1.5 mb-4">
              {FILTER_PRESETS && FILTER_PRESETS.map(preset => (
                <button
                  key={preset.id}
                  data-testid={`filter-preset-${preset.id}`}
                  onClick={() => applyFilterPreset(preset)}
                  className={`py-1.5 border-2 text-[10px] font-bold transition-colors ${
                    JSON.stringify(filterSettings) === JSON.stringify(preset.settings)
                      ? 'bg-purple-600 text-white border-purple-600'
                      : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-black hover:bg-gray-100'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Custom sliders */}
            {filterSettings && [
              { key: 'brightness', label: 'Brightness', min: 50, max: 200, unit: '%' },
              { key: 'contrast', label: 'Contrast', min: 50, max: 200, unit: '%' },
              { key: 'saturate', label: 'Saturate', min: 0, max: 200, unit: '%' },
              { key: 'grayscale', label: 'Grayscale', min: 0, max: 100, unit: '%' },
              { key: 'sepia', label: 'Sepia', min: 0, max: 100, unit: '%' },
              { key: 'blur', label: 'Blur', min: 0, max: 10, unit: 'px' },
              { key: 'hueRotate', label: 'Hue Rotate', min: -180, max: 180, unit: 'deg' },
            ].map(slider => (
              <div key={slider.key} className="mb-2">
                <Label className={`text-xs font-semibold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                  {slider.label}: {filterSettings[slider.key]}{slider.unit}
                </Label>
                <Slider
                  data-testid={`filter-${slider.key}`}
                  value={[filterSettings[slider.key]]}
                  onValueChange={([val]) => updateFilter(slider.key, val)}
                  min={slider.min}
                  max={slider.max}
                  step={slider.key === 'blur' ? 0.5 : 1}
                  className="mt-0.5"
                />
              </div>
            ))}
              </>)}
          </section>

          {/* PRIZE DOTS — a row of colored circles with per-dot text and an
              auto "Rd. 1 / Rd. 2 / … / NP" label above each. Draggable as a
              group on the canvas. Sits between Filters and Watermark so it's
              easy to find under Template Text tooling.
              Owner-exclusive: hidden entirely from Demo & Subscriber accounts. */}
          {isOwner && (
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={8.5} title="Prize Dots" icon={Type} stepKey="step-prize-dots" description="A row of colored circles with a text inside and an auto Rd. 1/2/…/NP label above. Drag on the canvas to place." />
            {!collapsedSteps.has("step-prize-dots") && (<>
              <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="prize-dots-enable"
                  checked={!!(prizeDots && prizeDots.enabled)}
                  onChange={(e) => setPrizeDots(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="w-4 h-4 accent-purple-500"
                />
                <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>Show Prize Dots</span>
              </label>
              {prizeDots && prizeDots.enabled && (
                <div className="space-y-3">
                  <div>
                    <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Number of dots</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="range" min={1} max={10} step={1}
                        data-testid="prize-dots-count"
                        value={prizeDots.count || 4}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          setPrizeDots(prev => {
                            const nextTexts = Array.from({ length: n }, (_, i) => (prev.texts && prev.texts[i] != null) ? prev.texts[i] : 'Text');
                            return { ...prev, count: n, texts: nextTexts };
                          });
                        }}
                        className="flex-1 accent-purple-500"
                      />
                      <span className={`text-[11px] font-mono ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>{prizeDots.count || 4}</span>
                    </div>
                  </div>
                  <div>
                    <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Dot Size</Label>
                    <input
                      type="range" min={20} max={200} step={2}
                      data-testid="prize-dots-size"
                      value={prizeDots.dotSize || 60}
                      onChange={(e) => setPrizeDots(prev => ({ ...prev, dotSize: parseInt(e.target.value, 10) }))}
                      className="w-full accent-purple-500"
                    />
                  </div>
                  <div>
                    <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Spacing (all at once)</Label>
                    <input
                      type="range" min={0} max={300} step={2}
                      data-testid="prize-dots-spacing"
                      value={prizeDots.spacing != null ? prizeDots.spacing : 40}
                      onChange={(e) => setPrizeDots(prev => ({ ...prev, spacing: parseInt(e.target.value, 10) }))}
                      className="w-full accent-purple-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Dot Color</Label>
                      <input type="color" data-testid="prize-dots-dot-color"
                        value={prizeDots.dotColor || '#0F3B7C'}
                        onChange={(e) => setPrizeDots(prev => ({ ...prev, dotColor: e.target.value }))}
                        className="w-full h-8 rounded cursor-pointer" />
                    </div>
                    <div>
                      <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Inside Text Color</Label>
                      <input type="color" data-testid="prize-dots-text-color"
                        value={prizeDots.textColor || '#FFFFFF'}
                        onChange={(e) => setPrizeDots(prev => ({ ...prev, textColor: e.target.value }))}
                        className="w-full h-8 rounded cursor-pointer" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="prize-dots-show-labels"
                      checked={prizeDots.showLabels !== false}
                      onChange={(e) => setPrizeDots(prev => ({ ...prev, showLabels: e.target.checked }))}
                      className="w-4 h-4 accent-purple-500"
                    />
                    <span className={`text-[11px] font-bold ${isDarkMode ? 'text-neutral-200' : 'text-black'}`}>Show labels above (Rd. 1, Rd. 2, …, NP)</span>
                  </label>
                  {prizeDots.showLabels !== false && (
                    <>
                      <div>
                        <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Label Color</Label>
                        <input type="color" data-testid="prize-dots-label-color"
                          value={prizeDots.labelColor || '#000000'}
                          onChange={(e) => setPrizeDots(prev => ({ ...prev, labelColor: e.target.value }))}
                          className="w-full h-8 rounded cursor-pointer" />
                      </div>
                      <div>
                        <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                          Round # Size {prizeDots.labelSize != null && (<span className="opacity-60 font-mono ml-1">{prizeDots.labelSize}px</span>)}
                        </Label>
                        <input
                          type="range" min={10} max={80} step={1}
                          data-testid="prize-dots-label-size"
                          value={prizeDots.labelSize != null ? prizeDots.labelSize : Math.max(14, (prizeDots.dotSize || 60) * 0.4)}
                          onChange={(e) => setPrizeDots(prev => ({ ...prev, labelSize: parseInt(e.target.value, 10) }))}
                          className="w-full accent-purple-500"
                        />
                        <button
                          type="button"
                          onClick={() => setPrizeDots(prev => ({ ...prev, labelSize: null }))}
                          className={`text-[9px] font-bold underline ${isDarkMode ? 'text-neutral-500 hover:text-white' : 'text-gray-400 hover:text-black'}`}
                        >Auto (from dot size)</button>
                      </div>
                      {/* Inside-circle text size — independent from the label
                          size above. Slider overrides the dot-size-derived
                          default; "Auto" resets to that default. */}
                      <div>
                        <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                          Text Size {prizeDots.textSize != null && (<span className="opacity-60 font-mono ml-1">{prizeDots.textSize}px</span>)}
                        </Label>
                        <input
                          type="range" min={10} max={120} step={1}
                          data-testid="prize-dots-text-size"
                          value={prizeDots.textSize != null ? prizeDots.textSize : Math.max(12, (prizeDots.dotSize || 60) * 0.5)}
                          onChange={(e) => setPrizeDots(prev => ({ ...prev, textSize: parseInt(e.target.value, 10) }))}
                          className="w-full accent-purple-500"
                        />
                        <button
                          type="button"
                          onClick={() => setPrizeDots(prev => ({ ...prev, textSize: null }))}
                          className={`text-[9px] font-bold underline ${isDarkMode ? 'text-neutral-500 hover:text-white' : 'text-gray-400 hover:text-black'}`}
                        >Auto (from dot size)</button>
                      </div>
                    </>
                  )}
                  {/* Independent outlines — Round # label vs. inside-circle
                      text. Each has its own width slider + color. Width 0
                      disables that outline. */}
                  <div className="pt-2 border-t border-neutral-700/40">
                    <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                      Dot Outline (circle)
                    </Label>
                    <div className="grid grid-cols-2 gap-2 items-end">
                      <div>
                        <span className={`text-[9px] font-bold block mb-0.5 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Width: {prizeDots.dotOutlineWidth || 0}</span>
                        <input
                          type="range" min={0} max={20} step={1}
                          data-testid="prize-dots-dot-outline-width"
                          value={prizeDots.dotOutlineWidth || 0}
                          onChange={(e) => setPrizeDots(prev => ({ ...prev, dotOutlineWidth: parseInt(e.target.value, 10) }))}
                          className="w-full accent-purple-500"
                        />
                      </div>
                      <div>
                        <span className={`text-[9px] font-bold block mb-0.5 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Color</span>
                        <input type="color" data-testid="prize-dots-dot-outline-color"
                          value={prizeDots.dotOutlineColor || '#000000'}
                          onChange={(e) => setPrizeDots(prev => ({ ...prev, dotOutlineColor: e.target.value }))}
                          className="w-full h-8 rounded cursor-pointer" />
                      </div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-neutral-700/40">
                    <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                      Round # Outline
                    </Label>
                    <div className="grid grid-cols-2 gap-2 items-end">
                      <div>
                        <span className={`text-[9px] font-bold block mb-0.5 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Width: {prizeDots.labelOutlineWidth || 0}</span>
                        <input
                          type="range" min={0} max={12} step={1}
                          data-testid="prize-dots-label-outline-width"
                          value={prizeDots.labelOutlineWidth || 0}
                          onChange={(e) => setPrizeDots(prev => ({ ...prev, labelOutlineWidth: parseInt(e.target.value, 10) }))}
                          className="w-full accent-purple-500"
                        />
                      </div>
                      <div>
                        <span className={`text-[9px] font-bold block mb-0.5 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Color</span>
                        <input type="color" data-testid="prize-dots-label-outline-color"
                          value={prizeDots.labelOutlineColor || '#000000'}
                          onChange={(e) => setPrizeDots(prev => ({ ...prev, labelOutlineColor: e.target.value }))}
                          className="w-full h-8 rounded cursor-pointer" />
                      </div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-neutral-700/40">
                    <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>
                      Text Outline (inside circle)
                    </Label>
                    <div className="grid grid-cols-2 gap-2 items-end">
                      <div>
                        <span className={`text-[9px] font-bold block mb-0.5 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Width: {prizeDots.textOutlineWidth || 0}</span>
                        <input
                          type="range" min={0} max={12} step={1}
                          data-testid="prize-dots-outline-width"
                          value={prizeDots.textOutlineWidth || 0}
                          onChange={(e) => setPrizeDots(prev => ({ ...prev, textOutlineWidth: parseInt(e.target.value, 10) }))}
                          className="w-full accent-purple-500"
                        />
                      </div>
                      <div>
                        <span className={`text-[9px] font-bold block mb-0.5 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Color</span>
                        <input type="color" data-testid="prize-dots-outline-color"
                          value={prizeDots.textOutlineColor || '#000000'}
                          onChange={(e) => setPrizeDots(prev => ({ ...prev, textOutlineColor: e.target.value }))}
                          className="w-full h-8 rounded cursor-pointer" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className={`text-[11px] font-semibold mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Text inside each circle</Label>
                    <div className="space-y-1">
                      {Array.from({ length: prizeDots.count || 4 }, (_, i) => {
                        const isNP = i === (prizeDots.count || 4) - 1;
                        const labelHint = isNP ? 'NP' : `Rd. ${i + 1}`;
                        return (
                          <div key={i} className="flex items-center gap-2">
                            <span className={`text-[9px] w-10 font-mono ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>{labelHint}</span>
                            <input
                              type="text"
                              data-testid={`prize-dot-text-${i}`}
                              value={(prizeDots.texts && prizeDots.texts[i] != null) ? prizeDots.texts[i] : 'Text'}
                              onChange={(e) => setPrizeDots(prev => {
                                const arr = Array.from({ length: prev.count || 4 }, (_, j) => (prev.texts && prev.texts[j] != null) ? prev.texts[j] : 'Text');
                                arr[i] = e.target.value;
                                return { ...prev, texts: arr };
                              })}
                              className={`flex-1 px-2 py-1 text-[11px] border ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'bg-white border-gray-300'}`}
                              placeholder="Text"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="prize-dots-reset-position"
                    onClick={() => setPrizeDots(prev => ({ ...prev, x: null, y: null }))}
                    className={`text-[10px] font-bold underline ${isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                  >Reset position (auto-center bottom)</button>
                  <p className={`text-[9px] leading-snug ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                    Tip: drag any dot on the canvas to reposition the whole group. All dots stay evenly spaced.
                  </p>
                </div>
              )}
            </>)}
          </section>
          )}

          {/* STEP 8: Watermark */}
          <section className={`border-b-2 pb-6 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <StepHeader number={9} title="Watermark (Optional)" icon={Type}  stepKey="step-watermark" description="Add a subtle diagonal watermark for branding/anti-piracy." />
              {!collapsedSteps.has("step-watermark") && (<>
            <div className="flex items-center gap-3 mb-3">
              <Switch checked={watermarkEnabled} onCheckedChange={setWatermarkEnabled} className="data-[state=checked]:bg-[#FF6B6B]" />
              <Label className={`font-medium text-sm ${isDarkMode ? 'text-neutral-300' : ''}`}>Enable Watermark</Label>
            </div>
            {watermarkEnabled && (
              <div className="space-y-3">
                <Input placeholder="Watermark text..." value={watermarkText} onChange={(e) => setWatermarkText(e.target.value)} className={`border-2 ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white placeholder:text-neutral-400' : 'border-black'}`} />

                {/* Font Style — uses shared fontOptions (system-only fonts auto-hidden if not installed) */}
                <div>
                  <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Font Style</Label>
                  <select data-testid="watermark-font-select" value={watermarkFont} onChange={(e) => setWatermarkFont(e.target.value)}
                    className={`w-full px-2 py-1.5 border-2 text-sm ${isDarkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'bg-white border-black text-black'}`}
                    style={{ fontFamily: (fontOptions || []).find(f => f.id === watermarkFont)?.family || "'Outfit'" }}>
                    {(fontOptions || []).map(f => (
                      <option key={f.id} value={f.id} style={{ fontFamily: f.family }}>{f.label}</option>
                    ))}
                  </select>
                </div>

                {/* Color */}
                <div>
                  <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Color</Label>
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {['#000000','#FFFFFF','#FF0000','#0000FF','#00AA00','#FF6600','#9900CC','#FF1493','#FFD700','#808080','#8B4513','#00CED1'].map(c => (
                      <button key={c} data-testid={`watermark-color-${c}`}
                        onClick={() => setWatermarkColor(c)}
                        className={`w-6 h-6 rounded-full border-2 transition-transform ${watermarkColor === c ? 'scale-125 ring-2 ring-offset-1 ring-purple-500' : isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input data-testid="watermark-color-custom" type="color" value={watermarkColor} onChange={(e) => setWatermarkColor(e.target.value)}
                      className="w-6 h-6 border-0 p-0 cursor-pointer rounded" />
                    <span className={`text-[10px] font-mono font-bold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>{watermarkColor.toUpperCase()}</span>
                  </div>
                </div>
                <div>
                  <Label className={`font-semibold text-xs mb-1 block ${isDarkMode ? 'text-neutral-300' : ''}`}>Opacity: {watermarkOpacity}%</Label>
                  <Slider value={[watermarkOpacity]} onValueChange={([val]) => setWatermarkOpacity(val)} min={10} max={80} step={5} />
                </div>
              </div>
            )}
              </>)}
          </section>

          {/* ── Import Section (AI-vision Loteria template image import) ───
              Sits ABOVE Export, mirroring the Export styling so the two
              "data in / data out" sections live side-by-side in the UI. */}
          {importTemplateFromImage && (
            <section data-testid="import-section">
              <div className={`flex items-center gap-2 mb-3 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                <Upload className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-neutral-800'}`} strokeWidth={2.5} />
                <span className="text-base font-bold">Import</span>
                <span className="ml-auto text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500 text-black">PRO</span>
              </div>
              <p className={`text-[10px] mb-2 leading-snug ${isDarkMode ? 'text-neutral-500' : 'text-gray-600'}`}>
                Upload a PNG/JPG of a Loteria template — the AI reads every board and rebuilds it here so Auto-Markup &amp; Live Caller work.
              </p>
              <input
                type="file"
                id="upload-template-file"
                data-testid="upload-template-file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                disabled={importingTemplate}
                onChange={async (e) => {
                  const f = e.target.files && e.target.files[0];
                  if (!f) return;
                  await importTemplateFromImage(f);
                  e.target.value = "";
                }}
                className="hidden"
              />
              <label
                htmlFor="upload-template-file"
                data-testid="upload-template-btn"
                className={`w-full py-2 rounded font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition-all ${
                  importingTemplate
                    ? 'bg-neutral-600 text-neutral-300 cursor-wait'
                    : 'bg-amber-500 text-black hover:bg-amber-400 hover:shadow-md'
                }`}
                style={{ pointerEvents: importingTemplate ? 'none' : 'auto' }}
              >
                {importingTemplate ? (
                  <>
                    <span className="inline-block w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Analyzing image…
                  </>
                ) : (
                  <>
                    <Upload className="w-3.5 h-3.5" /> Choose Loteria Image
                  </>
                )}
              </label>
              <p className={`text-[9px] mt-2 ${isDarkMode ? 'text-neutral-600' : 'text-gray-500'}`}>
                Requires your 54-card deck loaded above. Max 12 MB.
              </p>
            </section>
          )}

          {/* Export Section */}
          <section>
            <div className={`flex items-center gap-2 mb-3 ${isDarkMode ? 'text-white' : 'text-black'}`}>
              <Download className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-neutral-800'}`} strokeWidth={2.5} />
              <span className="text-base font-bold">Export</span>
              {!isPaidSubscriber && (
                <span className="ml-auto text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500 text-black">PRO</span>
              )}
            </div>
            <div className="flex gap-2">
              <button data-testid="export-png-btn" onClick={exportAsPNG} className={`flex-1 neo-button-primary flex items-center justify-center gap-2 ${!isPaidSubscriber ? 'opacity-60' : ''}`}>
                {!isPaidSubscriber && <Lock className="w-3 h-3" />}
                <FileImage className="w-4 h-4" /> PNG
              </button>
              <button data-testid="export-pdf-btn" onClick={exportAsPDF} className={`flex-1 neo-button-secondary flex items-center justify-center gap-2 ${!isPaidSubscriber ? 'opacity-60' : ''}`}>
                {!isPaidSubscriber && <Lock className="w-3 h-3" />}
                <FileDown className="w-4 h-4" /> PDF
              </button>
            </div>
            <button
              data-testid="export-png-transparent-btn"
              onClick={exportAsPNGNoBackground}
              className={`mt-2 w-full py-2 text-xs font-bold border-2 flex items-center justify-center gap-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white text-black hover:bg-gray-100'} ${!isPaidSubscriber ? 'opacity-60' : ''}`}
              title="Download PNG with boards only — no background color or image. Useful for composing over your own background in another tool."
            >
              {!isPaidSubscriber && <Lock className="w-3 h-3" />}
              <FileImage className="w-3.5 h-3.5" /> PNG (Transparent — Boards Only)
            </button>
          </section>
        </div>
      </ScrollArea>

      {/* Live Caller Button - bottom of sidebar */}
      {onOpenCaller && (
        <div className={`p-3 border-t-2 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
          <button data-testid="open-caller-btn" onClick={onOpenCaller}
            className="w-full py-2.5 rounded font-black text-sm flex items-center justify-center gap-2 transition-all hover:brightness-110"
            style={{ background: 'linear-gradient(135deg, #FF0088, #CC00FF)', color: 'white', boxShadow: '0 0 12px #FF008844' }}>
            <Play className="w-4 h-4" fill="white" /> LIVE BINGO CALLER
          </button>
        </div>
      )}
    </aside>
  );
}

export default memo(Sidebar);
